import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, Check, Trash2, RotateCw, Edit, ImagePlus } from 'lucide-react';
import { Modal, Button, MathceptDetailModal, useMathceptDetail, AiRunModal } from '../common';
import { useAuth } from '../../context/AuthContext';
// 개념 고르기·라벨 칸은 문제 편집과 **같은 부품**을 쓴다(오너 결정 2026-09-13 D4 — 개념 고르기는 ConceptPicker 가 정본).
// 개념 사전도 그 부품이 v2(GET /api/v2/mathcepts)로 읽는다 — 이 창이 v1(utils/mathConceptsUtils)을 치던 자리가 사라졌다.
import ConceptPicker from '../ProblemHubV2/ConceptPicker';
import LabelEditor from '../ProblemHubV2/LabelEditor';
import OcrSourcePreview from './OcrSourcePreview';
import { difficultyOptionLabel } from '../ProblemHubV2/problemApi';
import { isModifiedClick, problemDetailPath } from '../ProblemHubV2/ProblemIdLink';
// 정답을 수식으로 그리는 형제 다섯 곳이 전부 이것을 쓴다(ProblemEdit·ProblemSolution·ResultInputModal×2).
// $ 없는 bare LaTeX 문자열을 그대로 넘길 수 있는 이 저장소의 유일한 관용구다.
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css'; // 쓰는 파일이 각자 넣는 것이 이 저장소 관례다(29곳)
import {
  fetchProblemDetail,
  fetchPageImageUrl,
  patchProblem,
  reviewProblem,
  getAnalysisStatusDisplay,
  getFlagDisplay,
  getBlockingFlags,
  getAnswerTypeDisplay,
  fetchAllProjectPages,
  deleteProblem,
  reocrProblem,
  splitLabelText,
  toLabelList,
  PRINTED_NUMBER_LABEL,
} from './ocrApi';
import { BodyPayloadView, SolutionPayloadView, PageImageContext, CIRCLED, Blocks } from './BlockRenderer';
import { choiceNoOfAnswer, isLooseChoiceAnswer } from './answerChoice';
// 그림 편집 창은 문제은행과 같은 것을 쓴다 — 계약이 「경로만 다르다」고 했고(BE-20260827 §7),
// 창 스스로 목록·순번 대조·낙관락·오류 문구를 다 쥔다. 여기서는 target='ocr' 로 열기만 한다.
import FigureModal from '../FigureEditor/FigureModal';
// [이미지 추가]의 갈래 고르기와 AI 생성 창 — 문제 편집과 **같은 창**을 쓴다(오너 지시 2026-09-05
// 「어떤 경로든 동일 기능·동일 모달」). 생성은 재요청 창(FigureReconvertModal)의 kind='generate' 얼굴이다.
import FigureAddModal from '../FigureEditor/FigureAddModal';
import FigureReconvertModal from '../FigureEditor/FigureReconvertModal';
import OcrPasteImageModal from './OcrPasteImageModal';
import OcrFigurePickModal from './OcrFigurePickModal';
// 이 문제의 자리를 가리키는 조각 고르기 — 서버가 `is_body` 로 알려 준다(회신 2026-09-06 · dev 537792e).
import { pickBodySourceIndex, hasDirectSource } from './sourcePick';
import { pageRerunMessage } from './pageRerunMessage';
import { collectFigureNodes } from '../FigureEditor/figureMatch';
// purgeRemovedFigures — 편집기에서 손으로 지운 그림을 **저장 전에** 삭제 API 로 돌린다(2026-09-04).
// registerFigure·reconvertFigure — 올린 이미지로 생성할 때(등록 → 그 자리 재요청) 쓴다(runFigGen).
// fetchFigures — 생성 창에 낼 「이 자리 그림들의 원본」 카드를 읽는다(openFigGen · 2026-09-06 A 안).
import {
  purgeRemovedFigures, registerNewFigures, generateFigure, registerFigure, reconvertFigure, fetchFigures,
  readEditedCopyDropped, parseImageDataUri, AUTH_REDIRECT,
} from '../FigureEditor/figureApi';
import {
  describePurgeFailure, describePartialPurge, describeFigureError,
  FIGURE_NOT_GENERATED_TEXT, figureRedrawnText, figureRedrawRejectedText,
  describeRegisterFailure, describeImageReject,
} from '../FigureEditor/figureText';
import { listFigureOrigins } from '../FigureEditor/figureOrigins';
import OcrLexicalEditor, { normalizeEditorState } from './OcrLexicalEditor';
import OcrProblemList from './OcrProblemList';
import { rowPatchFromReview } from './reviewRowPatch';
import { parseAnswerForDisplay } from './answerLatex';

/**
 * OCR 검수 패널 (FE-4) — 문제 행 클릭 시 여는 풀스크린 모달(size="max").
 *
 * 좌: 원본 근거 — sources[]를 kind 탭(문제/정답/풀이)으로 묶고, 선택 소스의 페이지 이미지에
 *     bbox(0~1 정규화, 좌상단 원점) 하이라이트. presigned URL은 만료되므로 열 때마다 재요청.
 * 우: 추출 데이터 — 본문/정답/풀이 렌더(BlockRenderer) + 편집 + 저장(변경 키만 PATCH) + 승인/반려.
 *     본문·풀이 payload 편집은 staging 포맷 특성상 1차로 JSON 편집기(검수 가능 수준 우선,
 *     구조적 편집기는 Lexical 승격 후 과제).
 *
 * 이전/다음: 현재 목록(필터 순서) 기준. 로드된 페이지의 양끝에 닿으면 부모가 인접 페이지를
 *     불러와 그 페이지의 마지막(이전)·첫(다음) 문제로 이어 준다 — 비활성은 전체의 양끝에서만.
 */

const KIND_TABS = [
  { kind: 1, label: '문제' },
  { kind: 3, label: '정답' },
  { kind: 2, label: '풀이' },
];

// 맞춤은 원본 크기를 상한으로 가로·세로 모두 칸 안에 맞춘다. 확대는 사용자가 선택한 경우만 한다.
const ZOOMS = [
  { key: 'fit', label: '맞춤' },
  { key: 'x15', label: '150%', width: '150%' },
  { key: 'x20', label: '200%', width: '200%' },
];

// 받침에 따라 조사를 고른다 — "정답이" / "풀이가". 안내 문구가 값에 따라 달라지는 자리에만 쓴다.
// 한글이 아니면(숫자·영문) 앞의 것을 쓴다: 확인 문구에서 숫자 뒤에 오는 경우가 없어 그것으로 충분하다.
function josa(word, withBatchim, withoutBatchim) {
  const s = String(word || '');
  const c = s.charCodeAt(s.length - 1);
  if (Number.isNaN(c) || c < 0xac00 || c > 0xd7a3) return withBatchim;
  return (c - 0xac00) % 28 !== 0 ? withBatchim : withoutBatchim;
}

// answer 비교 정규화: trim 후 빈 문자열이면 null — 백엔드 bind가 ''를 저장할 수 있어
// 양쪽에 동일 규칙을 적용해야 무편집 dirty 오탐·의도치 않은 answer:null 전송이 없다(N2).
const normAnswer = (v) => {
  const s = v == null ? '' : String(v);
  return s.trim() === '' ? null : s;
};

function AiChip({ label }) {
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
      <Sparkles className="h-3 w-3" />
      {label}
    </span>
  );
}

/**
 * 정답 한 줄 — 평소에는 수식으로 보이고, 더블클릭하면 원문으로 바뀌어 고칠 수 있다.
 *
 * 본문 편집기의 수식 노드와 같은 규칙이다(src/editor/nodes/EquationComponent.tsx:110-128):
 * 렌더 ↔ 원문을 오가되 저장 값은 LaTeX 문자열 그대로. 그래서 백엔드 계약이 걸리지 않는다 —
 * 편집기를 붙이는 것이 아니라 같은 문자열을 다르게 보여 주는 것뿐이다.
 * (편집기를 붙이려면 answer_lexical 이 있어야 하는데, 계약에 있는 것은 body·solution 둘뿐이다.)
 *
 * 수식 노드와 다른 점 둘.
 * ① 한 번 눌러 들어간다. 저기는 글 흐름에 박힌 수식이라 단일 클릭이 이미 커서 놓기·선택에
 *    쓰이고 있어 더블클릭일 수밖에 없었다. 여기는 원래 input 이던 자리라 한 번 눌러 쳐지는 것이
 *    이 칸에 대한 기대다 — 눌러도 아무 일이 없으면 고장으로 읽힌다.
 * ② 키보드로도 들어간다. 저기는 편집기 안이라 마우스로 충분했지만 여기는 ←→·Enter 로 훑는
 *    검수 화면의 폼 칸이라, 고치려고 마우스를 잡게 할 수 없다.
 *
 * 키 충돌은 없다. 모달의 ←→ 리스너는 INPUT 에 포커스가 있으면 번호칸만 빼고 그냥 지나가고,
 * Enter 는 번호칸 onKeyDown 에만 걸려 있다. 그래도 여기서 stopPropagation 해 둔다 —
 * 나중에 전역 Enter 가 생겨도 정답을 고치다 승인되는 일이 없도록.
 */
function AnswerField({ value, onChange, editing, setEditing, disabled }) {
  const inputRef = useRef(null);
  const enteredWith = useRef(''); // ESC 로 되돌릴 값 — 편집에 들어간 순간의 원문

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const open = () => {
    if (disabled) return;
    enteredWith.current = value;
    setEditing(true);
  };

  // 고치는 중이면 disabled 여도 입력칸을 유지한다 — 저장하는 몇 초 동안 보기 상자로 바뀌었다가
  // 실패해서 되돌아오면 칸이 두 번 갈아엎히고, 고치던 자리도 잃는다.
  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        onBlur={() => setEditing(false)} // 벗어나면 확정 — 값은 이미 부모가 쥐고 있다
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            setEditing(false);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onChange(enteredWith.current); // 들어올 때 값으로 되돌린다
            setEditing(false);
          }
        }}
        placeholder="정답 없음"
        className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
      />
    );
  }

  const shown = parseAnswerForDisplay(value);
  return (
    // 보기 상자는 입력칸과 같은 치수를 쓴다 — 다르면 오갈 때마다 아래가 흔들린다
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="정답 — 눌러서 수정"
      title={disabled ? undefined : '눌러서 수정'}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          open();
        }
      }}
      className={`group flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
        disabled
          ? 'bg-gray-50 text-gray-400'
          : 'cursor-text hover:border-primary/50 focus:border-primary focus:outline-none'
      }`}
    >
      <span className="min-w-0 flex-1">
        {shown.mode === 'empty' ? (
          <span className="text-gray-400">정답 없음</span>
        ) : shown.mode === 'text' ? (
          <span>{shown.value}</span>
        ) : (
          // 문법이 깨진 값은 빨갛게 죽이지 않고 원문을 보여 준다 — 고치려면 무엇이 들었는지 봐야 한다
          <InlineMath
            math={shown.value}
            renderError={() => (
              <code className="rounded bg-gray-100 px-1 text-[0.9em] text-gray-700">{value}</code>
            )}
          />
        )}
      </span>
      {/* 고칠 수 있는 칸이라는 표시. 숨겼다 hover 에만 띄우지 않는다 — 마우스를 올려 보기 전에도
          알아야 한다. 평소에는 선 색(먹색 0.40 자리)으로 물러나 있다가 손이 오면 초록이 된다. */}
      {!disabled && (
        <Edit className="size-3.5 shrink-0 text-gray-300 transition-colors group-hover:text-primary" aria-hidden="true" />
      )}
    </div>
  );
}

// 값이 오기 전 자리를 지키는 회색 박스. 문제를 넘길 때 좌우 2단·섹션 제목·액션 바는 그대로 두고
// 값 자리만 이걸로 바뀐다 — 화면이 통째로 사라졌다 다시 서지 않게 하는 것이 목적.
// motion-safe: 는 이 저장소의 기존 관행(OcrProgressCard).
function Skel({ className = '' }) {
  return <div className={`rounded bg-gray-100 motion-safe:animate-pulse ${className}`} />;
}

// presigned URL 캐시 유효 판정 여유 — 남은 수명이 이보다 짧으면 캐시를 쓰지 않고 새로 받는다.
const IMAGE_URL_SAFETY_MS = 30_000;

// 원본이 안 뜨는 것을 화면이 말하게 한다.
// image-url 요청이 성공해도 그 객체가 S3 수명 정책으로 이미 만료돼 404가 오는 일이 있다 —
// 백엔드가 서명은 존재 확인 없이 발급하기 때문이다(2026-08-28 실측: 발급 1분 뒤 GET →
// 404 NoSuchKey + x-amz-delete-marker: true, 6분 뒤 재요청도 같은 삭제 표식).
// 그래서 image.error 는 켜지지 않고 <img> 만 조용히 깨져, 검수자는 빈 자리와 빨간 위치 상자만 본다.
// 계약 근거: docs/ocr-hub-v2/ocr-editor-lexical-contract-reply.md — "이미지가 S3 수명으로 만료된 경우
// '[그림]' 텍스트로 강등"(결정③) · "만료 = 원본 소실 상태 — 재업로드/재-OCR 로도 복구 불가하므로
// 안내 문구 권장"(FE 참고). 본문 속 크롭 그림은 그 폴백이 이미 있는데(BlockRenderer) 좌측 원본에만 없었다.
// 만료인지 일시적 네트워크 오류인지는 img 의 error 이벤트로 구별할 수 없다(이유를 주지 않는다).
// 그래서 단정하지 않고 둘 다 말한다 — 특히 '복구되지 않는다'를 말해야 바로 아래 [이 페이지 다시 OCR]을
// 헛되이 누르지 않는다.
const IMAGE_LOAD_FAILED_TEXT = '원본 이미지를 불러오지 못했습니다.\n'
  + '보관 기간이 지나 서버에서 사라졌을 수 있습니다 — 이 경우 [이 페이지 다시 OCR]로도 복구되지 않습니다.';

// AI 요청 창(AiRunModal)의 안내 끝에 붙는 한 줄 — 「비우면 프로젝트 기본 지시가 쓰인다」.
//
// **걸려 있을 때만 그린다.** 값이 비면 null 을 돌려 줄 자체를 없앤다 — 없는데 말하면 그것이 거짓이다.
// 이 창들은 지금 아무 말도 하지 않아서, 비우고 보내면 서버가 프로젝트 기본 지시를 쓰는데도
// 화면은 「아무 지시 없이 나간다」처럼 읽혔다.
//
// 갈래를 나누지 않는 이유: 기본 지시가 걸리는 곳이 run·reocr·analyze 셋인데(회신 §4-2 5-4)
// 이 모달의 AI 요청 창 넷이 부르는 것이 run 하나(이 페이지 다시 OCR)·reocr 하나(이 문제 다시 OCR)와
// analyze 둘(이 문제 분석·전체 분석)이라 **넷 다 해당한다.** 안 걸리는 것은 그림 재요청(figures/…/reconvert)·명세 변환인데 그 창은
// 여기가 아니라 FigureModal 이다.
//
// **입력칸에 미리 채우지 않는다.** 채우면 그 문장이 이 호출의 개별 지시(user_prompt)로 나가
// 서버가 문제 행의 reocr_prompt·analysis_prompt 에 기록한다(§4-2 5-3). 그러면 「기본값을 썼다」와
// 「같은 문장을 손으로 쳤다」가 서버 기록에서 갈리지 않는다.
//
// 같은 문구를 ProjectDetailPanel 도 갖는다(② 분리+OCR · ⑤ AI 분석 창). 거기서 가져오지 못하는 것은
// 순환 참조 때문이다 — ProjectDetailPanel 이 이 파일을 import 한다. 공용(common/)으로 올리는 것은
// 이 작업의 범위 밖이라, 세 번째 자리가 생기면 그때 올린다.
const DEFAULT_PROMPT_PREVIEW_MAX = 60; // 한 줄에 남는 폭. 지시는 500자까지 오므로 전문은 title 로 준다
function DefaultPromptNotice({ prompt }) {
  const v = String(prompt ?? '').trim();
  if (!v) return null;
  const shown = v.length > DEFAULT_PROMPT_PREVIEW_MAX ? `${v.slice(0, DEFAULT_PROMPT_PREVIEW_MAX)}…` : v;
  return (
    <p className="mt-2 text-xs text-gray-400" title={v}>
      지시를 비우고 실행하면 이 프로젝트의 기본 지시가 쓰입니다 — 「{shown}」
    </p>
  );
}

function OcrReviewModal({
  isOpen, problemId, projectId, items, closed = false, mode = 'ocr',
  // 서버 쪽에서 이 문제의 내용이 바뀌었을 수 있는 시점마다 증가한다(재분석 완료·매칭·승격).
  // 문제를 넘기지 않아도 상세를 다시 읽어야 하는 유일한 신호다.
  refreshKey = 0,
  page = 1, totalPages = 1, navPending = false, listError = null,
  onNavigate, onStepPage, onClose, onChanged,
  // 승인·반려 전용 경로. onChanged(목록 전체 재조회)와 갈라 둔 이유는 이 둘이 바꾸는 것이
  // 그 문제 한 건뿐이기 때문이다 — 부모는 그 줄만 갈아끼우고 표는 그대로 둔다.
  // 주지 않으면 종전대로 onChanged로 떨어진다(다른 화면이 이 모달을 쓰게 되어도 깨지지 않게).
  onReviewed = null,
  checkedIds, onChecked,
  // 좌측 '목록' 탭이 그릴 것들 — 목록 상태는 부모(OcrReviewSection)가 쥔다.
  // 모달이 스스로 목록을 불러오면 부모 화면과 어긋나고, 목록이 모달을 렌더하면 순환이 된다.
  listLoading = false, filter = 'all', onFilterChange, totalItems = 0,
  onPageChange, onClearChecked, onRetryList,
  onApproveAll, approving = null, approveResult = null, canApprove = true,
  onReanalyze = null, reanalyzing = false, analyzeLabel = '다시 분석',
  // 페이지 재실행(② 분리+OCR을 한 장만) — 검수 중 원본이 잘못 읽힌 것을 발견했을 때
  // ②로 되돌아가 범위를 손으로 넣는 왕복을 없앤다. 대상은 '지금 보고 있는 페이지'다.
  projectFiles = [], projectLocked = false, onRerunPage = null,
  // 프로젝트 공통 키워드 — 등록(승격) 때 이 프로젝트의 모든 문제에 붙는 라벨이다.
  // 문제 상세 응답에는 없는 값이라 부모(프로젝트 상세)가 넘겨 준다.
  projectKeywords = [],
  // 프로젝트 이름 — 제목 앞머리에 번호와 나란히 낸다. 문제 상세 응답에는 없다(프로젝트 메타다).
  // 안 넘어오면 '프로젝트'로 적는다 — 번호는 이미 있으니 이름이 비어도 무엇을 보는지는 남는다.
  projectName = '',
  // 프로젝트 기본 지시(default_prompt) — 요청에 개별 지시가 없을 때 서버가 대신 쓰는 문장이다
  // (BE-20260829-reply-fe-0828.md §4-2 5-4). **문제 상세 응답에는 없다**(프로젝트 메타다) —
  // projectKeywords 와 같은 이유·같은 자리로 부모(ProjectDetailPanel)가 자기 상세에서 꺼내 넘긴다.
  // 기본값이 빈 문자열인 것은 안전판이다: 다른 화면이 이 모달을 쓰면서 안 넘기면 안내를 아예 그리지
  // 않는다 — 걸려 있는지 모르는 채로 「기본 지시가 쓰입니다」라고 말하면 그것이 거짓이 된다.
  projectDefaultPrompt = '',
  // 한 문제만 보기 — 프로젝트 화면 밖(문제 v2 상세의 「OCR 등록」 배지 · ProblemHubV2/OcrSourceReviewModal)에서
  // 그 문제 하나를 띄울 때. 넘길 목록이 없으므로 이전·다음·목록 탭·「다음」을 말하는 안내를 걷는다(오너 지시 2026-10-02
  // 「해당 문제만, 다음·이전 필요 없음」). 편집·승인은 종전 규칙 그대로다 — 종료 프로젝트면 closed 로 잠긴다.
  singleProblem = false,
  // 문제은행 원본 보기(singleProblem)와 새 단일 OCR 작업은 다른 흐름이다. 기본 동작은 그대로 둔다.
  workflow = 'hub', singlePages = null, externalBusy = false, externalNotice = '',
  onBankSave = null, onSelectRegion = null, onDirtyChange = null,
}) {
  const singleWorkflow = workflow === 'single';
  const [singleActionBusy, setSingleActionBusy] = useState(false);
  const singleActionRef = useRef(false);
  const singleBusyRef = useRef(false);
  const { getAccessToken } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToastRaw] = useState(null); // { type: 'info'|'error', text }
  /**
   * **마지막 오류 문구를 붙잡아 둔다.** 붙여넣기 창은 검수 화면 위를 덮고 있어, 그 안에서 [저장]을
   * 눌러 실패했을 때 사유를 말하는 토스트가 창 뒤로 들어간다 — 사람 눈에는 아무 일도 일어나지
   * 않은 것으로 보인다(오너 보고 2026-09-04 「저장 버튼을 누른 뒤에도 … 할 수 있는 것이 없음」).
   * 상태가 아니라 ref 인 이유는 `await` 뒤의 닫힌 함수가 그 시점의 state 를 못 읽기 때문이다.
   */
  const lastErrorRef = useRef(null);
  const setToast = useCallback((t) => {
    if (t?.type === 'error' && t?.text) lastErrorRef.current = t.text;
    setToastRaw(t);
  }, []);

  // 편집 상태 (상세 로드 시 seed)
  const [answerText, setAnswerText] = useState('');
  // 정답을 원문으로 고치는 중인지. 문제를 넘기거나 편집을 취소하면 반드시 꺼진다(seedEdits) —
  // 켜진 채로 넘어가면 새 문제의 정답이 원문으로 열린 채 나타난다.
  const [answerEditing, setAnswerEditing] = useState(false);
  const [numberText, setNumberText] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [conceptIds, setConceptIds] = useState([]);
  // 본문·풀이 표시 모드: 'render' 편집 잠금 렌더 | 'editor' Lexical 편집기 | 'json' 정본 원문.
  // 셋 다 같은 정본(lexical)을 본다 — 다른 것은 고칠 수 있는지 여부뿐이다.
  // 기본은 'render' — 검수의 기본 동작은 훑기이고, 고칠 때만 [편집기]를 연다(seedEdits).
  const [bodyView, setBodyView] = useState('render');
  const [solutionView, setSolutionView] = useState('render');
  // Lexical 편집기 상태 통로 — 저장 시점에 getState/isDirty를 읽는다(OcrLexicalEditor.stateRef)
  const bodyEditorRef = useRef(null);
  const solutionEditorRef = useRef(null);
  // 모드 전환(보기/JSON)으로 편집기가 언마운트돼도 편집분을 잃지 않도록 보관하는 스냅샷.
  // { key, state, dirty } — key가 현재 문제·버전과 다르면 무시한다(이전 문제 잔재 차단).
  const bodyLexPendingRef = useRef(null);
  const solutionLexPendingRef = useRef(null);
  const [bodyEditorDirty, setBodyEditorDirty] = useState(false);
  const [solutionEditorDirty, setSolutionEditorDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(null); // 'approve' | 'reject' | null
  const [deleting, setDeleting] = useState(false);
  const [deletingSolution, setDeletingSolution] = useState(false); // 풀이만 삭제(문제 행은 유지)
  const [lexResetSeq, setLexResetSeq] = useState(0); // [편집 취소]가 편집기를 다시 세우는 카운터
  const [reocring, setReocring] = useState(false); // 이 문제 다시 OCR(동기) 진행 중
  // 열린 그림 편집 창 — { action, field, index, src, nodes }. null 이면 닫힘.
  const [figureTarget, setFigureTarget] = useState(null);
  /**
   * 편집기에 들어오려다 **가로채인** 이미지 — 붙여넣기 창이 뜬 동안 여기 머문다.
   * `{ field, src, altText, precedingSrcs, unsaved }`. 창에서 고른 뒤에야 어디론가 간다
   * (서버 등록·교체이거나, 편집기에 그대로 넣기).
   */
  const [pasteTarget, setPasteTarget] = useState(null);
  /**
   * AI 생성 — 만들 자리('body'|'solution') | null. 이 값이 곧 창의 열림 여부다.
   * 계약 §9-2 가 OCR 경로(`POST /api/v2/ocr/problems/{id}/figures/generate`)를 함께 열어 뒀는데
   * 화면에는 문제 편집에만 붙어 있었다(오너 지적 2026-09-04). 같은 창·같은 이름으로 맞춘다.
   */
  const [figGenField, setFigGenField] = useState(null);
  const [figGenBusy, setFigGenBusy] = useState(false);
  /**
   * 생성 창에 낼 「이 자리 그림들의 원본」 카드(2026-09-06 A 안) — `{ loading, items, token }`.
   * 창을 열 때 목록(GET …/figures)을 읽어 짓고(figureOrigins), `token` 은 그 응답의 updated_at 이다 —
   * 원본 카드로 재요청할 때의 낙관락으로 쓴다(방금 읽은 것이 가장 새 값이다).
   */
  const [figGenOrigins, setFigGenOrigins] = useState({ loading: false, items: [], token: null });
  const figGenSeqRef = useRef(0); // 창을 닫았다 다른 자리로 다시 열었을 때 늦게 온 목록이 덮지 않게
  /**
   * [이미지 추가] 가 여는 **갈래 고르기** 창 — 만들 자리('body'|'solution') | null.
   *
   * 버튼을 둘로 나누지 않고 하나로 둔 이유(오너 지시 2026-09-04): 머리줄에 버튼이 늘어나면
   * [이 문제 다시 OCR]·탭 스위처와 섞여 무엇이 무엇인지 읽히지 않는다. 사람이 하려는 일은
   * 「이 자리에 그림을 넣는다」 하나이고, AI 로 짓느냐 파일을 고르느냐는 **그다음 선택**이다.
   */
  const [figAddField, setFigAddField] = useState(null);
  /**
   * **서버가 가진 그림 골라 넣기** — 만들 자리('body'|'solution') | null.
   * 본문에서 지웠다가 되돌리고 싶을 때 쓴다. 서버가 이미 세는 그림이라 등록하지 않고
   * 편집기에 노드만 넣는다(OcrFigurePickModal 머리주석).
   */
  const [figPickField, setFigPickField] = useState(null);
  /** [이미지 추가] 가 여는 파일 고르기. 고른 파일은 붙여넣기와 **같은 창**으로 들어간다. */
  const imageInputRef = useRef(null);
  const imageInputFieldRef = useRef('body');
  // AI 요청 창 — 재-OCR · 이 문제 분석 · 목록 전체 분석 셋이 같은 창을 쓴다.
  // { kind, title, summary, warning } | null. 종전 window.confirm 셋의 문구가 그대로 들어온다.
  const [aiRun, setAiRun] = useState(null);
  // 개별 라벨(extra_labels) — 공통(프로젝트 키워드)·자동(N번)은 승격 시 서버가 붙이므로 여기엔 개별분만
  const [extraLabels, setExtraLabels] = useState([]);
  const [labelInput, setLabelInput] = useState('');
  // image 블록 크롭용 페이지 presigned URL — bbox는 그 kind 소스 페이지의 좌표계(Q2)라
  // 본문(kind=1)과 풀이(kind=2)를 각자의 페이지로 나눠 쥔다(좌측 뷰와 별개로 유지)
  const [bodyPageImage, setBodyPageImage] = useState(null); // { url } | null
  const [solutionPageImage, setSolutionPageImage] = useState(null); // { url } | null
  // 아래에서 정의되는 hasUnsavedChanges를 위쪽 effect가 쓴다 — 렌더마다 최신 것을 담아 둔다
  const hasUnsavedChangesRef = useRef(null);
  // 그림 작업 직전의 자동 저장(ensureSaved) — 의존성 없는 콜백(handleFigureAction)이 최신 것을 읽는 통로
  const ensureSavedRef = useRef(null);
  // 들어오는 이미지의 입구(takeIncomingImage) — 같은 이유로 ref 로 나른다
  const takeIncomingRef = useRef(null);
  // 그림 편집 콜백이 최신 상세를 읽는 통로. deps 에 detail 을 넣으면 상세가 바뀔 때마다
  // figureEditByField 가 새 객체가 되어 편집기 안 그림이 전부 다시 그려진다.
  const detailRef = useRef(null);

  // 좌측 소스 뷰
  const [leftTab, setLeftTab] = useState('source'); // 'source' 원본 | 'list' 문제 목록
  const [activeSourceIdx, setActiveSourceIdx] = useState(0);
  const [image, setImage] = useState({ url: null, loading: false, error: null });
  const [zoom, setZoom] = useState('fit');

  // 개념 칩을 누르면 뜨는 상세 — 저장소 공용 경로(9개 화면이 같은 훅을 쓴다)
  const {
    isModalOpen: conceptDetailOpen,
    loading: conceptDetailLoading,
    data: conceptDetailData,
    fetchAndShow: showConceptDetail,
    closeModal: closeConceptDetail,
  } = useMathceptDetail({ handleAuth: false });
  // 상세가 떠 있는 동안에는 ←→ 가 뒤 화면(검수)의 문제를 넘기면 안 된다 — 최신 값을 ref로 읽는다
  const conceptDetailOpenRef = useRef(false);
  const figureOpenRef = useRef(false);

  const detailSeqRef = useRef(0);
  const imageSeqRef = useRef(0);
  const numberInputRef = useRef(null); // 문제가 바뀔 때마다 번호칸에 포커스 — Enter만으로 훑어 나갈 수 있게
  const problemIdRef = useRef(problemId); // in-flight 저장/검수 응답이 다른 문제 화면을 덮지 않도록 현재 대상 추적
  const lastIdxRef = useRef(0); // 승인/반려로 항목이 필터에서 빠져도 이전/다음이 이어지도록 마지막 위치 기억
  const imageCacheRef = useRef(new Map()); // page_id → { url, expiresAt } — 같은 페이지 재요청으로 인한 이미지 깜빡임 방지
  const rightPaneRef = useRef(null); // 문제가 바뀌면 편집 패널을 맨 위로
  const imagePaneRef = useRef(null); // 페이지가 바뀔 때만 원본 스크롤을 맨 위로(같은 페이지면 보던 자리 유지)

  useEffect(() => { problemIdRef.current = problemId; }, [problemId]);
  useEffect(() => { conceptDetailOpenRef.current = conceptDetailOpen; }, [conceptDetailOpen]);
  // 문제가 바뀌면 열린 그림 창을 반드시 닫는다.
  // 그 창은 problemId 를 prop 으로 받으므로, 남겨 두면 **새 문제의 id 에 이전 문제의 순번**을
  // 보낸다 — 이 화면이 통째로 막으려는 바로 그 사고다. 키보드 가드(아래)만으로는 부족하다:
  // 좌측 목록 클릭·승인 후 자동 이동처럼 부모가 문제를 바꾸는 경로가 따로 있다.
  useEffect(() => { setFigureTarget(null); }, [problemId]);

  // opts.keepView: 표시 모드를 건드리지 않는다. [편집 취소] 전용이다 — 취소는 '지금 보고 있는
  // 것을 되돌리는' 일이라, 탭까지 [보기]로 옮겨 버리면 되돌아간 결과를 편집기에서 확인할 수 없다.
  // (되돌리기 자체는 lexResetSeq 가 key 를 바꿔 성립한다 — readLexEdit 의 key 대조 참조.)
  const seedEdits = useCallback((d, opts = {}) => {
    setAnswerText(d?.answer ?? '');
    setAnswerEditing(false); // 값과 함께 표시 모드도 되돌린다 — 문제 이동·편집 취소 양쪽이 여기를 지난다
    setNumberText(d?.problem_number != null ? String(d.problem_number) : '');
    setDifficulty(d?.difficulty != null ? String(d.difficulty) : '');
    setConceptIds(Array.isArray(d?.concept_ids) ? d.concept_ids : []);
    setExtraLabels(toLabelList(d?.extra_labels));
    setLabelInput('');
    // 표시 모드는 문제마다 [보기]로 되돌린다.
    // 한때 편집본이 있으면 [편집기]를 기본으로 열었다 — "편집본이 정본이니 승인 판단을 정본으로"가
    // 근거였는데, 세 탭이 모두 lexical 정본을 그리게 되면서(2850485) 그 근거가 사라졌다.
    // 남은 것은 부작용뿐이었다: 편집기가 켜진 채 문제를 넘기면 커서가 본문에 들어가 ←→ 가 죽고
    // Enter 가 저장 대신 본문에 빈 줄을 넣었다.
    if (!opts.keepView) {
      setBodyView('render');
      setSolutionView('render');
    }
    setBodyEditorDirty(false);
    setSolutionEditorDirty(false);
    // 새 상세를 seed하면 이전 편집 스냅샷은 무효(키가 어긋나 어차피 안 쓰이지만 명시적으로 비운다)
    bodyLexPendingRef.current = null;
    solutionLexPendingRef.current = null;
  }, []);

  // opts 는 seedEdits 로 그대로 넘어간다 — 지금 쓰는 것은 keepView 하나다(표시 모드를 지킨다).
  const loadDetail = useCallback(async (opts = {}) => {
    if (problemId == null) return;
    const seq = (detailSeqRef.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const d = await fetchProblemDetail(problemId, getAccessToken);
      if (seq !== detailSeqRef.current) return;
      setDetail(d);
      seedEdits(d, opts);
      /**
       * **이 문제가 직접 참조하는 조각**을 골라 둔다(2026-09-06 회신 · dev `537792e`).
       *
       * 종전에는 첫째를 골랐고, 그 첫째가 남의 조각인 문제가 있어 빨간 상자가 다른 문제를 가리켰다
       * (오너 보고 · 실측 683 — 제 본문은 둘째였다). 순서로는 가를 수 없었다: 613 은 첫째가 제 것이라
       * 규칙 둘(읽는 차례 · 본문 그림 bbox)이 각각 반례를 맞았다. 이제 서버가 `sources[].is_body` 로
       * 직접 참조를 표시하므로 그것으로 고른다 — 정렬 계약(직접 참조가 먼저)도 함께 왔지만, 표시로 고르는
       * 편이 순서가 바뀌어도 옳다(sourcePick 머리주석).
       */
      setActiveSourceIdx(pickBodySourceIndex(d?.sources));
      // 배율은 검수자가 맞춰 둔 작업 설정이지 문제의 속성이 아니다 — 문제를 넘겨도 유지한다.
    } catch (e) {
      if (seq !== detailSeqRef.current) return;
      setDetail(null);
      setLoadError(e.message || '문제 상세를 불러오지 못했습니다.');
    } finally {
      if (seq === detailSeqRef.current) setLoading(false);
    }
  }, [problemId, getAccessToken, seedEdits]);

  // 문제 전환 시 상세 로드 + 토스트 초기화
  useEffect(() => {
    if (!isOpen || problemId == null) return;
    setToast(null);
    // 앞 문제에서 치던 개념 검색어는 ConceptPicker 가 resetKey(problemId)로 비운다
    loadDetail();
  }, [isOpen, problemId, loadDetail, setToast]);

  // 서버에서 이 문제가 바뀌었다는 신호(refreshKey)가 오면 상세를 다시 읽는다.
  // 재분석은 열어 둔 그 문제의 정답·풀이·검수상태를 갈아 끼우는데, 화면은 problemId가 바뀔 때만
  // 다시 읽었다 — 그대로 두면 사람이 한 번도 보지 못한 새 AI 산출물을 승인하게 된다.
  // 편집 중이면 덮지 않는다. 고치던 값을 서버 값으로 되돌리는 것이 더 나쁘다.
  const prevRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (prevRefreshKeyRef.current === refreshKey) return;
    prevRefreshKeyRef.current = refreshKey;
    if (!isOpen || problemId == null) return;
    if (hasUnsavedChangesRef.current && hasUnsavedChangesRef.current()) {
      setToast({ type: 'info', text: '서버에서 이 문제가 바뀌었습니다 — 저장한 뒤 다시 열면 최신 값이 보입니다.' });
      return;
    }
    // 표시 모드는 그대로 둔다 — 이 신호는 **내 저장**(onChanged → 부모 재조회)으로도 온다. 종전에는
    // 여기서 탭이 [보기]로 돌아가, 편집기에서 [저장]을 누를 때마다 편집기가 닫혔다(2026-09-04 실측).
    loadDetail({ keepView: true });
  }, [refreshKey, isOpen, problemId, loadDetail, setToast]);

  // 닫을 때 상세를 비운다 — 남겨 두면 다음에 '다른' 문제를 열 때 흐림 전환의 밑그림으로
  // 지난 문제의 내용이 비친다. 문제 사이를 넘어갈 때만 이전 값을 밑그림으로 쓴다.
  useEffect(() => {
    if (!isOpen) setDetail(null);
  }, [isOpen]);

  // 개념 상세는 이 모달의 자식이지만 state는 여기 남는다 — 검수를 닫아도 열림 상태가 살아 있어
  // 다음에 열 때 되살아났다(응답이 늦게 도착하면 검수를 닫은 뒤에 혼자 뜨기도 했다).
  // 문제를 넘길 때도 닫는다 — 앞 문제의 개념 상세가 다음 문제 위에 떠 있을 이유가 없다.
  useEffect(() => {
    if (!isOpen) closeConceptDetail();
  }, [isOpen, closeConceptDetail]);
  useEffect(() => { closeConceptDetail(); }, [problemId, closeConceptDetail]);

  const sources = Array.isArray(detail?.sources) ? detail.sources : [];
  const activeSource = sources[activeSourceIdx] || null;

  // 프로젝트 페이지 목록 — 원본을 앞뒤로 넘길 때 이웃 페이지를 여기서 찾는다.
  // 예전에는 page_id가 파일 안에서 연속이라는 관찰에 기대어 id를 지어냈는데, 파일이 여럿이면
  // 경계에서 남의 파일 페이지를 열게 된다. 목록 API가 M1부터 있었음을 백엔드가 알려 줬다.
  // 실패해도 모달은 그대로 뜬다 — 넘기기만 막힌다(빈 배열).
  const [pages, setPages] = useState(null); // null = 아직 모름 · [] = 못 받음
  useEffect(() => {
    if (!isOpen || !projectId) return undefined;
    if (singleWorkflow) { setPages(singlePages || []); return undefined; }
    let alive = true;
    fetchAllProjectPages(projectId, getAccessToken)
      .then((list) => { if (alive) setPages(list); })
      .catch(() => { if (alive) setPages([]); });
    return () => { alive = false; };
  }, [isOpen, projectId, getAccessToken, singleWorkflow, singlePages]);

  // 이 문제가 가리키지 않는 페이지를 잠깐 넘겨 볼 때만 값이 든다. 다른 페이지를 보는 동안에는
  // 빨간 위치 상자를 그리지 않는다(그 페이지의 문제가 아니다).
  const [pageOverride, setPageOverride] = useState(null); // { pageId, pageIndex } | null
  useEffect(() => { setPageOverride(null); }, [problemId, activeSourceIdx]);
  const viewPageId = pageOverride ? pageOverride.pageId : activeSource?.page_id;
  const viewPageIndex = pageOverride ? pageOverride.pageIndex : (activeSource?.page_index ?? 0);
  const isOtherPage = pageOverride != null;

  // 지금 보는 페이지와 같은 파일에 속한 페이지들 — 넘기기는 이 안에서만 한다.
  const siblingPages = useMemo(() => {
    if (!pages || !pages.length || !viewPageId) return [];
    const cur = pages.find((p) => String(p.page_id) === String(viewPageId));
    if (!cur) return [];
    return pages
      .filter((p) => p.file_uuid === cur.file_uuid)
      .sort((a, b) => (a.page_index ?? 0) - (b.page_index ?? 0));
  }, [pages, viewPageId]);
  const siblingAt = siblingPages.findIndex((p) => String(p.page_id) === String(viewPageId));
  const canStepPage = (delta) => siblingAt >= 0 && !!siblingPages[siblingAt + delta];
  const stepPageBy = (delta) => {
    const next = siblingAt >= 0 ? siblingPages[siblingAt + delta] : null;
    if (!next) return;
    // 원래 소스 페이지로 되돌아오면 override를 풀어 위치 상자를 다시 그린다
    if (String(next.page_id) === String(activeSource?.page_id)) { setPageOverride(null); return; }
    setPageOverride({ pageId: next.page_id, pageIndex: next.page_index ?? 0 });
  };

  // ── 이 페이지 다시 OCR ──
  // 대상은 지금 화면에 보이는 한 장(viewPageIndex)이다. ②의 [분리 + OCR 실행]과 같은 API를 부르되
  // 범위를 그 한 장으로 좁힌다 — 검수 중 원본이 잘못 읽힌 것을 보고 ②까지 되돌아가지 않게.
  // run은 '파일 안의 페이지 번호'로 도는데 페이지 목록에는 file_uuid·파일명만 있고 정수 file_id가
  // 없다. 상세의 files[]에서 찾아 함께 보낸다 — 파일을 지정하지 않으면 파일이 여럿일 때
  // 엉뚱한 파일의 같은 번호를 다시 읽을 수 있다. 못 찾으면 부르지 않고 버튼을 잠근다.
  const [rerunning, setRerunning] = useState(false);
  const targetFileId = useMemo(() => {
    const files = Array.isArray(projectFiles) ? projectFiles : [];
    if (files.length === 1) return files[0]?.file_id ?? null; // 하나뿐이면 자명하다
    const name = activeSource?.file_name;
    if (!name) return null;
    const hit = files.filter((f) => f.original_filename === name);
    return hit.length === 1 ? (hit[0].file_id ?? null) : null; // 같은 이름이 둘이면 고르지 않는다
  }, [projectFiles, activeSource]);

  // 창을 연다 — 누르기 전에 알아야 할 것(쓰임새·비용·반영 시점)을 창 본문으로 말한다.
  // 종전 window.confirm 의 문구가 이 창으로 옮겨 왔다(AiRunModal 머리 주석 — 둘 다 두면 창이 두 번 뜬다).
  // 지시 칸을 다는 이유: 쪽이 길어 모델 출력이 상한에서 잘리면 그 쪽은 실패(500)로 남는데, 그때의
  // 복구 절차가 「같은 버튼에 『이 쪽에서 12번 문항만 추출하세요』를 넣어 다시 누르기」다
  // (BE-20260914-reply-page-reocr-force.md §5-3). 확인창으로는 그 한 줄을 받을 수 없다.
  // 대상 쪽은 **여는 순간** 굳혀 창에 싣는다 — 창이 말한 쪽과 실제로 보내는 쪽이 갈리지 않게.
  const openRerunPageAi = () => {
    if (!onRerunPage || rerunning || projectLocked || targetFileId == null) return;
    const pageNo = viewPageIndex + 1;
    const fileName = activeSource?.file_name || '이 파일';
    setAiRun({
      kind: 'rerunPage',
      title: '이 페이지 다시 OCR',
      target: { pageIndex: viewPageIndex, fileId: targetFileId, pageNo },
      // 한 문단으로 흘리면 대상(파일·쪽)이 긴 파일명과 함께 문단 속에 묻힌다(2026-09-14 오너 지적).
      // 대상 한 줄 + 알아 둘 것 목록으로 가른다. 목록 모양은 그림 삭제 창(FigureDeleteModal)의
      // list-disc · space-y-1.5 와 맞춘다. 마지막 항목은 회신 §6-2 꼬리(그림 후처리엔 지시가 안 붙음)다.
      summary: (
        <>
          <p>
            <span className="font-medium text-gray-900">{fileName}</span> {pageNo}쪽을 처음부터 다시 읽습니다.
          </p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5">
            <li>이 쪽에서 문제가 통째로 빠졌을 때 씁니다. 한 문제만 잘못 읽혔으면 [이 문제 다시 OCR]이 맞습니다.</li>
            <li>읽기가 끝난 뒤 ③ 문제 매칭을 다시 실행해야 빠졌던 문제가 목록에 생깁니다.</li>
            <li>읽다가 잘려 실패하면 아래 지시에 빠진 번호만 적어 다시 누르세요.</li>
            <li>지시는 본문·풀이·정답을 읽는 데만 쓰이고, 그림 변환에는 적용되지 않습니다.</li>
          </ul>
        </>
      ),
      // 회신 §3 — 다시 읽기가 바꾸는 행은 검수 대기(100)뿐이다: 내용이 바뀐 조각을 가리키면 편집본이
      // 폐기되고, 번호를 옮겨 둔 행은 다시 매칭할 때 그 번호의 본문으로 덮일 수 있다. 승인·반려·등록은 불변.
      warning: '되돌릴 수 없고, 쪽 하나를 읽는 비용이 듭니다. 이 쪽에서 아직 검수 대기인 문제는 내용이 바뀌면'
        + ' 직접 고친 본문·풀이(편집본)가 초기화되고, 번호를 옮겨 둔 것은 다시 매칭할 때 덮일 수 있습니다'
        + ' — 지킬 문제는 먼저 승인해 두세요. 승인·반려·등록된 문제는 그대로 둡니다.',
    });
  };

  const handleRerunPage = async (userPrompt, target) => {
    if (!onRerunPage || rerunning || projectLocked || !target || target.fileId == null) return;
    setRerunning(true);
    setToast(null);
    try {
      // 결과 문구의 갈래(범위 오해석 · 서버 오류 · 처리중 건너뜀 · 쪽 없음 · 정상)는 pageRerunMessage 가 쥔다.
      const r = await onRerunPage({ pageIndex: target.pageIndex, fileId: target.fileId, userPrompt });
      setToast(pageRerunMessage({ pageNo: target.pageNo, ...(r || {}) }));
    } catch (e) {
      setToast({ type: 'error', text: e.message || '페이지 재실행에 실패했습니다.' });
    } finally {
      setRerunning(false);
    }
  };

  // <img> 가 실제로 못 그렸을 때 그 사실을 상태로 올린다 — URL 확보 성공과 그림이 뜨는 것은 별개다.
  // 지금 그리고 있는 그 URL 의 실패일 때만 켠다: 페이지를 넘기는 중 앞 페이지의 늦은 실패가
  // 새 페이지의 상태를 덮으면, 멀쩡한 원본 위에 실패 문구가 뜬다.
  const handleImageLoadError = useCallback((failedUrl) => {
    setImage((prev) => (prev.url === failedUrl
      ? { ...prev, loading: false, error: IMAGE_LOAD_FAILED_TEXT }
      : prev));
  }, []);

  // 소스 전환 시 페이지 이미지 presigned URL 확보.
  // 만료 전까지는 page_id별로 캐시해 같은 URL 문자열을 돌려준다 — 연속된 문제가 같은 페이지에
  // 있을 때(대부분) <img src>가 그대로라 브라우저가 다시 받지 않고, 빨간 bbox만 옮겨 간다.
  // 캐시 없이 매번 새 서명을 받으면 URL이 달라져 같은 그림을 다시 그리며 깜빡인다.
  useEffect(() => {
    const pageId = viewPageId;
    if (!isOpen || !pageId) {
      imageSeqRef.current += 1; // in-flight 응답 무효화 — 이전 문제 이미지·bbox가 되살아나는 것 방지(R1)
      setImage({ url: null, loading: false, error: null });
      return;
    }
    const seq = (imageSeqRef.current += 1);
    const hit = imageCacheRef.current.get(pageId);
    if (hit) {
      if (hit.expiresAt > Date.now() + IMAGE_URL_SAFETY_MS) {
        setImage({ url: hit.url, loading: false, error: null });
        return;
      }
      imageCacheRef.current.delete(pageId); // 만료분은 버린다
    }
    setImage({ url: null, loading: true, error: null });
    fetchPageImageUrl(pageId, getAccessToken)
      .then((d) => {
        if (seq !== imageSeqRef.current) return;
        const ttlMs = Number(d?.expires_in) > 0 ? Number(d.expires_in) * 1000 : 0;
        // 수명을 모르거나 여유보다 짧으면 캐시하지 않는다 — 만료된 URL을 재사용하는 것이 더 나쁘다
        if (d?.url && ttlMs > IMAGE_URL_SAFETY_MS) {
          imageCacheRef.current.set(pageId, { url: d.url, expiresAt: Date.now() + ttlMs });
        }
        setImage({ url: d?.url || null, loading: false, error: d?.url ? null : '이미지 URL이 비어 있습니다.' });
      })
      .catch((e) => {
        if (seq !== imageSeqRef.current) return;
        setImage({ url: null, loading: false, error: e.message || '이미지를 불러오지 못했습니다.' });
      });
    // problemId·activeSourceIdx도 키에 포함 — 같은 page_id의 다른 소스/문제로 전환해도 이 훅이
    // 다시 돌아야 실패 상태를 재시도한다. 재요청 여부는 위 캐시가 판단한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, problemId, activeSourceIdx, viewPageId, getAccessToken]);

  // image 블록 크롭용 페이지 URL — 본문은 kind=1 첫 소스, 풀이는 kind=2 첫 소스(없으면 본문 페이지
  // 폴백: 같은 지면에 풀이가 붙는 형태가 흔하다). bbox가 그 페이지 좌표(BE 회신 Q2)라 kind별로
  // 나눠 확보한다. 캐시는 좌측 뷰와 공유. 실패는 [그림] 뱃지 폴백이라 별도 표면화하지 않는다.
  useEffect(() => {
    const srcs = Array.isArray(detail?.sources) ? detail.sources : [];
    const bodyPageId = (srcs.find((s) => Number(s?.kind) === 1) || srcs[0] || null)?.page_id ?? null;
    const solPageId = (srcs.find((s) => Number(s?.kind) === 2) || null)?.page_id ?? bodyPageId;
    if (!isOpen) { setBodyPageImage(null); setSolutionPageImage(null); return undefined; }
    let alive = true;
    const resolve = (pageId, apply) => {
      if (pageId == null) { apply(null); return; }
      const hit = imageCacheRef.current.get(pageId);
      if (hit && hit.expiresAt > Date.now() + IMAGE_URL_SAFETY_MS) {
        apply({ url: hit.url });
        return;
      }
      apply(null);
      fetchPageImageUrl(pageId, getAccessToken)
        .then((d) => {
          if (!alive) return;
          const ttlMs = Number(d?.expires_in) > 0 ? Number(d.expires_in) * 1000 : 0;
          if (d?.url && ttlMs > IMAGE_URL_SAFETY_MS) {
            imageCacheRef.current.set(pageId, { url: d.url, expiresAt: Date.now() + ttlMs });
          }
          apply(d?.url ? { url: d.url } : null);
        })
        .catch(() => { if (alive) apply(null); });
    };
    resolve(bodyPageId, (v) => {
      if (!alive) return;
      setBodyPageImage(v);
      if (solPageId === bodyPageId) setSolutionPageImage(v); // 같은 페이지면 요청 1회로 양쪽을 채운다
    });
    if (solPageId !== bodyPageId) {
      resolve(solPageId, (v) => { if (alive) setSolutionPageImage(v); });
    }
    return () => { alive = false; };
  }, [isOpen, detail, getAccessToken]);

  // 새로고침·창 닫기 이탈 경고 — 미저장 변경이 있을 때만(편집기 0단계 안전망)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (hasUnsavedChangesRef.current?.() || singleBusyRef.current) {
        e.preventDefault();
        e.returnValue = ''; // Chrome 관례 — 값이 있어야 확인 창이 뜬다
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isOpen]);

  // 문제가 바뀌면 편집 패널은 맨 위부터. 원본 이미지 쪽은 페이지가 바뀔 때만 되돌린다
  // (같은 페이지에서 다음 문제로 갈 때 보던 자리가 튀지 않도록).
  useEffect(() => {
    if (rightPaneRef.current) rightPaneRef.current.scrollTop = 0;
  }, [problemId]);
  useEffect(() => {
    // 이 컨테이너는 [원본]과 [목록]을 함께 담는다 — 목록을 보는 중이면 건드리지 않는다.
    // 문제를 고르면 viewPageId가 따라 바뀌므로, 조건이 없으면 목록에서 한 건 고를 때마다
    // 표가 맨 위로 튀어 보던 자리를 잃었다(다음 건을 고르려면 매번 다시 내려야 했다).
    // leftTab은 deps에서 뺀다 — 탭을 오가는 것 자체는 스크롤을 되돌릴 이유가 아니다.
    if (leftTab !== 'source') return;
    if (imagePaneRef.current) imagePaneRef.current.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPageId]);

  // 상세가 들어오면 번호칸에 포커스를 준다 — 번호만 훑을 때 Enter만으로 끝까지 갈 수 있게.
  // preventScroll: 포커스 때문에 우측 패널이 번호칸으로 튀면 위 스크롤 초기화가 무의미해진다.
  useEffect(() => {
    if (!isOpen || loading || !detail) return;
    numberInputRef.current?.focus({ preventScroll: true });
    numberInputRef.current?.select();
    // detail 객체 전체가 아니라 대상 id에만 반응 — 저장 응답으로 detail이 갈릴 때 포커스를 뺏지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loading, problemId]);

  // ── Lexical 편집분 읽기 ──
  // 편집기 인스턴스 키 = 문제 + 서버 버전. 저장·재OCR로 updated_at이 바뀌면 리마운트되고,
  // 그 이전 스냅샷은 키 불일치로 자동 폐기된다(낡은 편집이 새 서버 상태를 덮지 않게).
  // lexResetSeq: [편집 취소]가 편집기를 강제로 다시 세우는 손잡이. 키가 문제·서버버전만으로
  // 만들어지면 저장 전에는 값이 그대로라 리마운트가 일어나지 않아, 되돌렸다면서 편집기 안 글자는
  // 남아 있었다(배지만 꺼졌다). 세 탭이 같은 값을 보는 지금은 그 거짓이 세 곳에 다 보인다.
  const lexKey = (prefix) => `${prefix}:${detail?.ocr_prob_id ?? ''}:${detail?.updated_at ?? ''}:${lexResetSeq}`;
  // root에 자식이 하나도 없는 editorState — Lexical이 언마운트하며 남기는 껍데기의 모양이다.
  const isEmptyEditorState = (s) => {
    try { return (JSON.parse(String(s))?.root?.children || []).length === 0; } catch (_) { return false; }
  };
  // 편집기 언마운트(모드 전환·이동) 시 스냅샷 보관 — '열 수 없는 편집'은 보관하지 않는다.
  // 보관해 봐야 저장 경로(readLexEdit)가 걸러 내므로, 배지만 '수정됨'으로 남아 저장·이탈 가드와
  // 어긋난다. 여기서 비우고 배지를 내리고 사유를 말해 세 신호를 한 방향으로 맞춘다.
  const keepLexSnapshot = (label, snap, pendingRef, key, setDirty) => {
    // 탭 전환 직전에 flushLexBeforeSwitch가 이미 판정했으면 그것이 정본이다.
    // 언마운트 순간의 editorState는 믿을 수 없다 — Lexical이 플러그인을 정리하면서 root를
    // 비운 뒤라 항상 빈 문서로 읽힌다(실측: children [] · 90바이트). 그대로 두면 멀쩡한
    // 편집분이 '못 여는 편집'으로 몰려 버려지고, 거짓 경고까지 뜬다.
    const kept = pendingRef.current;
    if (kept?.flushed && kept.key === key) {
      kept.flushed = false;
      return;
    }
    const usable = snap?.dirty ? normalizeEditorState(snap.state) : null;
    if (usable) {
      pendingRef.current = { state: usable, dirty: true, key };
      return;
    }
    pendingRef.current = null;
    setDirty(false);
    // 언마운트 순간의 editorState는 늘 빈 문서다(위 실측) — 그것을 '사용자가 다 지웠다'로 읽으면
    // 탭 전환·편집 취소·문제 이동마다 거짓 경고가 뜬다. 진짜로 비운 경우는 편집기가 살아 있는
    // 동안 flushLexBeforeSwitch가 잡아 말한다.
    if (snap?.dirty && !isEmptyEditorState(snap.state)) {
      setToast({ type: 'error', text: `${label}을 완전히 비울 수는 없어 저장본으로 되돌렸습니다.` });
    }
  };

  // 마운트 중이면 라이브 상태가 정본(서버 값 대비 dirty일 때만), 언마운트 상태면 스냅샷을 쓴다.
  // 편집이 없으면 null — 저장 patch에서 빠진다.
  const readLexEdit = (ref, pendingRef, key) => {
    // 라이브 편집기는 '이 문제의 것'일 때만 정본이다. 문제를 넘긴 직후의 렌더에서는 이전 문제의
    // 편집기가 아직 언마운트되지 않아, key를 안 보면 남의 본문이 새 문제 화면으로 딸려 온다
    // (실제로 4번에서 고친 글자가 5번 본문에 나타났다). 남의 것이면 아래 스냅샷 경로로 내려간다.
    if (ref.current && ref.current.key === key) {
      return ref.current.isDirty() ? ref.current.getState() : null;
    }
    const p = pendingRef.current;
    // 스냅샷은 열 수 있는 형태일 때만 쓴다 — 마운트가 서버 값으로 폴백한 상황에서
    // 저장만 깨진 스냅샷을 보내면 화면과 저장물이 갈린다.
    if (p && p.key === key && p.dirty) return normalizeEditorState(p.state);
    return null;
  };

  // 지금 이 문제의 '정본' 본문/풀이 — 미저장 편집분이 있으면 그것, 없으면 서버 값.
  // 보기·편집기·JSON 세 탭이 전부 이 값을 본다. 탭마다 다른 원본을 보면 검수자가 승인한 것과
  // 승격되는 것이 갈린다(payload 는 OCR 원문이고 승격에 쓰이는 것은 lexical 이다 — 규약 회신 결정 ①).
  const lexNow = (ref, pendingRef, key, serverValue) =>
    readLexEdit(ref, pendingRef, key) ?? serverValue ?? null;

  // 탭을 떠나기 '전에' 편집기 상태를 스냅샷으로 밀어 넣는다.
  // 언마운트 cleanup(keepLexSnapshot)은 새 탭이 렌더된 뒤에 돌기 때문에, 그것만 믿으면
  // 보기·JSON 탭이 한 박자 늦은 내용을 그린다(편집기에서 고치고 보기로 가면 고치기 전 글이 뜬다).
  // 편집기가 아직 살아 있는 이 시점의 상태만 믿을 수 있으므로, 보관 여부와 '못 여는 편집'
  // 경고까지 여기서 끝낸다. flushed 표시를 남겨 뒤이어 도는 언마운트 cleanup이 이 판정을
  // 빈 문서로 덮지 않게 한다.
  const flushLexBeforeSwitch = (label, ref, pendingRef, key, setDirty) => {
    const st = ref.current;
    if (!st) return;
    let usable = null;
    let dirty = false;
    try {
      dirty = st.isDirty();
      usable = dirty ? normalizeEditorState(st.getState()) : null;
    } catch (_) {
      return; // 상태를 못 읽으면 판정을 미룬다 — 언마운트 cleanup이 맡는다
    }
    if (dirty && !usable) {
      setToast({ type: 'error', text: `${label}을 완전히 비울 수는 없어 저장본으로 되돌렸습니다.` });
    }
    if (!usable) setDirty(false);
    pendingRef.current = usable
      ? { state: usable, dirty: true, key, flushed: true }
      : { dirty: false, key, flushed: true };
  };

  // editorState JSON 문자열을 사람이 읽을 들여쓰기로. 파스가 안 되면 원문 그대로 보여준다.
  const prettyLexical = (s) => {
    if (typeof s !== 'string' || s.trim() === '') return '';
    try { return JSON.stringify(JSON.parse(s), null, 2); } catch (_) { return s; }
  };

  // ── 저장: 변경된 키만 모아 PATCH ──
  // quiet: 번호칸에서 Enter로 넘어갈 때 쓰는 모드 — 바뀐 게 없어도 "변경 사항이 없습니다"를 띄우지 않는다.
  // 반환값은 '다음 문제로 넘어가도 되는가'다. 입력이 잘못됐거나 저장이 실패하면 false라 그 자리에 머문다.
  // keepView: 표시 모드(보기/편집기)를 그대로 둔다 — **기본이 참**이다(2026-09-04). 종전에는 저장이
  //   seedEdits 를 기본값으로 불러 탭이 [보기]로 돌아갔고, 편집기에서 [저장]을 누를 때마다 편집기가
  //   닫혔다. 저장은 하던 자리를 바꾸지 않는다 — 탭을 [보기]로 되돌리는 것은 문제를 넘길 때뿐이다
  //   (loadDetail 이 problemId 변경으로 불릴 때). 그림 작업 직전의 자동 저장(ensureSaved)도 같은 값이다.
  const handleSave = async ({ quiet = false, keepView = true } = {}) => {
    if (saving || !detail || closed) return false; // 종료 프로젝트는 읽기 전용
    if (loading || navPending) return false; // 전환 중 — detail이 아직 넘어오기 전 문제의 값이다
    // 등록(승격)된 문제도 이제 고칠 수 있다 — FE-5 §1-3 의 「PATCH 시 423」은 폐기됐다
    // (BE-20260830-reply-ocr-repromote-history-close.md §3, 삭제만 423). 잠그지 않는다.
    const patch = {};

    const origAnswer = normAnswer(detail.answer);
    const curAnswer = normAnswer(answerText);
    if (curAnswer !== origAnswer) patch.answer = curAnswer;

    if (!singleWorkflow) {
      if (numberText.trim() === '') {
        setToast({ type: 'error', text: '인덱스는 비울 수 없습니다.' });
        return false;
      }
      const num = Number(numberText);
      if (!Number.isInteger(num) || num <= 0) {
        setToast({ type: 'error', text: '인덱스는 1 이상의 정수여야 합니다.' });
        return false;
      }
      if (num !== detail.problem_number) patch.problem_number = num;
    }

    const origDiff = detail.difficulty ?? null;
    const curDiff = difficulty === '' ? null : Number(difficulty);
    if (curDiff !== origDiff) patch.difficulty = curDiff;

    const origCids = Array.isArray(detail.concept_ids) ? detail.concept_ids : [];
    if ([...conceptIds].sort().join(',') !== [...origCids].sort().join(',')) {
      patch.concept_ids = conceptIds.length > 0 ? conceptIds : null;
    }

    // 개별 라벨 — 순서 유지 비교(라벨은 순서도 의미가 있을 수 있어 정렬하지 않는다).
    // 입력칸의 미확정 텍스트도 커밋해 함께 저장한다(Enter를 잊은 경우 유실 방지).
    const curLabels = effectiveLabels();
    if (singleWorkflow && curLabels.some((label) => [...label].length > 100)) {
      setToast({ type: 'error', text: '라벨은 100자 이하로 입력해 주세요.' });
      return false;
    }
    const origLabels = toLabelList(detail.extra_labels);
    if (JSON.stringify(curLabels) !== JSON.stringify(origLabels)) {
      patch.extra_labels = curLabels.length > 0 ? curLabels : null;
    }

    // Lexical 편집본 — 편집분이 있으면 직렬화 문자열 그대로 전송(FE 무변환 — 규약 회신 §2-2)
    const bodyLex = readLexEdit(bodyEditorRef, bodyLexPendingRef, lexKey('b'));
    const solLex = readLexEdit(solutionEditorRef, solutionLexPendingRef, lexKey('s'));
    const bodyLexDirty = bodyLex != null;
    const solLexDirty = solLex != null;
    if (bodyLexDirty) patch.body_lexical = bodyLex;
    if (solLexDirty) patch.solution_lexical = solLex;

    // payload(OCR 원문)는 이 화면에서 고치지 않는다 — 편집은 Lexical 한 곳으로 모았다.
    // 풀이를 지우는 것만 예외이고, 그것은 [풀이 삭제] 버튼이 따로 보낸다(handleDeleteSolution).

    /**
     * ── 본문이 바뀐 field 의 그림을 **서버 목록과 맞춘다** — 삭제 → 등록 → 본문 PATCH 순 ──
     *
     * 그림 삭제는 DELETE API 여야 한다(회신 §2 Q8) — 본문 PATCH 로 image 노드가 사라지면 서버는
     * 그 사실을 몰라 지운 그림의 이력이 남고 뒤 그림들의 index 가 어긋난다. 어떤 방법으로 지웠든
     * (Backspace·범위 선택·드래그·[지우기]) 저장할 때 삭제 API 로 돌린다(purgeRemovedFigures).
     * 붙여넣은 이미지는 같은 자리에서 등록한다(registerNewFigures).
     *
     * **자는 「저장본 대 새 문서」가 아니라 「서버 목록 대 새 문서」다**(2026-09-04 실측 정정).
     * 종전에는 저장본과 새 문서의 그림 **개수**를 견줘 줄었을 때만 삭제를 돌렸는데, ① 한 장을
     * 지우고 한 장을 넣어 수가 같아지면 지운 그림이 서버에 남고 ② 한 번 그렇게 남은 그림은 저장본에도
     * 없어 **다음 저장이 영영 잡지 못했다**(도판이 서버에만 남아 화면이 「서버는 2장으로 셉니다」).
     * 본문이 바뀔 때마다 서버 목록을 한 번 읽어 맞추면 그 구멍이 없다 — 어차피 등록이 같은 조회를 한다.
     *
     * 문서를 읽지 못한 field(null)는 건드리지 않는다 — 모르는 것으로 지우거나 등록하지 않는다.
     */
    const lexPlans = [
      bodyLexDirty ? { field: 'body', nodes: collectFigureNodes(bodyLex) } : null,
      solLexDirty ? { field: 'solution', nodes: collectFigureNodes(solLex) } : null,
    ].filter((x) => x && Array.isArray(x.nodes));

    if (Object.keys(patch).length === 0) {
      if (!quiet) setToast({ type: 'info', text: '변경 사항이 없습니다.' });
      return true; // 바뀐 게 없으면 그대로 다음으로 넘어가도 된다
    }

    setSaving(true);
    setToast(null);
    // 이번 저장이 서버 그림에 한 일 — 저장 문구가 함께 말한다(지운·등록한 장수, 등록 못 한 웹 이미지).
    // 그림 삭제는 되돌릴 수 없는 서버 쓰기라, 조용히 넘기면 사람은 「본문만 저장됐다」고 믿는다.
    let purgedCount = 0;
    let registeredCount = 0;
    let skippedCount = 0;
    try {
      // 낙관적 잠금(BE 회신 Q8) — 상세의 updated_at을 그대로 되돌려 보낸다. 그 사이 타처(다른 창·재-OCR)
      // 수정이 있었으면 409로 막혀 무흔적 덮어쓰기가 없다.
      // **삭제가 이 값을 전진시키므로** 그 뒤에 싣는다.
      let token = detail.updated_at;
      if (lexPlans.length > 0) {
        const r = await purgeRemovedFigures('ocr', detail.ocr_prob_id, lexPlans, { expectedUpdatedAt: token }, getAccessToken);
        if (r === AUTH_REDIRECT) return false;
        if (!r.ok) {
          // 못 가르는 경우다(대조할 src 없는 그림이 일부만 사라짐 등). 본문도 저장하지 않는다 —
          // 저장하면 서버가 모르는 삭제가 되어 이 관문이 애초에 막으려던 일이 일어난다.
          setToast({ type: 'error', text: describePurgeFailure(r.reason, r.field) });
          return false;
        }
        token = r.updatedAt;
        purgedCount = Object.values(r.deleted || {}).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0);
      }

      /**
       * **본문에 새로 들어온 이미지를 서버 그림으로 등록한다**(오너 지시 2026-09-04).
       *
       * 삭제(위)를 **먼저** 하고 등록을 나중에 하는 이유: 삭제는 지금 서버 목록의 순번으로
       * 지정되고, 등록은 그 뒤 상태에 자리를 잡는다. 순서를 바꾸면 등록이 순번을 밀어
       * 삭제가 엉뚱한 그림을 지운다. `registerNewFigures` 는 목록을 다시 읽어 계획을 세운다.
       *
       * 붙여넣기 창을 거치지 않은 경로(웹페이지에서 복사한 `<img>` 는 삽입 명령을 타지 않는다)로
       * 들어온 이미지도 여기서 함께 등록된다 — 어느 길로 들어왔든 저장하면 서버가 세는 그림이 된다.
       */
      if (lexPlans.length > 0) {
        const r = await registerNewFigures('ocr', detail.ocr_prob_id, lexPlans, { expectedUpdatedAt: token }, getAccessToken);
        if (r === AUTH_REDIRECT) return false;
        if (!r.ok) {
          // 등록할 수 없는 이미지가 섞여 있다 — 본문도 저장하지 않는다. 저장하면 그 이미지가
          // 서버가 모르는 채 편집본에 남아, 그 field 의 그림 편집이 잠긴다.
          setToast({ type: 'error', text: describeRegisterFailure(r.reason, r.field) });
          return false;
        }
        token = r.updatedAt;
        registeredCount = Object.values(r.registered || {}).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0);
        skippedCount = Object.values(r.skipped || {}).reduce((s, v) => s + (Number(v) || 0), 0);
      }

      if (token) patch.expected_updated_at = token;

      const d = await patchProblem(detail.ocr_prob_id, patch, getAccessToken);
      if (d === null) return false; // 401 리다이렉트 중
      // 응답 도착 시점에 다른 문제로 이동해 있으면 화면 상태를 덮지 않는다(목록 갱신만)
      if (String(d.ocr_prob_id) === String(problemIdRef.current)) {
        detailSeqRef.current += 1; // in-flight loadDetail 구응답이 저장 결과를 되덮는 것 방지
        setDetail(d);
        // 렌더를 기다리지 않고 바로 갱신한다 — 저장 직후 이어지는 그림 작업(handleFigureAction 의
        // 자동 저장)이 이 값을 읽는데, setState 뒤의 await 이음새에서는 아직 옛 상세가 들어 있다.
        detailRef.current = d;
        seedEdits(d, { keepView });
        const figureNote = [
          purgedCount > 0 ? `그림 ${purgedCount}장 삭제` : null,
          registeredCount > 0 ? `그림 ${registeredCount}장 등록` : null,
        ].filter(Boolean).join(' · ');
        const tail = [
          skippedCount > 0 ? `웹 주소 이미지 ${skippedCount}장은 그림으로 등록할 수 없어 참고 이미지로 남았습니다.` : null,
        ].filter(Boolean);
        setToast({
          type: tail.length > 0 ? 'error' : 'info',
          text: [figureNote ? `저장되었습니다 — ${figureNote}.` : '저장되었습니다.', ...tail].join(' '),
        });
      }
      onChanged && onChanged();
      return true;
    } catch (e) {
      /**
       * **삭제가 중간에 멈췄다** — 이미 지운 것은 서버에 반영돼 있다. 그 사실을 반드시 먼저
       * 말한다: 「저장에 실패했습니다」로만 끝내면 사람은 아무 일도 없었다고 믿고 같은 것을
       * 다시 지운다(그러면 그때는 다른 그림이 지워진다 — 순번이 이미 당겨졌으므로).
       */
      if (e?.partial?.deleted?.length) {
        setToast({
          type: 'error',
          text: describePartialPurge(e.partial.deleted.length, e.partial.field, describeFigureError(e).text),
        });
        return false;
      }
      // 409는 이제 두 갈래 — ① 동시 수정(expected_updated_at 불일치, message "다른 곳에서 먼저 수정…")
      // ② 번호 중복(message에 기존 문제 id). message로 구분한다.
      const isConflict = e.httpStatus === 409 && String(e.message || '').includes('먼저 수정');
      if (isConflict) {
        // 편집 상태는 건드리지 않는다(자동 재조회로 seed하면 사용자의 미저장 편집이 날아간다).
        // 안내만 하고, 이동/재열기로 최신을 받은 뒤 다시 적용하게 한다.
        setToast({ type: 'error', text: `${e.message} 이 문제를 다시 열면 최신 내용을 불러옵니다.` });
        return false;
      }
      // 번호 중복 — 여러 문제의 번호를 한꺼번에 바꾸는 일은 여기서 하나씩 할 수 없다
      // (서로의 번호를 밟아 반드시 막힌다) → '인덱스 다시 매기기'로 안내한다.
      const hint = !singleWorkflow && e.httpStatus === 409
        ? " 여러 문제의 인덱스를 한꺼번에 옮기려면 문제 목록 위 '인덱스 다시 매기기'를 쓰세요."
        : '';
      setToast({ type: 'error', text: (e.message || '저장에 실패했습니다.') + hint });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ── 승인/반려 ──
  // 성공하면 true. Enter(approveAndNext)가 이 값을 보고 다음 문제로 넘어갈지 정한다 —
  // 승인이 거부됐는데 넘어가면 검수자가 '했다고 여기고' 지나친다.
  // quiet: 성공 토스트를 띄우지 않는다 — 승인하고 바로 다음 문제로 넘어가는 Enter 흐름에서는
  // 토스트가 뜨자마자 문제 전환이 지워(224행) 한 프레임 깜빡이고, 남더라도 '다음 문제' 화면에
  // 앞 문제의 결과가 떠 있는 꼴이 된다. 승인 결과는 줄 배경과 인덱스 지도가 말한다.
  const handleReview = async (action, { quiet = false } = {}) => {
    if (singleWorkflow || reviewing || !detail || closed) return false; // 종료 프로젝트는 읽기 전용
    if (loading || navPending) return false; // 전환 중 — detail이 아직 넘어오기 전 문제의 값이다
    // 등록된 문제의 검수 변경도 열렸다(같은 계약 §3). 잠그지 않는다.
    // 반려는 AI 산출물을 지운다 — 백엔드가 반려 시 ai_generated 분을 제거하고
    // analysis_status를 100으로 되돌려 재분석 대상에 복귀시킨다(사람이 넣은 값은 그대로 둔다).
    // 지워질 것이 실제로 있을 때만 묻는다. 반려는 흔한 동작이라 매번 확인을 띄우면 방해가 된다.
    if (action === 'reject' && (detail.ai_generated?.answer || detail.ai_generated?.solution)) {
      const what = [detail.ai_generated?.answer && '정답', detail.ai_generated?.solution && '풀이']
        .filter(Boolean).join('·');
      if (!window.confirm(
        `반려하면 AI가 만든 ${what}${josa(what, '이', '가')} 지워집니다.\n`
        + '사람이 넣은 값은 그대로 둡니다.\n'
        + '다시 승인하면 그때 분석 대상으로 돌아갑니다 — 반려한 채로 두면 분석도 등록도 되지 않습니다.\n\n'
        + '반려할까요?',
      )) return false;
    }
    // 고쳐 놓고 승인/반려를 누르는 것은 한 동작이다 — 먼저 저장한다.
    // 이 줄이 없으면 검수 응답(상세 전문)이 화면을 덮으면서 저장하지 않은 편집이 조용히 사라진다.
    // 저장이 실패하면(인덱스 비었거나 JSON 오류·409) 검수로 넘어가지 않는다 — 사유는 handleSave가 말한다.
    if (hasUnsavedChanges()) {
      const saved = await handleSave({ quiet: true });
      if (!saved) return false;
    }
    setReviewing(action);
    setToast(null);
    const targetId = detail.ocr_prob_id;
    try {
      const d = await reviewProblem(targetId, action, getAccessToken);
      if (d === null) return false; // 401 리다이렉트 중
      // 응답 도착 시점에 다른 문제로 이동해 있으면 화면 상태를 덮지 않는다(목록 갱신만)
      if (String(targetId) === String(problemIdRef.current)) {
        if (d && d.ocr_prob_id != null && d.body_payload !== undefined) {
          detailSeqRef.current += 1; // in-flight loadDetail 구응답이 검수 결과를 되덮는 것 방지
          setDetail(d);
          seedEdits(d);
        } else {
          await loadDetail(); // 응답이 상세 전문이 아니면 재조회
        }
        if (!quiet) setToast({ type: 'info', text: action === 'approve' ? '승인되었습니다.' : '반려되었습니다.' });
      }
      // 목록은 다시 읽지 않는다 — 바뀐 것은 이 한 줄뿐이라 그 줄의 배지만 갈아끼운다.
      // 표를 통째로 다시 읽으면 「로딩 중…」이 표 자리를 차지해 스크롤이 맨 위로 돌아갔고,
      // Enter로 훑는 동안 승인 한 건마다 그 일이 일어났다.
      if (onReviewed) onReviewed(targetId, rowPatchFromReview(d, action));
      else onChanged && onChanged();
      return true;
    } catch (e) {
      setToast({ type: 'error', text: e.message || '처리에 실패했습니다.' });
      return false;
    } finally {
      setReviewing(null);
    }
  };

  // ── 삭제 ──
  // 매칭이 잘못 만든 행·필요 없어진 행을 치운다. soft delete지만 화면에서 되살릴 길은 없으므로
  // 한 번 묻는다. 승격·종료는 백엔드가 423으로 막고, 여기서도 busy로 선제 차단한다.
  const handleDelete = async () => {
    if (singleWorkflow) return;
    if (deleting || !detail || busy || transitioning) return;
    const label = !singleWorkflow && detail.problem_number != null ? `인덱스 ${detail.problem_number} 문제` : '이 문제';
    if (!window.confirm(
      `${label}를 삭제합니다. 되돌릴 수 없습니다.\n\n`
      + '같은 인덱스에 본문이 하나만 남게 되면 충돌 표시는 자동으로 풀립니다.',
    )) return;
    setDeleting(true);
    setToast(null);
    try {
      const d = await deleteProblem(detail.ocr_prob_id, getAccessToken);
      if (d === null) return; // 401 → /signin
      // 지운 문제를 계속 보고 있지 않도록 이웃으로 옮긴 뒤 목록을 갱신한다.
      // 방금 지운 문제의 미저장 변경을 물을 이유가 없어 confirmDiscard를 거치지 않는다.
      // 배열의 끝이 곧 전체의 끝은 아니다 — goPrev/goNext와 같은 판정으로 인접 페이지까지 잇는다.
      // 이게 없으면 2페이지에 한 건 남은 상태에서 지울 때 1페이지에 20건이 있는데도 모달이 닫혔다.
      const goTo = nextItem || prevItem;
      if (goTo && onNavigate) onNavigate(goTo.ocr_prob_id);
      else if (canNextPage) onStepPage(1);
      else if (canPrevPage) onStepPage(-1);
      else if (onClose) onClose(); // 정말 마지막 한 건이었다
      onChanged && onChanged();
    } catch (e) {
      setToast({
        type: 'error',
        text: e.httpStatus === 423
          ? '등록된 문제나 종료된 프로젝트의 문제는 삭제할 수 없습니다.'
          : (e.message || '삭제에 실패했습니다.'),
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── 풀이만 삭제 ──
  // JSON 탭을 비워 저장하던 경로를 대신한다(그 탭은 읽기 전용이 됐다).
  // solution_payload를 null로 보내면 서버 규칙상 풀이 Lexical 편집본도 함께 무효화되므로
  // (규약 회신 §2-2) 한 번의 요청으로 두 표현이 같이 비워진다 — 따로 지울 필요가 없다.
  const handleDeleteSolution = async () => {
    if (deletingSolution || !detail || busy || transitioning) return;
    const hasEdit = !!detail.lexical_edited?.solution;
    if (!window.confirm(
      '이 문제의 풀이를 지웁니다. 본문·정답은 그대로 둡니다.\n'
      + (hasEdit ? '편집기에서 고친 풀이 편집본도 함께 사라집니다.\n' : '')
      + '\n지울까요?',
    )) return;
    setDeletingSolution(true);
    setToast(null);
    const targetId = detail.ocr_prob_id;
    try {
      const d = await patchProblem(targetId, { solution_payload: null }, getAccessToken);
      if (d === null) return; // 401 → /signin
      if (String(targetId) === String(problemIdRef.current)) {
        detailSeqRef.current += 1; // in-flight loadDetail 구응답이 이 결과를 되덮지 않게
        setDetail(d);
        seedEdits(d);
        setToast({ type: 'info', text: '풀이를 지웠습니다.' });
      }
      onChanged && onChanged();
    } catch (e) {
      setToast({
        type: 'error',
        text: e.httpStatus === 423
          ? '등록된 문제나 종료된 프로젝트는 수정할 수 없습니다.'
          : (e.message || '풀이 삭제에 실패했습니다.'),
      });
    } finally {
      setDeletingSolution(false);
    }
  };

  // ── 이 문제만 분석 ──
  // 분석이 끝난 문제도 지정하면 다시 돌아간다(처리 중인 것만 제외). 실행·폴링은 부모가 쥔다.
  // 아직 안 돌린 문제(100)도 여기서 부를 수 있다 — 그러지 않으면 재-OCR로 분석이 초기화된 뒤
  // 이 화면에서 다시 걸 방법이 없어, 검수자가 ⑤ 단계까지 갔다 와야 한다.
  const openAnalyzeOneAi = () => {
    if (!onReanalyze || !detail || busy || transitioning) return;
    const label = !singleWorkflow && detail.problem_number != null ? `인덱스 ${detail.problem_number} 문제` : '이 문제';
    // 등록된 행은 정답·풀이를 다시 만들지 않고 개념·난이도만 다시 매긴다 — 새 AI 답이 다시 등록으로
    // 은행의 사람 정정을 덮지 않게(BE-20260925 B14, 서버 _mark_regen). 창이 그 차이를 말한다.
    const rowPromoted = !!detail.promoted_problem_uuid || detail.promoted === true;
    setAiRun({
      kind: 'analyzeOne',
      title: analysisStatusValue === 200 ? '이 문제 다시 분석' : '이 문제 AI 분석',
      summary: rowPromoted
        ? `${label}의 개념·난이도를 다시 매깁니다. 등록된 문제라 정답·풀이는 다시 만들지 않습니다.`
        : `${label}의 개념·난이도를 매기고 AI 정답·풀이를 만듭니다.`,
      // 지울 것이 있을 때만 그 사실을 말한다 — 처음 돌리는 문제에 「바뀝니다」는 거짓이다.
      // B14 부터 지정 재분석은 AI 가 만든 정답·풀이를 **분석이 성공했을 때만** 새 결과로 바꾸고,
      // 사람이 넣거나 고친 값(AI 표시가 없는 값)은 건드리지 않는다.
      warning: analysisStatusValue === 200 && !rowPromoted
        ? 'AI 가 만든 정답·풀이는 새 결과로 바뀝니다(분석이 실패하면 그대로 둡니다). 사람이 넣거나 고친 정답·풀이는 그대로 둡니다.'
        : null,
    });
  };

  const handleReanalyzeOne = async (userPrompt) => {
    if (!onReanalyze || !detail || busy || transitioning) return;
    if (singleWorkflow) {
      await runSingleAction(async () => {
        try { return await onReanalyze([detail.ocr_prob_id], userPrompt); }
        // 실패해도 서버의 분석 상태는 바뀔 수 있다. 최신 상세 확인까지 작업 잠금을 유지한다.
        // 조회도 실패하면 loadDetail이 이전 상세를 비우므로 옛 완료 상태로 저장할 수 없다.
        finally { await loadDetail({ keepView: true }); }
      });
      return;
    }
    // 고쳐 놓고 다시 분석하는 것도 한 동작 — 먼저 저장한다(W6, handleReocr 와 같은 규칙).
    // 저장하지 않으면 서버는 옛 값을 기준으로 다시 만들고 고친 정답은 무시된다. 저장하면 PATCH 가
    // 그 필드의 AI 표시를 지워 사람 값이 지켜진다(BE-20260925 B14). 저장이 실패하면 분석을 걸지 않는다.
    if (hasUnsavedChanges()) {
      const saved = await handleSave({ quiet: true });
      if (!saved) return;
    }
    setToast(null);
    const r = await onReanalyze([detail.ocr_prob_id], userPrompt);
    if (r) setToast({ type: r.ok ? 'info' : 'error', text: r.message });
  };

  // ── 이 문제 다시 OCR (동기 단건 재추출 — reocr 계약) ──
  // 원본 영역만 크롭해 다시 읽는다. review_status 유지 · AI 산출물 제거+분석 100(반려 규칙) ·
  // 결과를 확인한 뒤 같은 화면의 AI 분석 버튼으로 분석한다. 자동 실행하지 않는다.
  // 창을 연다 — 지워질 것이 있으면 경고를 만들어 넘긴다(종전 window.confirm 과 같은 판정).
  const openReocrAi = () => {
    if (reocring || !detail || busy || transitioning) return;
    const label = !singleWorkflow && detail.problem_number != null ? `인덱스 ${detail.problem_number} 문제` : '이 문제';
    const hasAi = !!(aiGen?.answer || aiGen?.solution);
    const hasLexEdit = !!(detail.lexical_edited?.body || detail.lexical_edited?.solution)
      || readLexEdit(bodyEditorRef, bodyLexPendingRef, lexKey('b')) != null
      || readLexEdit(solutionEditorRef, solutionLexPendingRef, lexKey('s')) != null;
    let warning = null;
    if (hasAi || hasLexEdit) {
      const what = [aiGen?.answer && 'AI 생성 정답', aiGen?.solution && 'AI 생성 풀이'].filter(Boolean).join('·');
      warning = [
        hasAi ? `AI 가 만든 ${what}${josa(what, '이', '가')} 지워집니다.` : null,
        hasLexEdit ? '직접 수정한 본문·풀이(편집본)도 초기화됩니다.' : null,
      ].filter(Boolean).join(' ');
    }
    setAiRun({
      kind: 'reocr',
      title: '이 문제 다시 OCR',
      // 마지막 문장은 회신 §6-2 꼬리다 — 「OCR run·reocr 의 지시는 본문/풀이/정답 읽기
      // 프롬프트에만 붙고 **그림 후처리(figure pass)에는 붙지 않습니다**」. 안 적으면 사람이
      // 여기에 「원은 8개다」를 쓰고 그림이 안 고쳐지는 것을 고장으로 읽는다.
      // 그림에 대한 지시는 그림 창의 [AI 요청]가 받는다(figures/…/reconvert).
      summary: `${label}의 원본을 다시 읽습니다. 사람이 넣은 ${singleWorkflow ? '정답·개념·라벨' : '정답·번호·개념·라벨'}은 그대로 둡니다.`
        + (singleWorkflow ? ' 본문만 다시 읽습니다. 아래 지시는 그림 변환에는 적용되지 않습니다.' : ' 아래 지시는 본문·풀이·정답을 읽는 데만 쓰이고, 그림 변환에는 적용되지 않습니다.'),
      warning,
    });
  };

  const handleReocr = async (userPrompt) => {
    if (reocring || !detail || busy || transitioning || (singleWorkflow && singleActionRef.current)) return;
    if (singleWorkflow) singleActionRef.current = true;
    setReocring(true);
    setToast(null);
    const targetId = detail.ocr_prob_id;
    try {
      // 사람 편집은 읽기 전에만 저장한다. 읽기 뒤에 저장하면 새 OCR을 옛 편집값으로 덮게 된다.
      if (hasUnsavedChanges() && !(await handleSave({ quiet: true }))) return;
      // kinds 생략 = 원본 전부. userPrompt 는 AiRunModal 이 받아 온 사람의 지시다 —
      // 받아 놓고 싣지 않으면 화면이 지시를 물어 놓고 버리는 셈이라 거짓말이 된다.
      // 서버가 받는다(회신 §4-1): strip 후 500자 이하, 프롬프트 말미 `[추가 지시]` 블록,
      // 문제 `reocr_prompt` 에 기록. ocrApi.reocrProblem 이 빈 값이면 키를 아예 안 싣는다.
      const d = await reocrProblem(targetId, { userPrompt, ...(singleWorkflow ? { kinds: [1] } : {}) }, getAccessToken);
      if (d === null) return; // 401 리다이렉트 중
      if (String(targetId) === String(problemIdRef.current)) {
        detailSeqRef.current += 1; // in-flight loadDetail 구응답이 재OCR 결과를 되덮는 것 방지
        setDetail(d);
        seedEdits(d);
        setToast({ type: 'info', text: `다시 읽었습니다. 결과 확인 후 필요하면 [${singleWorkflow ? 'AI 분석' : '이 문제 AI 분석'}]을 실행하세요.` });
      }
      onChanged && onChanged();
    } catch (e) {
      if (e.httpStatus === 404) {
        // 없는·이미 삭제된 문제 — 문구 없이 목록을 새로 읽고 모달을 닫는다(요청서 §4.2)
        onChanged && onChanged();
        onClose && onClose();
        return;
      }
      // 423 = 승격·종료 둘 다(계약 §1) — 한쪽만 말하면 종료 프로젝트에서 거짓 안내가 된다
      const text = e.httpStatus === 423 ? '등록된 문제거나 종료된 프로젝트라 다시 읽을 수 없습니다.'
        : e.httpStatus === 409 ? '진행 중입니다 — 잠시 뒤 다시 시도하세요.'
          : e.httpStatus === 422 ? (e.message || '원본 위치 정보가 없어 다시 읽을 수 없습니다.')
            : (e.message || '다시 OCR에 실패했습니다.');
      setToast({ type: 'error', text });
    } finally {
      setReocring(false);
      if (singleWorkflow) singleActionRef.current = false;
    }
  };

  // ── 라벨(개별 extra_labels) 편집 ──
  const splitLabels = splitLabelText;
  const mergeLabels = (prev, added) => {
    const next = [...prev];
    added.forEach((v) => { if (!next.includes(v)) next.push(v); });
    return next;
  };
  const addLabel = () => {
    const added = splitLabels(labelInput);
    if (singleWorkflow && added.some((label) => [...label].length > 100)) {
      setToast({ type: 'error', text: '라벨은 100자 이하로 입력해 주세요.' });
      return;
    }
    if (added.length === 0) return;
    setExtraLabels((prev) => mergeLabels(prev, added));
    setLabelInput('');
  };
  // 입력칸에 남은 미확정 텍스트까지 포함한 최종 라벨 — 저장·dirty 판정 공용
  // (칩 추가(Enter)를 잊고 저장해도 유실되지 않게 — ProjectCreateModal 제출 관례와 동일)
  const effectiveLabels = () => mergeLabels(extraLabels, splitLabels(labelInput));

  // ── 등록되면 이 문제에 붙을 라벨 ──
  // 승격은 공통(프로젝트 키워드) ∪ 개별(extra_labels)을 합쳐 문제은행에 싣는다
  // (BE 회신 Q6 — ProjectDetailPanel.handlePromote 의 확인 문구와 같은 규칙).
  // 자동 「N번」은 여기에 없다 — 그 값은 지면 번호가 아니라 인덱스(원본 등장 순서)라
  // 문제은행에서 교재 번호와 섞이므로 승격에서 끈다(handlePromote 의 auto_labels:false).
  // 지면 번호는 매칭 직후 개별 라벨로 굳혀 파란 칩으로 뜬다.
  // 공통 키워드는 서버가 붙이는 것이라 이 화면에서 뗄 수 없다. 그래서 안 보여 주는 대신
  // 자물쇠 칩으로 함께 보여 준다 — 검수자가 「이 문제에 무엇이 붙는가」를 등록 전에 세려면
  // 두 출처가 한 줄에 있어야 한다. 지금까지는 개별만 그려 대부분의 문제가 「없음」이었다.
  const lockedLabels = useMemo(() => {
    const out = [];
    const kw = Array.isArray(projectKeywords) ? projectKeywords : [];
    kw.map((v) => String(v ?? '').trim()).filter(Boolean).forEach((v) => {
      // 같은 값을 개별로도 쳤으면 회색 칩은 접는다 — 승격은 합집합이라 실제로 붙는 것은 하나인데
      // 칩이 둘이면 두 개로 읽힌다. 개별 칩을 떼면 이 회색 칩이 다시 나타나, 떼도 공통으로
      // 붙는다는 사실을 화면이 그대로 말한다.
      if (!extraLabels.includes(v)) {
        out.push({ text: v, hint: '프로젝트 공통 키워드 — 등록할 때 이 프로젝트의 모든 문제에 붙습니다. 빼려면 프로젝트 키워드를 고칩니다.' });
      }
    });
    return out;
  }, [projectKeywords, extraLabels]);

  // 「없음」 판정과 개수 표기의 기준 — 세 출처를 합친 것이 등록 뒤 실제로 붙는 라벨이다
  const promotionLabelCount = lockedLabels.length + extraLabels.length;

  // ── 미저장 변경 감지 — 이동·닫기 전 confirm(R4) ──
  const hasUnsavedChanges = () => {
    // 문제 전환 로딩 중에는 편집 state가 아직 이전 문제의 stale 값 — 이미 버리기로 확인한
    // 변경에 이중 confirm이 뜨는 오탐 방지(N1)
    if (loading || !detail) return false;
    if (normAnswer(answerText) !== normAnswer(detail.answer)) return true;
    // 숫자 정규화 비교 — handleSave의 diff 판정과 일치("07"·" 7" 입력이 스퓨리어스 confirm을 내지 않도록)
    const numNorm = numberText.trim() === '' ? '' : String(Number(numberText));
    if (!singleWorkflow && numNorm !== (detail.problem_number != null ? String(detail.problem_number) : '')) return true;
    if (difficulty !== (detail.difficulty != null ? String(detail.difficulty) : '')) return true;
    const origCids = Array.isArray(detail.concept_ids) ? detail.concept_ids : [];
    if ([...conceptIds].sort().join(',') !== [...origCids].sort().join(',')) return true;
    const origLabels = toLabelList(detail.extra_labels);
    if (JSON.stringify(effectiveLabels()) !== JSON.stringify(origLabels)) return true;
    // Lexical 편집기 — state 플래그(리렌더용)가 아니라 라이브/스냅샷을 직접 읽는다(최신값)
    if (readLexEdit(bodyEditorRef, bodyLexPendingRef, lexKey('b')) != null) return true;
    if (readLexEdit(solutionEditorRef, solutionLexPendingRef, lexKey('s')) != null) return true;
    return false;
  };
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  detailRef.current = detail;
  figureOpenRef.current = figureTarget != null;

  /**
   * **그림을 서버에 쓰기 전에 본문을 먼저 저장한다** — 그림 창·AI 생성·이미지 교체가 다 이것을 지난다.
   *
   * 그 셋은 서버 본문을 바로 바꾸고 화면이 상세를 다시 읽는데, 그 순간 저장하지 않은 편집이 덮인다.
   * 종전에는 「먼저 [저장]하세요」로 막았고, 사람은 창을 닫고 저장하고 다시 여는 왕복을 했다
   * (오너 지시 2026-09-04 「되돌아가야 하는 동선은 없어야 함」). 저장은 붙여넣은 이미지의 등록과
   * 지운 그림의 삭제이기도 하므로, 이것을 지나면 문서와 서버 목록이 같은 그림을 같은 순서로 센다.
   * 실패하면 사유는 handleSave 가 말하고, 부르는 쪽은 그 자리에서 멈춘다.
   */
  const ensureSaved = async () => {
    if (!hasUnsavedChanges()) return true;
    return handleSave({ quiet: true, keepView: true });
  };
  ensureSavedRef.current = ensureSaved;

  const confirmDiscard = () =>
    !hasUnsavedChanges() || window.confirm('저장하지 않은 변경 사항이 있습니다. 버리고 이동할까요?');

  const guardedNavigate = (id) => {
    if (!onNavigate || id == null) return;
    if (confirmDiscard()) onNavigate(id);
  };

  const guardedClose = () => {
    if (singleWorkflow && (singleBusyRef.current || singleActionRef.current)) return;
    if (confirmDiscard()) onClose();
  };

  // 공유 저장을 먼저 실행한다. 편집 중인 Lexical·미확정 라벨·그림 정합성 처리를 모두 유지한다.
  const runSingleAction = async (action, bank = false) => {
    if (!action || busy || transitioning || singleActionRef.current) return;
    const checkAnalysis = () => {
      if (!bank || detailRef.current?.analysis_status === 200) return true;
      setToast({ type: 'error', text: 'AI 분석을 완료한 뒤 문제를 저장해 주세요.' });
      return false;
    };
    if (!checkAnalysis()) return;
    singleActionRef.current = true; setSingleActionBusy(true);
    try {
      if (!(await handleSave({ quiet: true }))) return;
      // 편집 PATCH 응답에서 분석 상태가 바뀌었을 수 있으므로 최신 상세를 다시 확인한다.
      if (!checkAnalysis()) return;
      if (bank && effectiveLabels().length === 0 && !window.confirm('라벨 없이 문제를 저장할까요?')) return;
      const result = await action();
      if (result?.message) setToast({ type: result.ok ? 'info' : 'error', text: result.message });
    } catch (e) { setToast({ type: 'error', text: e.message || '작업을 완료하지 못했습니다.' }); }
    finally { singleActionRef.current = false; setSingleActionBusy(false); }
  };

  // ── 이전/다음 (현재 목록 필터 순서 기준) ──
  // 승인/반려로 현재 항목이 필터에서 빠져도(재조회 후 curIdx=-1) 마지막 위치를 앵커로 내비 유지.
  const list = Array.isArray(items) ? items : [];
  const curIdx = list.findIndex((it) => String(it.ocr_prob_id) === String(problemId));
  // 지금 보는 문제의 목록 행. curIdx와 달리 필터·페이지가 바뀌어도 id로 찾으므로 끊기지 않는다 —
  // 상세에 없는 값(analysis_status 등)을 보완하는 데 쓴다.
  const listRow = list.find((it) => String(it.ocr_prob_id) === String(problemId)) || null;
  useEffect(() => {
    if (curIdx >= 0) lastIdxRef.current = curIdx;
  }, [curIdx]);
  let prevItem = null;
  let nextItem = null;
  if (curIdx >= 0) {
    prevItem = curIdx > 0 ? list[curIdx - 1] : null;
    nextItem = curIdx < list.length - 1 ? list[curIdx + 1] : null;
  } else if (list.length > 0) {
    const anchor = Math.min(lastIdxRef.current, list.length); // 이탈 항목 자리 = 다음 항목이 그 자리로 당겨짐
    nextItem = list[anchor] ?? null;
    prevItem = list[anchor - 1] ?? null;
  }
  // 목록이 페이지로 나뉘어 있으면 배열 양끝에서 멈추지 않고 인접 페이지로 이어간다.
  // 부모가 그 페이지를 불러온 뒤 마지막(이전)·첫(다음) 문제를 열어 준다 → 비활성은 전체의 양끝에서만.
  const canPrevPage = !!onStepPage && page > 1;
  const canNextPage = !!onStepPage && page < totalPages;
  const goPrev = () => {
    if (prevItem) { guardedNavigate(prevItem.ocr_prob_id); return; }
    if (canPrevPage && confirmDiscard()) onStepPage(-1);
  };
  const goNext = () => {
    if (nextItem) { guardedNavigate(nextItem.ocr_prob_id); return; }
    if (canNextPage && confirmDiscard()) onStepPage(1);
  };

  // 번호칸에서 Enter — 저장하고 바로 다음 문제로. 번호만 훑어 고칠 때 저장 버튼과 다음 버튼을
  // 번갈아 누르지 않게 한다. 저장에 실패하면(입력 오류·409) 넘어가지 않고 그 자리에서 사유를 본다.
  const saveAndNext = async () => {
    const ok = await handleSave({ quiet: true });
    if (!ok) return;
    // 저장이 성공한 것만 '확인함'으로 센다. ←→ 로 그냥 지나친 문제는 여기 들어오지 않는다.
    onChecked && onChecked(problemId);
    if (nextItem) onNavigate && onNavigate(nextItem.ocr_prob_id); // 방금 저장했으므로 버리기 확인이 필요 없다
    else if (canNextPage) onStepPage(1);
    // 마지막 문제까지 확인했다 — 목록을 펼쳐 전체 승인으로 이어지게 한다.
    // 여기서 아무 일도 안 하면 "끝났는데 다음이 없다"는 상태로 멈춰 무엇을 할지 알 수 없다.
    // 한 문제만 볼 때는 펼칠 목록이 없다 — 그 자리에 머문다.
    else if (!singleProblem) setLeftTab('list');
  };

  // 번호칸에서 Enter — 승인하고 바로 다음 문제로.
  // 검수에서 가장 흔한 한 동작은 '보고 승인'이므로 가장 가까운 키를 거기에 준다.
  // →는 승인 없이 지나가는 길이라 둘은 같은 일이 아니다(한때 Enter가 '저장하고 다음'이라
  // →와 구별되지 않았다 — 저장은 승인에 이미 포함돼 있다: handleReview가 먼저 저장한다).
  // 승인이 실패하면 넘어가지 않는다 — 사유는 handleReview·handleSave가 말한다.
  const approveAndNext = async () => {
    // 이미 승인된 문제는 같은 상태로 API를 다시 부르지 않고 그냥 넘어간다.
    if (!isApproved) {
      const ok = await handleReview('approve', { quiet: true });
      if (!ok) return;
    } else if (hasUnsavedChanges()) {
      // 승인한 뒤 돌아와 고친 것 — 종전엔 저장도 확인도 없이 다음 문제로 넘어가 조용히 사라졌다
      // (2026-09-25 분석: 화살표·목록 이동은 묻는데 이 길만 onNavigate 로 곧장 갔다). 승인은 이미 됐으니 저장만 한다.
      const saved = await handleSave({ quiet: true });
      if (!saved) return;
    }
    onChecked && onChecked(problemId);
    if (nextItem) onNavigate && onNavigate(nextItem.ocr_prob_id); // 방금 저장·승인했으므로 버리기 확인이 필요 없다
    else if (canNextPage) onStepPage(1);
    // 마지막 문제까지 왔다 — 목록을 펼쳐 전체 승인으로 이어지게 한다(saveAndNext와 같은 이유).
    else if (!singleProblem) setLeftTab('list');
  };

  // ← → 로 이전/다음. goPrev/goNext는 매 렌더 새로 만들어지므로 리스너에 직접 넣으면
  // 떼었다 붙이기를 반복한다 → 리스너는 한 번만 걸고 최신 함수만 ref로 갈아 끼운다.
  const navRef = useRef({ goPrev, goNext });
  useEffect(() => { navRef.current = { goPrev, goNext }; });
  useEffect(() => {
    if (!isOpen || singleWorkflow) return undefined;
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (conceptDetailOpenRef.current) return; // 개념 상세가 위에 떠 있다
      if (figureOpenRef.current) return; // 그림 편집 창이 위에 떠 있다
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = document.activeElement;
      // 글을 고치는 중이면 커서 이동이 우선이다. 번호칸만 예외 — 값이 한두 자리라 커서를 옮길 일이
      // 거의 없고, 훑는 동안 손이 계속 그 칸에 있기 때문이다.
      if (el?.isContentEditable || el?.tagName === 'TEXTAREA') return;
      if (el?.tagName === 'INPUT' && el !== numberInputRef.current) return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') navRef.current.goPrev();
      else navRef.current.goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, singleWorkflow]);

  // 페이지 경계 이동 중 목록 조회가 실패하면 모달 안에서도 알린다 — 버튼이 조용히 아무 일도
  // 하지 않는 것처럼 보이지 않도록. 성공하면 부모가 loadError를 비워 다시 뜨지 않는다.
  useEffect(() => {
    if (listError) setToast({ type: 'error', text: listError });
  }, [listError, setToast]);

  const flags = Array.isArray(detail?.flags) ? detail.flags : [];
  const aiGen = detail?.ai_generated || null;
  // ── 이 문제에 쓰인 AI 지시 (BE-20260829-reply-fe-0828.md §0 · §4-1) ──
  // reocr_prompt = 마지막 [이 문제 다시 OCR]에 실린 지시 · analysis_prompt = 마지막 분석에 실린 지시.
  // 여태 저장소 어디도 이 두 키를 읽지 않아, 「무슨 지시로 이렇게 읽혔는지」를 사람이 알 길이 없었다.
  //
  // 말하는 것은 **값이 있다는 사실**뿐이다. 개별 지시 없이 나간 요청이면 서버가 프로젝트 기본 지시를
  // 대신 쓰는데(§4-2 5-4), 그때 이 칸에 그 문장이 적히는지 비는지는 회신에 없다. 그래서 「이 문장으로
  // 읽혔다」로 단정하지 않고 비면 줄 자체를 그리지 않는다 — 빈 칸이 「지시 없이 읽혔다」는 거짓을
  // 말하지 않게(→ 백엔드 질의 대상).
  const usedPrompts = useMemo(() => ([
    { key: 'reocr', label: '다시 읽기', text: String(detail?.reocr_prompt ?? '').trim() },
    { key: 'analysis', label: '분석', text: String(detail?.analysis_prompt ?? '').trim() },
  ].filter((p) => p.text !== '')), [detail]);
  // 검수 상태 — 액션 바가 이 값에 따라 무엇이 '이미 한 일'인지 보여 준다.
  // 100 검수대기 / 200 검수완료 / 400 반려. 승인·반려는 되돌릴 수 있으므로(백엔드가 200→400을 허용한다)
  // 이미 한 쪽만 잠그고 반대쪽은 살려 둔다 — 잠그는 것은 되돌릴 수 없는 승격뿐이다.
  const reviewStatusValue = detail?.review_status ?? listRow?.review_status ?? null;
  const isApproved = reviewStatusValue === 200;
  const isRejected = reviewStatusValue === 400;
  // 분석 상태는 상세에 없을 수 있어 목록 행으로 보완한다 — '다시 분석' 노출 판정에만 쓴다.
  // curIdx가 아니라 id로 찾는다: 승인 직후 그 문제가 '검수대기' 필터에서 빠지면 curIdx는 -1이 되는데,
  // 그때 보완이 끊겨 버튼이 사라지는 것은 값이 바뀌어서가 아니라 화면 상태 때문이다.
  const analysisStatusValue = detail?.analysis_status ?? listRow?.analysis_status ?? null;
  const analysisInfo = detail && detail.analysis_status !== undefined
    ? getAnalysisStatusDisplay(detail.analysis_status) : null;
  // 객관식/서답형 — OCR이 body_payload.answerType에 채워 준다. 인덱스를 순서대로 매기고 나면
  // 지면의 「서답형 1」 같은 표기와 인덱스가 달라지므로, 유형만은 눈에 보이게 둔다.
  const answerTypeInfo = getAnswerTypeDisplay(detail?.body_payload?.answerType);

  const zoomMeta = ZOOMS.find((z) => z.key === zoom) || ZOOMS[0];
  // 원본 한 장이 통째로 화면에 들어오는 모드. 이때만 좌측 아래 칸을 세로 flex로 세워
  // 이미지가 남은 공간 안에 원본 비율로 들어가게 한다(목록 탭·확대 배율에서는 예전대로 스크롤).
  const fitMode = leftTab === 'source' && zoom === 'fit';
  const kindLabelOf = (k) => KIND_TABS.find((t) => t.kind === k)?.label || `종류 ${k}`;
  // 승격된 문제 — 편집·저장·승인/반려 전면 차단(백엔드도 423, UI 선제 차단 — FE-5 §2-2)
  const promoted = !!detail?.promoted_problem_uuid || detail?.promoted === true;
  // ④ 문제 검수 · ⑤ AI 분석 두 단계가 이 모달 하나를 쓴다. 2026-08-30 에는 단계마다 보이는 블록을 갈랐다
  // (④ 라벨만 · ⑤ 개념·난이도만). 2026-09-13 오너 결정 D1 로 **두 단계 모두 세 항목(개념·라벨·난이도)을 고친다.**
  // 그래서 mode 는 이제 단계 전용 조작 둘([이 문제 다시 OCR] · [다시 분석])만 가른다.
  // ⚠ ④에서 넣은 개념·난이도는 AI 분석이 조건 없이 덮는다(kyhub services/bulkocr_analysis.py:52-53) — 개념 칸에 적어 둔다.
  const modeAnalysis = mode === 'analysis';
  // 저장/검수/재OCR 요청 진행 중 편집 잠금(R3) + 승격·프로젝트 종료(closed)는 읽기 전용(FE-5 수정요청 #1-1).
  // reocring 포함 — 재OCR은 동기 LLM 호출로 수 초 이상 걸리고, 완료 응답이 seedEdits로 화면을
  // 갈아끼우므로 그 사이 입력·저장·승인이 살아 있으면 대기 중 편집이 무통보 폐기된다.
  // 그림 생성도 서버 쓰기라 함께 잠근다 — 그 사이 [저장]이 나가면 낙관락이 서로를 밀어낸다
  // (문제 편집이 같은 이유로 같은 잠금을 쓴다 · ProblemViewTab 의 busy).
  const busy = saving || !!reviewing || reocring || closed || figGenBusy
    || (singleWorkflow && (externalBusy || singleActionBusy || reanalyzing || deletingSolution));
  singleBusyRef.current = singleWorkflow && (busy || deleting);
  useEffect(() => { onDirtyChange?.(hasUnsavedChangesRef.current?.() || (singleWorkflow && busy)); });

  /**
   * 보기 상자를 **두 번 누르면 [편집기] 탭으로 들어간다** — 문제 편집(ProblemViewTab)의
   * 같은 동작을 여기에도 둔다(오너 지시 2026-09-05). 본문·풀이 둘 다.
   *
   * 손이 짧아진다 — 지금은 고칠 곳을 화면에서 찾은 뒤 눈과 손이 위의 [편집기] 칩까지 갔다가
   * 그 자리로 되돌아와야 한다. 두 번 누르기는 그 왕복을 없앤다(문서 편집기의 오랜 관례다).
   *
   * **마우스 편의일 뿐이다.** 키보드·보조기기 경로는 위의 [보기|편집기|JSON] 칩이 그대로
   * 갖는다. 그래서 onDoubleClick 하나만 얹고 role·tabIndex 는 만들지 않는다: 없는 버튼을
   * 보조기기에 알리면 그쪽에서는 **누를 수 없는 것**이 하나 생긴다.
   *
   * 전환은 칩과 **같은 절차**를 밟는다(flush 뒤 setView). 편집기가 살아 있는 지금만 그 상태를
   * 믿을 수 있어서다 — 여기서 건너뛰면 칩으로 들어갈 때와 결과가 갈린다.
   *
   * 치르는 값 하나 — 보기 상자에서 **낱말을 두 번 눌러 고르던 동작**이 편집기 진입으로 바뀐다.
   * 편집기 안에서도 글자는 그대로 고를 수 있으니 잃는 것은 그 한 번의 선택뿐이다.
   *
   * 문제 편집에 있는 **스크롤 붙들기는 옮기지 않았다.** 그쪽이 푸는 문제는 「모드가 본문·정답·
   * 풀이 세 칸을 함께 바꿔서 아래 것이 밀린다」인데, 여기서는 본문과 풀이가 각각 제 탭을 쥐고
   * 있어 한 칸만 바뀐다. 붙들 칸의 윗변이 애초에 움직이지 않아 잴 차이가 없다.
   *
   * title 을 함께 다는 이유: 두 번 누르기는 화면에 흔적이 없다. 적어 두지 않으면 아무도 모른다.
   *
   * 「보기일 때만」이라는 가드는 두지 않는다 — 이 묶음을 받는 상자 자체가 보기(render) 가지에서만
   * 그려진다. 항상 참인 조건을 적어 두면 읽는 사람이 없는 갈림길을 찾게 된다.
   */
  const enterEditorOnDoubleClick = (setView, label, editorRef, pendingRef, prefix, setDirty) => ({
    /**
     * 두 번째 눌림의 **기본동작(낱말 고르기)을 막는다.** 안 막으면 편집기로 들어가는 순간
     * 글자가 파랗게 잡힌 채로 시작하는데, 그 선택은 복사하려던 것이 아니라 두 번 누른
     * 부산물이다. `detail` 은 연속 눌림 횟수라 **첫 눌림(1)은 건드리지 않는다** —
     * 끌어서 글자를 고르는 길은 그대로 남는다.
     */
    onMouseDown: (e) => { if (e.detail >= 2) e.preventDefault(); },
    onDoubleClick: () => {
      // 칩과 같은 자로 잰다 — lexKey 는 **누르는 시점**에 부른다(그리는 시점 값을 가둬 두지 않는다)
      flushLexBeforeSwitch(label, editorRef, pendingRef, lexKey(prefix), setDirty);
      setView('editor');
    },
    title: '두 번 누르면 편집기로 바뀝니다',
  });

  // ── 그림 편집 (BE-20260827) ──
  // [편집기] 탭 안의 그림을 선택하면 버튼이 뜨고, 그 누름이 여기로 올라온다.
  // 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md (§8 이 2026-08-29 갱신분)
  // 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §2 — Q1·Q2·Q3·Q5·Q6 확정
  //
  // 버튼 세트는 그림 종류로 갈린다(오너 결정 2026-08-28) —
  //   SVG  → [편집]                  : 명세를 고친다
  //   픽셀 → [재생성] [교체] [삭제]   : reconvert · 이력 restore · DELETE API
  // (옛 이름이다 — 2026-08-31 부터 넷 다 뜨고 이름은 [편집][AI 요청][이미지 교체][지우기].)
  //
  // 서버가 그림을 가리키는 것은 **순번**(계약 §1-1: stem→box→choices)이고, 화면이 그리는 것은
  // body_lexical 문서다. 둘이 어긋난 채 보내면 서버는 **다른 그림에** 성공적으로 PATCH 하고
  // 200 을 준다 — 화면은 「저장됐습니다」라 말하고 사람은 모른다. 조용히 망가지는 종류라 관문이 있다.
  //
  // 관문 셋을 차례로 지난다:
  //   ① 미저장 변경이 있으면 막는다. 순번은 저장본 기준인데 화면은 고치던 것을 그리고 있다.
  //   ② 창이 열리며 서버 목록을 받아 **줄 전체를 나란히 맞춘다**(matchFigureSequence).
  //      개수가 다르거나 자리마다 종류(도판/원본)가 다르면 쓰기를 막고 무엇이 어긋났는지 말한다.
  //   ③ 그 순번의 그림 하나를 단건 대조한다(matchFigure).
  // 종전에 남겨 두었던 구멍 — 「같은 종류의 그림이 자리만 바뀐 경우」 — 은 2026-08-29 회신으로
  // 닫혔다. OCR 항목에 `src` 가 붙었고 그 값이 검수 상세의 image 노드 src 와 **바이트 동일**이라
  // (회신 Q5), 이제 ②③ 이 `node.src === item.src` 까지 본다.
  // ④ 편집본(lexical_edited)이 있는 field 에서 대조가 어긋나면 원인이 편집본이다 — 회신 Q3 이
  //    「편집 불가로 막고 편집본 폐기를 안내」하라고 지정했다. 그 판단에 쓰라고 값을 함께 싣는다.
  //
  // **저장본에 있는 그림의 삭제도 이 관문을 전부 지난다**(2026-08-29 회신 §2 Q8). 종전에는
  // 예외였다 — 편집기 안에서 노드를 빼고 기존 저장 경로(PATCH body_lexical)로 내보냈고, 그래서
  // 「순번과 무관하다」고 적어 두었다. 회신이 그것을 정면으로 금지했다: 서버가 노드가 사라진 것을
  // 몰라 지운 그림의 이력이 남고 뒤 그림들의 index 가 밀린다. 이제 삭제는
  // `DELETE …/figures/{field}/{index}` 로 나가고, **순번으로 지정되며 되돌릴 수 없다**.
  //
  // 편집기 키(Delete·Backspace·Cmd+X)만 잡아서는 **범위 선택**이 남는다 — 그림을 문단째 긁어
  // 지우거나 그 위에 타이핑하면 RangeSelection 이라 그림 노드의 명령에 걸리지 않는다. 그 그물은
  // 저장 직전에 있다(handleSave 의 「그림이 줄어든 저장」 검사).
  //
  // 반환값은 **「내가 맡았는가」**다(FigureEditContext 의 계약). `false` 를 돌려주면 편집기가
  // 종전대로 노드를 지운다.
  //
  // ── 2026-09-04: 지우기는 편집기의 일, 서버 삭제는 [저장]의 일 ──
  // 종전에는 서버가 아는 그림의 [지우기]·Backspace 가 삭제 창을 열어 **즉시** DELETE 를 보냈고,
  // 미저장 편집이 있으면 「먼저 저장하라」로 막았다. 이제 어느 그림이든 편집기가 노드를 빼고,
  // [저장]이 사라진 그림을 삭제 API 로 돌린다(purgeRemovedFigures). 저장 전에는 실행 취소로
  // 되돌릴 수 있고, 글을 지우는 것과 같은 결로 움직인다(오너 지시 「백스페이스로 자유롭게 제거」).
  //
  // ── 나머지 셋(편집·AI 요청·이미지 교체)은 **먼저 저장하고** 연다 ──
  // 그림 창은 서버에 바로 쓰고 닫힐 때 상세를 다시 읽는다 — 저장하지 않은 편집이 그때 덮인다.
  // 종전에는 여기서 막았는데(「먼저 [저장]한 뒤에 열 수 있습니다」), 그 왕복이 곧 「안 된다」로
  // 읽혔다. 저장이 곧 붙여넣은 이미지의 등록이기도 하므로, 창이 열릴 때 문서와 서버 목록은
  // 같은 그림을 같은 순서로 센다 — 창의 순번 대조가 어긋날 이유가 사라진다.
  const handleFigureAction = useCallback(({ action, field, index, src }) => {
    if (action === 'delete') return false;
    if (index < 0) {
      setToast({ type: 'error', text: '그 그림을 본문에서 찾지 못했습니다 — 문제를 다시 열어 주세요.' });
      return true;
    }
    const pid = detailRef.current?.ocr_prob_id;
    (async () => {
      const ok = await ensureSavedRef.current?.();
      if (!ok) return; // 사유는 handleSave 가 말했다
      const d = detailRef.current;
      if (pid == null || String(d?.ocr_prob_id) !== String(pid)) return; // 저장하는 사이 문제를 옮겼다
      // 대조에 쓸 화면 쪽 그림 줄 — **상세 응답의 lexical**(방금 저장한 것)을 쓴다.
      const doc = field === 'solution' ? d?.solution_lexical : d?.body_lexical;
      const nodes = collectFigureNodes(doc);
      // 그 대조 문서 자체가 편집본일 수 있다 — 규약 회신(ocr-editor-lexical-contract-reply.md §2-1)이
      // 「body_lexical 은 편집본이 저장돼 있으면 그것」이라고 못박았다. 그 field 의 편집본 여부를
      // 창까지 나른다(회신 Q3).
      const edited = field === 'solution' ? !!d?.lexical_edited?.solution : !!d?.lexical_edited?.body;
      // 문제 id 를 창에 **박아 둔다** — 검수 모달이 닫히며 problemId prop 이 먼저 null 이 되는 렌더에서
      // 창이 `…/problems/null/figures` 를 부르던 것(2026-09-04 실측)을 막는다.
      setFigureTarget({ action, field, index, src, nodes, edited, problemId: pid });
    })();
    return true;
  }, [setToast]);

  // 창을 닫으면 **무조건** 상세를 다시 읽는다.
  // 계약 §6 이 「그림 목록·이력 첫 조회는 쓰기(seed)를 동반한다」고 했다. 그 seed 가 낙관락
  // 토큰(updated_at)을 전진시키지는 **않는다** — 회신 §2 Q4 가 실측으로 확인했다(2026-08-29).
  // 그래도 조건 없이 읽는 이유는 남는다: 이 창은 그림을 실제로 고쳤을 수 있고(PATCH·재요청·되돌리기가
  // 본문과 updated_at 을 함께 움직인다), 창이 무엇을 했는지 바깥이 신뢰해 판단하는 것보다
  // 한 번 더 읽는 값이 싸다(ProblemViewTab.closeFigure 와 같은 판단).
  // 삭제로 닫힌 경우에만 result 가 실려 온다 — 창이 사라진 뒤라 그 사실을 말할 자리가 여기뿐이다.
  // `remaining` 은 쓰지 않는다: 그 field 의 남은 수인지 문제 전체인지 계약에 없다(→ 백엔드 질의).
  /**
   * 그림 창이 「서버가 모르는 그림」이라 판정한 이미지를 본문에서 뺀다(계약 §10 · 회신 §5).
   * **판정은 창이 한다** — 서버 그림 목록을 쥔 쪽이 거기다. 여기서는 시킨 노드를 빼기만 한다
   * (저장은 서버 목록과 새 문서를 견주므로, 서버가 모르는 그림을 뺀 것은 삭제로 읽히지 않는다).
   *
   * 라이브 편집기가 이 문제의 것일 때만 된다(readLexEdit 와 같은 key 대조) — [보기]·[JSON] 탭에
   * 가 있으면 편집기가 언마운트돼 있어 뺄 수 없고, 그때는 창이 문구로 [편집기] 탭을 부른다.
   */
  // useCallback 을 쓰지 않는다 — lexKey 가 매 렌더 새 함수라 의존성에 넣으면 memo 가 무의미하고,
  // 빼면 낡은 키를 붙든다. 이 함수를 받는 창은 memo 가 아니라 그대로 두는 편이 정직하다.
  const removeUnknownFigureNode = ({ field, src }) => {
    const ref = field === 'solution' ? solutionEditorRef : bodyEditorRef;
    const key = lexKey(field === 'solution' ? 's' : 'b');
    const live = ref.current;
    if (!live || live.key !== key || typeof live.removeImagesBySrc !== 'function') return { ok: false };
    const removed = live.removeImagesBySrc(src);
    return { ok: removed > 0, removed };
  };

  const closeFigure = useCallback((changed, result) => {
    setFigureTarget(null);
    /**
     * 본문에서 노드만 뺀 경우 — **상세를 다시 읽지 않는다.** 서버는 아무것도 바뀌지 않았고,
     * 다시 읽으면 편집기가 서버 값으로 리마운트되어 방금 뺀 이미지가 그대로 되돌아온다.
     */
    if (result?.removedNode) {
      const where = result.field === 'solution' ? '풀이' : '본문';
      setToast({ type: 'info', text: `${where}에서 그 이미지를 뺐습니다 — [저장]해야 반영됩니다.` });
      return;
    }
    /**
     * 창에서 「서버가 모르는 그림」을 그 자리에서 등록했다(계약 §10 · 회신 §1).
     * 서버가 payload 에 그림을 넣었고 편집본은 그대로다 — 아래 loadDetail 이 새 상세를 받는다.
     */
    if (result?.registered) {
      const where = result.field === 'solution' ? '풀이' : '본문';
      setToast({
        type: 'info',
        text: `${where} ${Number(result.index) + 1}번째 그림으로 등록했습니다 — 편집·AI 요청을 할 수 있습니다.`,
      });
    }
    if (result?.deleted) {
      const where = result.field === 'solution' ? '풀이' : '본문';
      /**
       * 편집본이 버려졌으면 **그것이 본론이다**(계약 §10 · 회신 §2). 그림을 지운 것은 시킨 일이고,
       * 고쳐 둔 문장이 사라진 것은 시키지 않은 손실이라 뒤에 붙이지 않고 한 줄로 함께 낸다.
       * 다른 그림 쓰기는 창이 남아 띠로 말하지만(FigureModal.editedDropped) 삭제만은 창이 닫힌다.
       */
      setToast(result.editedCopyDropped
        ? {
          type: 'error',
          text: `${where}에서 그림을 지웠습니다. 다만 ${where}의 편집본이 서버가 세는 그림과 맞지 않아 함께 버려졌습니다`
            + ' — 편집기에서 고쳤던 문장이 OCR 원문으로 되돌아갑니다.',
        }
        : { type: 'info', text: `${where}에서 그림을 지웠습니다.` });
    }
    // keepView — 그림은 [편집기] 탭에서 골라 고친다. 상세를 다시 읽으며 탭까지 [보기]로 돌리면
    // 방금 고친 그림을 그 자리에서 확인할 수 없다([편집 취소]가 같은 이유로 쓰는 옵션이다).
    loadDetail({ keepView: true });
  }, [loadDetail, setToast]);

  // Provider 로 내려갈 값. 매 렌더마다 새 객체를 만들면 편집기 안 그림이 전부 다시 그려진다.
  // busy(저장·검수·재OCR 진행 · 승격 · 프로젝트 종료)면 아예 주지 않는다 — 백엔드의 423 게이트와 짝이다.
  /**
   * 이미지가 편집기에 들어오려는 순간을 받는다(PasteFigurePlugin → 이 콜백 → 붙여넣기 창).
   *
   * 여기서 **판정하지 않는다** — 서버 그림 목록을 받아 무엇을 할 수 있는지 정하는 일은 창이 한다.
   * 이 함수가 하는 일은 재료를 모아 창을 여는 것뿐이다. 미저장 여부만 미리 읽어 싣는다:
   * 창이 뜬 뒤에는 편집기 상태가 바뀔 수 있고, 그때 읽으면 붙여넣기 자체가 만든 dirty 를 셀 수 있다.
   */
  /**
   * ── AI 생성 (계약 §9-2) ──
   *
   * 본문·풀이의 글을 AI 가 읽고 그림 하나를 지어 **그 field 맨 끝에 붙인다.** 있던 그림을 바꾸는
   * 것이 아니라 더하는 것이라, 대체하려면 있던 그림을 지우고 부른다.
   *
   * **미저장 편집분이 있으면 먼저 저장한다**(ensureSaved) — 이 호출은 서버 본문을 바로 바꾸고
   * `updated_at` 을 전진시켜, 성공한 뒤 화면이 최신을 다시 읽는다. 그때 고치던 것이 덮이지 않게
   * 저장을 앞세운다. 그림 편집 창(handleFigureAction)과 같은 규칙이다.
   */
  const openFigGen = async (field) => {
    const ok = await ensureSaved();
    if (!ok) return; // 사유는 handleSave 가 말했다
    setToast(null);
    setFigGenField(field);
    /**
     * 이 자리 그림들의 원본 카드(2026-09-06 A 안) — 서버 목록을 읽어 짓는다. 창은 먼저 열고 카드는 도착하면
     * 늘어난다(창 안의 「읽는 중」 한 줄). 읽기가 실패해도 창은 쓸 수 있다 — 본문 글·올린 이미지 갈래는 목록과 무관하다.
     */
    const seq = (figGenSeqRef.current += 1);
    const pid = detailRef.current?.ocr_prob_id;
    setFigGenOrigins({ loading: pid != null, items: [], token: null });
    if (pid == null) return;
    try {
      const d = await fetchFigures('ocr', pid, getAccessToken);
      if (d === AUTH_REDIRECT || seq !== figGenSeqRef.current) return;
      setFigGenOrigins({ loading: false, items: listFigureOrigins(d?.items, 'ocr', field), token: d?.updated_at || null });
    } catch {
      if (seq === figGenSeqRef.current) setFigGenOrigins({ loading: false, items: [], token: null });
    }
  };

  /**
   * 생성 창(FigureReconvertModal kind='generate')이 고른 것으로 만든다.
   *  · source 'text'   본문 글로 짓는다 — `POST …/figures/generate` (계약 §9-2, mode 는 2026-09-05 확인)
   *  · source 'upload' **올린 이미지로 새 그림을 만든다.** 생성 API 는 이미지를 받지 않으므로 두 번 간다:
   *      ① 등록(`POST …/figures`, 맨 끝) → 서버가 세는 픽셀 그림이 된다(회신 §1)
   *      ② 그 자리에 재요청 → 서버가 자체 이미지를 재료로 옮긴다(회신 §4 우선순위 — image 를 다시 싣지 않는다)
   *    ②가 거절되거나(converted:false) 실패해도 ①은 남는다 — 손실은 없고, 그 사실을 말한다.
   *    이것이 「캡처해서 AI 생성」의 한 번에 가는 길이다(오너 요구 2026-09-04 ③).
   *  · source 'figure' **이 자리 그림의 원본으로 그 그림을 그 자리에서 다시 그린다**(2026-09-06 오너 결정 A 안).
   *      OCR 원본은 그림 단위(페이지 크롭)라 새 그림을 더하지 않고 그 그림을 재요청한다 — [AI 요청]과 같은 호출.
   *      아무것도 싣지 않는다(회신 §4 — 비우면 곧 서버 보관 원본). 이전 판은 이력에 남는다.
   */
  const runFigGen = async ({ source, image, mode, userPrompt, figure } = {}) => {
    const field = figGenField;
    const pid = detailRef.current?.ocr_prob_id;
    if (!field || pid == null) return;
    setFigGenBusy(true);
    setToast(null);
    try {
      if (source === 'figure' && figure) {
        const where = figure.field === 'solution' ? '풀이' : '본문';
        const r = await reconvertFigure(
          'ocr', pid, figure.field, Number(figure.index),
          {
            // 방금 읽은 목록의 토큰이 가장 새 값이다 — 없으면 상세의 것.
            expectedUpdatedAt: figGenOrigins.token || detailRef.current?.updated_at,
            userPrompt, mode, ...(figure.request || {}),
          },
          getAccessToken,
        );
        if (r === AUTH_REDIRECT) return;
        if (String(problemIdRef.current) !== String(pid)) return;
        if (r?.converted === false) {
          // 실패가 아니다 — 원본은 그대로다. 서버가 남은 길(suggest:"trace")을 주면 이름으로 부른다.
          setToast({
            type: 'info',
            text: figureRedrawRejectedText(where, figure.index, r?.suggest === 'trace'),
            issues: Array.isArray(r.issues) ? r.issues : [],
          });
          return;
        }
        const done = figureRedrawnText(where, figure.index, mode);
        setToast(readEditedCopyDropped(r)
          ? { type: 'error', text: `${done} 다만 ${where}의 편집본이 서버가 세는 그림과 맞지 않아 함께 버려졌습니다.` }
          : { type: 'info', text: done });
        loadDetail({ keepView: true });
        return;
      }
      if (source === 'upload' && image) {
        const where = field === 'solution' ? '풀이' : '본문';
        const reg = await registerFigure(
          'ocr', pid, { field, image, expectedUpdatedAt: detailRef.current?.updated_at }, getAccessToken,
        );
        if (reg === AUTH_REDIRECT) return;
        try {
          const r = await reconvertFigure(
            'ocr', pid, field, Number(reg?.index),
            { expectedUpdatedAt: reg?.updated_at, userPrompt, mode },
            getAccessToken,
          );
          if (r === AUTH_REDIRECT) return;
          if (String(problemIdRef.current) !== String(pid)) return;
          if (r?.converted === false) {
            setToast({
              type: 'info',
              text: `${where} 끝에 올린 이미지를 그림으로 넣었습니다. AI 가 좌표·식으로 옮기지는 못해 이미지 그대로 둡니다(손실 없음).`,
              issues: Array.isArray(r.issues) ? r.issues : [],
            });
          } else {
            setToast(readEditedCopyDropped(r)
              ? { type: 'error', text: `${where} 끝에 올린 이미지로 그림을 만들었습니다. 다만 ${where}의 편집본이 서버가 세는 그림과 맞지 않아 함께 버려졌습니다.` }
              : { type: 'info', text: `${where} 끝에 올린 이미지로 그림을 만들었습니다 — 그림을 눌러 고칠 수 있습니다.` });
          }
        } catch (e) {
          if (String(problemIdRef.current) !== String(pid)) return;
          setToast({ type: 'error', text: `${where} 끝에 올린 이미지는 그림으로 넣었지만 AI 요청은 실패했습니다 — ${describeFigureError(e).text}` });
        }
        loadDetail({ keepView: true });
        return;
      }
      const d = await generateFigure(
        'ocr', pid,
        { field, userPrompt, mode, expectedUpdatedAt: detailRef.current?.updated_at },
        getAccessToken,
      );
      if (d === AUTH_REDIRECT) return;
      if (String(problemIdRef.current) !== String(pid)) return; // 기다리는 동안 문제를 옮겼으면 버린다
      // 계약 §9-2: 모델이 「그림 불필요·확정 불가」로 본 경우 200 + generated:false 다.
      // **실패가 아니다** — 본문은 그대로고 손실이 없다. 오류로 말하면 사용자가 망가졌다고 읽는다.
      if (d?.generated === false) {
        setToast({ type: 'info', text: FIGURE_NOT_GENERATED_TEXT, issues: d.issues });
        return;
      }
      const where = field === 'solution' ? '풀이' : '본문';
      setToast(readEditedCopyDropped(d)
        ? { type: 'error', text: `${where} 끝에 그림을 만들어 붙였습니다. 다만 ${where}의 편집본이 서버가 세는 그림과 맞지 않아 함께 버려졌습니다.` }
        : { type: 'info', text: `${where} 끝에 그림을 만들어 붙였습니다 — 그림을 눌러 고칠 수 있습니다.` });
      // 본문이 서버에서 바뀌었다. 다시 읽지 않으면 편집기가 새 그림을 모르고, 다음 저장이
      // 옛 updated_at 으로 나가 409 가 된다.
      loadDetail({ keepView: true });
    } catch (e) {
      setToast({ type: 'error', text: describeFigureError(e).text });
    } finally {
      setFigGenBusy(false);
      setFigGenField(null);
    }
  };

  /**
   * [이미지 추가] — 파일을 골라 **붙여넣기와 같은 창**으로 보낸다(교체 / 새 그림으로 등록 / 그냥 넣기).
   *
   * 왜 자체 버튼인가: 공용 편집기 툴바에도 이미지 삽입이 있지만(InsertDropdown 의 영어 [Image]),
   * 그 길은 공용 다이얼로그(URL/파일)를 한 번 거친 뒤 삽입 명령을 쏘아 **창이 두 번** 뜬다.
   * 여기서는 파일 고르기 → 창 하나로 끝난다.
   *
   * `precedingSrcs: null` 을 싣는다 — 커서 자리를 알 수 없으므로 등록하면 서버가 맨 끝에 넣는다.
   * 「모른다」와 「맨 앞」을 가르는 값이다(countKnownBefore 주석).
   */
  const openImagePicker = (field) => {
    imageInputFieldRef.current = field;
    if (imageInputRef.current) imageInputRef.current.value = '';
    imageInputRef.current?.click();
  };

  const takeImageFile = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setToast({ type: 'error', text: '이미지 파일만 넣을 수 있습니다.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => takeIncomingImage({
      field: imageInputFieldRef.current, src: String(reader.result || ''), altText: '', precedingSrcs: null,
    });
    reader.onerror = () => setToast({ type: 'error', text: '이미지를 읽지 못했습니다.' });
    reader.readAsDataURL(file);
  };

  /** 편집기에 이미지를 그대로 넣는다 — 서버 등록은 [저장]이 한다(registerNewFigures). */
  const insertImageNow = (field, src, altText) => {
    const ref = field === 'solution' ? solutionEditorRef : bodyEditorRef;
    const key = lexKey(field === 'solution' ? 's' : 'b');
    const live = ref.current;
    const w = field === 'solution' ? '풀이' : '본문';
    const ok = live && live.key === key && typeof live.insertImage === 'function'
      && live.insertImage({ src, altText: altText || '' });
    setToast(ok
      ? { type: 'info', text: `${w}에 이미지를 넣었습니다 — [저장]하면 그림으로 등록됩니다.` }
      : { type: 'error', text: `${w} 편집기에 넣지 못했습니다 — [편집기] 탭에서 다시 시도해 주세요.` });
    return !!ok;
  };

  /**
   * **들어오는 이미지의 한 입구** — 붙여넣기·드롭·툴바 삽입(PasteFigurePlugin)과 [이미지 파일 추가]가
   * 전부 여기로 온다.
   *  ① 서버가 받지 않는 이미지(형식·2MB)는 여기서 막는다. 넣고 나서 저장이 막히면 사람이 어느
   *     이미지가 원인인지 찾아야 한다 — 고르는 순간 말하는 편이 짧다.
   *  ② 그 자리에 **바꿀 그림이 없으면 묻지 않고 넣는다.** 물을 것이 「어느 그림을 바꿀까」뿐이라,
   *     바꿀 것이 없는 창은 사람에게 아무 선택도 주지 못하는 한 단계다.
   *  ③ 바꿀 그림이 있을 때만 창을 연다(OcrPasteImageModal) — 교체 / 새 그림으로 넣기.
   * 어느 길이든 서버 등록은 [저장]이 한다 — 종전의 「새 그림으로 등록」과 「그냥 넣기」는 저장 뒤
   * 같은 상태가 되므로 하나로 합쳤다.
   */
  const takeIncomingImage = ({ field: f, src, altText = '', precedingSrcs = null }) => {
    const field = f === 'solution' ? 'solution' : 'body';
    const check = parseImageDataUri(src);
    if (!check.ok) { setToast({ type: 'error', text: describeImageReject(check) }); return; }
    const d = detailRef.current;
    const saved = collectFigureNodes(field === 'solution' ? d?.solution_lexical : d?.body_lexical);
    if (!saved || saved.length === 0) { insertImageNow(field, src, altText); return; }
    setPasteTarget({ field, src, altText, precedingSrcs });
  };
  takeIncomingRef.current = takeIncomingImage;

  /**
   * 본문·풀이 머리줄에 나란히 서는 그림 추가 버튼 둘. **한 함수에서 그린다** — 두 자리에 같은
   * 마크업을 두 벌 두면 한쪽만 고쳐지고, 이 저장소가 그 사고를 이미 여러 번 겪었다.
   *
   * 얼굴은 같은 줄의 [이 문제 다시 OCR]과 맞춘다(size="xs" · variant="primary-outline") —
   * 값을 베끼지 않고 같은 공용 Button 을 쓴다.
   */
  const renderFigureAddButtons = (field) => {
    const where = field === 'solution' ? '풀이' : '본문';
    // 진행 중(저장·검수·재OCR·승격·종료)에는 내지 않는다 — 서버 쓰기가 서로를 밀어낸다.
    if (busy || transitioning || reocring) return null;
    // 풀이가 없으면 내지 않는다 — 생성은 409(본문 없음)로 거절되고(계약 §9-2), 파일·저장된 그림은 넣을
    // 편집기가 없다. 눌리면 반드시 실패하는 버튼을 내지 않는다(문제 편집의 `solutionNow &&` 와 같은 가드).
    if (field === 'solution' && !detail?.solution_payload && !detail?.solution_lexical) return null;
    return (
      <Button
        size="xs" variant="primary-outline"
        onClick={() => setFigAddField(field)}
        disabled={figGenBusy}
        title={`${where}에 그림을 넣습니다 — AI 로 만들거나 이미지 파일을 고릅니다`}
        icon={<ImagePlus className="size-3.5" />}
      >
        이미지 추가
      </Button>
    );
  };

  // 편집기가 이미지를 받으려는 순간(PasteFigurePlugin) — 판정은 takeIncomingImage 가 한다.
  const handlePasteImage = useCallback((incoming) => { takeIncomingRef.current?.(incoming); }, []);

  const figureEditByField = useMemo(() => (busy ? null : {
    body: { field: 'body', svgEditorEnabled: FIGURE_SVG_EDITOR, onAction: handleFigureAction, onPasteImage: handlePasteImage },
    solution: { field: 'solution', svgEditorEnabled: FIGURE_SVG_EDITOR, onAction: handleFigureAction, onPasteImage: handlePasteImage },
  }), [busy, handleFigureAction, handlePasteImage]);
  const figureEditFor = (field) => figureEditByField?.[field] ?? null;

  // ── 문제 전환의 표현 ──
  // 스켈레톤을 매번 띄우면 응답이 빠를 때 번쩍하고 사라져 되레 플리커로 보인다.
  // 그래서 갈아 끼울 이전 내용이 있으면(=문제를 넘기는 중) 스켈레톤을 쓰지 않고
  // 그 내용을 흐리게 낮춰 두었다가, 새 값이 들어온 뒤 다시 진하게 올린다.
  // 값이 교체되는 순간은 가장 흐린 지점이라 눈에 걸리지 않는다.
  // 스켈레톤은 흐리게 할 것이 아예 없는 첫 진입에만 쓴다.
  const transitioning = loading || navPending;
  const showSkeleton = transitioning && !detail;
  const dimming = transitioning && !!detail;
  // 전환 중에는 마우스만 막는다 — disabled를 걸면 입력 배경색까지 바뀌어 그 자체가 깜빡인다.
  // 잘못된 대상으로 나가면 안 되는 저장·승인·반려만 아래에서 따로 잠근다.
  const dimCls = `transition-opacity duration-200 ${dimming ? 'opacity-40' : 'opacity-100'}${transitioning ? ' pointer-events-none' : ''}`;
  // bbox 하이라이트 — 0~1 정규화 [x0,y0,x1,y1], 좌상단 원점. 같은 페이지의 다른 소스도
  // 흐린 점선으로 함께 표시하고 선택 소스만 진한 강조(R2 — duplicate_body 두 후보 동시 대조).
  // 전환 중에는 감추되 렌더는 유지한다 — 조건부로 빼 버리면 엘리먼트가 사라져
  // 투명도 전환이 걸리지 않고 툭 튄다.
  // 다른 페이지를 넘겨 보는 동안에는 그리지 않는다 — 이 문제의 페이지가 아니다.
  // 이 페이지에 실제로 그려지는 상자 수 — 아래 범례를 낼지 정한다(하나뿐이면 설명할 것이 없다).
  const drawnBoxCount = isOtherPage ? 0 : sources.filter(
    (s) => s.page_id === activeSource?.page_id && Array.isArray(s.bbox) && s.bbox.length === 4,
  ).length;
  // 이 문제에 묶인 「문제」 조각 수 — 여럿이면 나머지는 같은 번호의 후보다(다른 페이지에 있는 것까지 센다).
  const bodySourceCount = sources.filter((s) => Number(s.kind) === 1).length;
  // 서버가 「이 문제가 직접 참조하는 조각」을 알려 줬는가 — 알려 줬으면 빨간 상자를 단언할 수 있다.
  const directKnown = hasDirectSource(sources);
  const bboxOverlay = isOtherPage ? null : sources.map((s, i) => {
    if (s.page_id !== activeSource?.page_id) return null;
    const b = Array.isArray(s.bbox) && s.bbox.length === 4 ? s.bbox : null;
    if (!b) return null;
    const isActive = i === activeSourceIdx;
    return (
      <div
        key={i}
        className={`pointer-events-none absolute rounded transition-opacity duration-200 ${
          transitioning ? 'opacity-0' : 'opacity-100'
        } ${
          isActive
            ? 'border-2 border-red-500/80 bg-red-500/10'
            : 'border border-dashed border-blue-400/70 bg-blue-400/5'
        }`}
        style={{
          left: `${b[0] * 100}%`,
          top: `${b[1] * 100}%`,
          width: `${(b[2] - b[0]) * 100}%`,
          height: `${(b[3] - b[1]) * 100}%`,
        }}
      />
    );
  });
  // (transitioning을 읽으므로 그 정의 뒤에 있어야 한다 — 앞에 두면 렌더 중 TDZ로 터진다)

  // 승인이 거부될 수 있는 플래그 — 버튼을 막지는 않는다(차단 판정은 백엔드 몫).
  const blockingFlags = getBlockingFlags(flags);
  // 막지 않는 경고 플래그의 확인 문구(BE-20260925 §2-6 — 정답 의심·풀이·정답 불일치·그림 없이 분석·OCR 정확도 낮음).
  // 칩 이름만으로는 무엇을 대조해야 하는지 모른다 — 머리 아래 한 줄씩 말한다.
  const flagHints = (Array.isArray(flags) ? flags : [])
    .map((code) => ({ code, ...getFlagDisplay(code) }))
    .filter((f) => f.hint);
  // 액션 바 왼쪽 안내는 한 번에 하나만 — 셋이 겹치면 어느 것도 읽히지 않는다.
  // 읽기 전용 사유 > 방금 받은 거부 사유 > 누르기 전 경고 순.
  // (거부 사유는 모달 맨 위 토스트에도 뜨지만, 승인 버튼은 화면 반대쪽 끝이라 여기서 다시 말한다)
  let actionNote = null;
  // 종료가 먼저다 — 종료 프로젝트는 등록된 문제여도 고칠 수 없는데, 등록 안내가 앞서면 「여기서 고쳐도」가
  // 잠긴 편집을 고칠 수 있는 것처럼 말했다(오너 지적 2026-10-02). 등록 사실은 머리의 「등록됨」 배지가 말한다.
  if (closed) actionNote = { cls: 'text-gray-400', text: '종료된 프로젝트입니다 — 읽기 전용' };
  // 이제 고칠 수 있다. 다만 이 저장소는 **재등록(덮어쓰기)을 하지 않으므로**(오너 결정 2026-08-30)
  // 여기서 고친 것이 문제은행에 가지 않는다 — 조용히 갈라지지 않게 그 사실을 말한다.
  else if (promoted) actionNote = { cls: 'text-amber-600', text: '등록된 문제입니다 — 여기서 고쳐도 문제은행에는 반영되지 않습니다' };
  // 토스트는 상단 배지 줄이 사라지면서 갈 곳이 없어졌다 — 저장 결과든 거부 사유든 여기서 말한다
  else if (singleWorkflow && externalNotice) actionNote = { cls: externalBusy ? 'text-primary' : 'text-red-600', text: externalNotice };
  else if (toast) actionNote = { cls: toast.type === 'error' ? 'text-red-600' : 'text-blue-600', text: toast.text };
  else if (singleWorkflow && detail?.analysis_status !== 200) actionNote = { cls: 'text-gray-500', text: 'AI 분석을 완료하면 문제를 저장할 수 있습니다.' };
  else if (blockingFlags.length > 0) {
    actionNote = {
      cls: 'text-amber-700',
      text: `${blockingFlags.map((c) => getFlagDisplay(c).text).join(' · ')} 플래그가 남아 있습니다 — ${singleWorkflow ? '저장이' : '승인이'} 거부될 수 있습니다`,
    };
  }

  const badge = (info) => info && (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${info.cls}`}>{info.text}</span>
  );
  return (
    <Modal
      isOpen={isOpen}
      onClose={guardedClose}
      // 공용 Modal은 인스턴스마다 document에 ESC를 걸고 '맨 위인가'를 따지지 않는다.
      // 개념 상세가 떠 있을 때 ESC를 그대로 두면 위아래 두 모달이 함께 닫힌다(←→ 가드와 같은 이유).
      // 그림 창·붙여넣기 창·그림 고르기·이미지 추가·AI 생성도 같은 자리에 겹쳐 뜬다(2026-09-04 실측:
      // 그림 창에서 ESC 한 번에 검수 모달까지 닫혔다) — 위에 무엇이 떠 있든 이 창은 ESC 를 받지 않는다.
      closeOnEsc={!conceptDetailOpen && aiRun == null && figureTarget == null && pasteTarget == null
        && figPickField == null && figAddField == null && figGenField == null}
      size="max"
      fixedHeight
      maxHeight="92vh"
      // 제목은 「검수 — 어느 프로젝트 — 몇 번째」 세 토막이다. 읽는 순서가 그 순서다.
      // 세 토막의 글자 크기는 같다(text-base) — 한 줄에 나란히 선 같은 층위라 크기로 갈라 두면
      // 뒤 토막이 각주처럼 읽힌다. h2 기본(text-xl)보다 한 단 낮춘 것은 오너 지시(2026-09-06)다.
      // 크기를 뺀 나머지는 형제에서 가져왔다:
      //   번호 — 프로젝트 목록 표(ProjectListPanel의 「프로젝트」 칸)의 고정폭·primary. 종료
      //          프로젝트에서 primary 를 잃고 회색이 되는 것까지 같다(그 표가 종료를 그렇게 말한다).
      //   이름 — 이 창을 연 프로젝트 상세 패널의 헤더(ProjectDetailPanel)와 같은 굵은 검정.
      // 문제의 인덱스 번호(problem_number)는 제목에서 뺐다(오너 지시 2026-09-06) — 인덱스가
      // 1..N 으로 정리된 프로젝트에서는 목록 위치와 같은 수라 같은 숫자를 두 번 읽게 했다.
      // 둘이 갈리는 자리(필터를 걸었을 때·2페이지 이후)에서는 좌측 목록과 본문이 인덱스를 말한다.
      title={(
        <span className="flex items-baseline gap-2 text-base">
          <span className="shrink-0">{singleWorkflow ? '한 문제 OCR —' : '검수 —'}</span>
          {!singleWorkflow && projectId != null && (
            <span className={`shrink-0 font-mono ${closed ? 'text-gray-900' : 'text-primary'}`}>
              {projectId}
            </span>
          )}
          {/* 책 이름은 길다 — 헤더 오른쪽의 [이전]/[다음]을 밀어내지 않게 여기서만 폭을 묶는다 */}
          <span className="min-w-0 max-w-96 shrink truncate text-gray-900">
            {projectName || '프로젝트'}
          </span>
          {curIdx >= 0 && (
            <span className="shrink-0">{`— ${curIdx + 1}/${list.length}`}</span>
          )}
        </span>
      )}
      // rounded-b-3xl: 모달 겉껍질은 rounded-3xl이지만 잘라내지(overflow-hidden) 않는다.
      // 자르는 것은 이 바디인데 바디에는 둥근 모서리가 없어, 바닥까지 색을 칠하는 자식
      // (좌측 목록·원본의 bg-gray-50)이 왼쪽 아래 모서리를 네모로 덮었다.
      // 겉껍질과 같은 값을 바디 아래쪽에 줘서 잘리는 모양을 맞춘다.
      bodyClassName="flex min-h-0 flex-col overflow-hidden rounded-b-3xl !p-0"
      // 한 문제만 볼 때는 넘길 곳이 없다 — 늘 잠긴 버튼 둘을 세워 두지 않는다
      headerActions={singleProblem ? null : (
        <div className="flex items-center gap-1">
          {/* 저장/검수 진행 중에는 이동 차단 — 늦게 도착한 응답이 다음 문제 화면을 덮는 것 방지.
              navPending(인접 페이지 로딩 중)도 같은 이유로 잠근다. */}
          <button
            onClick={goPrev}
            disabled={(!prevItem && !canPrevPage) || saving || !!reviewing || reocring || transitioning}
            className="flex items-center gap-0.5 rounded-full px-2.5 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
            이전
          </button>
          {/* 이 문제를 처리했으면 다음에 할 일은 다음 문제다 — 그때만 강조한다.
              아직 판단하지 않은 문제에서 강조하면 '넘기라'는 말로 읽힌다. */}
          <button
            onClick={goNext}
            disabled={(!nextItem && !canNextPage) || saving || !!reviewing || reocring || transitioning}
            className={`flex items-center gap-0.5 rounded-full px-2.5 py-1 text-sm transition-colors disabled:opacity-30 ${
              (isApproved || isRejected) && (nextItem || canNextPage)
                ? 'bg-primary/10 font-medium text-primary hover:bg-primary/15'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            다음
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    >
      {/* 문제를 넘길 때 이 골격(좌우 2단·섹션 제목·액션 바)은 언마운트하지 않는다.
          조회 실패만 화면 전체를 대체한다. */}
      {!loading && !detail ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-gray-500">
          {loadError || '문제를 불러올 수 없습니다.'}
          <Button size="sm" variant="secondary" onClick={loadDetail}>다시 시도</Button>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* ── 좌측: 원본 근거 ──
              여기는 흐리게 하지 않는다. 다음 문제도 대개 같은 페이지라 그림이 그대로 유지되는데
              흐렸다 되살리면 그 자체가 깜빡임이 된다. 대신 빨간 bbox만 부드럽게 사라졌다가
              새 위치에 나타난다(전환 중 좌표는 아직 이전 문제 것이라 그대로 두면 엉뚱한 곳을 가리킨다). */}
          <div className={`flex min-h-0 min-w-0 flex-1 flex-col border-b lg:border-b-0 lg:border-r ${
            transitioning ? 'pointer-events-none' : ''
          }`}>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b px-4 py-2">
              {/* 원본 ↔ 목록 — 같은 자리에서 갈아 끼운다. 목록을 보려고 모달을 닫을 일이 없어진다.
                  한 문제만 볼 때는 목록이 없어 원본만 남긴다 */}
              {!singleProblem && (
                <span className="mr-1 flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5">
                  {[{ k: 'source', label: '원본' }, { k: 'list', label: '목록' }].map((t) => (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setLeftTab(t.k)}
                      className={`rounded-full px-2.5 py-0.5 text-xs transition-colors ${
                        leftTab === t.k ? 'bg-white font-bold text-primary shadow-sm' : 'font-medium text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </span>
              )}
              {leftTab === 'list' && (
                <span className="text-xs text-gray-400">문제를 고르면 오른쪽이 바뀝니다 — 목록은 그대로 둡니다</span>
              )}
              {leftTab === 'source' && KIND_TABS.map((t) => {
                const kindSources = sources.map((s, i) => ({ s, i })).filter(({ s }) => s.kind === t.kind);
                if (kindSources.length === 0) return null;
                const active = activeSource?.kind === t.kind;
                return (
                  <button
                    key={t.kind}
                    onClick={() => setActiveSourceIdx(kindSources[0].i)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      active ? 'bg-primary/10 font-bold text-primary' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {t.label} {kindSources.length > 1 ? `(${kindSources.length})` : ''}
                  </button>
                );
              })}
              {singleWorkflow && onSelectRegion && <Button size="xs" variant="secondary" disabled={busy || transitioning} onClick={() => runSingleAction(onSelectRegion)}>영역 다시 선택</Button>}
              {leftTab === 'source' && sources.length === 0 && (showSkeleton
                ? <Skel className="h-6 w-32 rounded-full" />
                : <span className="text-xs text-gray-400">원본 소스 정보가 없습니다.</span>)}
              {/* 같은 kind에 소스 여러 개(풀이 2페이지 등) — 페이지 서브 선택 */}
              {leftTab === 'source' && activeSource && sources.filter((s) => s.kind === activeSource.kind).length > 1 && (
                <span className="ml-1 flex items-center gap-1">
                  {sources.map((s, i) => {
                    if (s.kind !== activeSource.kind) return null;
                    const pageNo = (s.page_index ?? 0) + 1;
                    // 같은 페이지 라벨이 중복되면 kind 병기, 같은 kind·페이지 중복(duplicate_body)은 순번 병기(R2)
                    const samePageOther = sources.some((o, j) => j !== i && (o.page_index ?? 0) + 1 === pageNo);
                    const sameKindPage = sources
                      .map((o, j) => ({ o, j }))
                      .filter(({ o }) => o.kind === s.kind && (o.page_index ?? 0) === (s.page_index ?? 0));
                    const ordinal = sameKindPage.length > 1
                      ? ` #${sameKindPage.findIndex(({ j }) => j === i) + 1}`
                      : '';
                    return (
                      <button
                        key={i}
                        onClick={() => setActiveSourceIdx(i)}
                        className={`rounded px-1.5 py-0.5 text-[10px] ${i === activeSourceIdx ? 'bg-primary/15 font-bold text-primary' : 'bg-gray-100 text-gray-500'}`}
                      >
                        p.{pageNo}{samePageOther ? ` ${kindLabelOf(s.kind)}` : ''}{ordinal}
                      </button>
                    );
                  })}
                </span>
              )}
              <span className={`ml-auto flex items-center gap-1 ${leftTab === 'source' ? '' : 'hidden'}`}>
                {ZOOMS.map((z) => (
                  <button
                    key={z.key}
                    onClick={() => setZoom(z.key)}
                    className={`rounded px-1.5 py-0.5 text-[10px] ${zoom === z.key ? 'bg-gray-200 font-bold text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {z.label}
                  </button>
                ))}
              </span>
            </div>
            <div
              ref={imagePaneRef}
              className={`min-h-0 flex-1 overflow-auto bg-gray-50 p-3 ${fitMode ? 'flex flex-col' : ''}`}
            >
              {leftTab === 'list' ? (
                <OcrProblemList
                  items={items}
                  loading={listLoading}
                  loadError={listError}
                  filter={filter}
                  onFilterChange={onFilterChange}
                  totalItems={totalItems}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  checkedIds={checkedIds}
                  onClearChecked={onClearChecked}
                  selectedProbId={problemId}
                  onSelect={(id) => {
                    // 고른 문제로 오른쪽만 바꾸고 목록에 머문다 — 목록은 여러 건을 견주며
                    // 고르는 자리라, 한 건 고를 때마다 [원본]으로 튀면 매번 되돌아와야 했다.
                    // 원본을 볼지는 검수자가 탭으로 정한다.
                    if (String(id) !== String(problemId)) guardedNavigate(id);
                  }}
                  onRetry={onRetryList}
                  onApproveAll={onApproveAll}
                  approving={approving}
                  approveResult={approveResult}
                  canApprove={canApprove}
                  onReanalyze={() => setAiRun({
                    kind: 'analyzeAll',
                    title: analyzeLabel,
                    summary: '아직 분석하지 않은 문제를 분석합니다. 대상은 검수 승인된 문제 중 미분석·실패한 것입니다 — '
                      + '이미 끝난 문제는 대상이 아닙니다.',
                    warning: 'LLM 을 호출하므로 비용이 들고, 시작하면 중간에 멈출 수 없습니다. '
                      + 'AI 가 정답·풀이를 만든 문제는 검수대기로 되돌아와 한 번 더 확인해야 합니다.',
                  })}
                  analyzeLabel={analyzeLabel}
                  reanalyzing={reanalyzing}
                />
              ) : image.loading ? (
                <Skel className="h-96 w-full" />
              ) : image.error ? (
                <div className="whitespace-pre-line py-10 text-center text-sm text-gray-500">{image.error}</div>
              ) : image.url ? (
                /* 겉 상자는 두 모드 모두 '이미지와 정확히 같은 크기'여야 한다 —
                   빨간 위치 상자를 그 안에 %로 얹기 때문에, 어긋나면 엉뚱한 곳을 가리킨다.
                   맞춤: 원본보다 확대하지 않고 가로·세로 모두 남은 공간에 맞춘다.
                   확대: 예전대로 지정한 배율만큼 폭을 늘리고 넘치는 만큼 스크롤한다. */
                fitMode ? (
                  <OcrSourcePreview
                    key={image.url}
                    src={image.url}
                    alt={`페이지 ${viewPageIndex + 1} 원본`}
                    onError={() => handleImageLoadError(image.url)}
                  >
                    {bboxOverlay}
                  </OcrSourcePreview>
                ) : (
                  <div className="relative inline-block" style={{ width: zoomMeta.width }}>
                    <img
                      src={image.url}
                      alt={`페이지 ${viewPageIndex + 1} 원본`}
                      className="w-full rounded-lg shadow"
                      onError={() => handleImageLoadError(image.url)}
                    />
                    {bboxOverlay}
                  </div>
                )
              ) : showSkeleton ? (
                /* 첫 진입 — 아직 sources를 몰라 이미지 요청조차 못 한 상태 */
                <Skel className="h-96 w-full" />
              ) : (
                <div className="py-10 text-center text-sm text-gray-400">표시할 원본이 없습니다.</div>
              )}
              {leftTab === 'source' && activeSource && (
                /* 맞춤일 때 이 줄이 눌리면 그만큼 이미지가 밀려 스크롤이 생긴다 — 높이를 지킨다 */
                <div className={`mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-400 ${fitMode ? 'shrink-0' : ''}`}>
                  {/* 상자·후보가 여럿일 때만 풀어 준다 — 오너가 파란 점선을 「승인된 문제」로 읽었다(2026-09-06).
                      색은 승인과 무관하고, 고른 것과 아닌 것을 가른다.
                      같은 날 회신 뒤: 서버가 **이 문제가 직접 참조하는 조각**을 알려 주므로(is_body) 그때는
                      빨강을 「이 문제의 자리」라고 단언할 수 있다. 표시가 없는 배포에서는 종전대로 「고른 자리」다 —
                      단언과 추측을 같은 문장으로 뭉개지 않는다.
                      하나뿐일 때는 설명할 것이 없어 내지 않는다 — 줄이 길어지면 페이지 넘김 버튼이 밀린다. */}
                  {(drawnBoxCount > 1 || bodySourceCount > 1) && (
                    <span className="text-[11px] text-gray-400">
                      {drawnBoxCount > 1 && (
                        <>
                          <b className="text-red-500">빨강</b> {directKnown ? '이 문제의 자리' : '지금 고른 자리'}
                          {' · '}<b className="text-blue-400">파랑 점선</b> 같은 페이지의 다른 후보
                          {bodySourceCount > 1 ? ' · ' : ''}
                        </>
                      )}
                      {bodySourceCount > 1 && (directKnown
                        ? `같은 번호로 묶인 자리가 ${bodySourceCount}곳입니다 — 아래에서 다른 후보도 볼 수 있습니다`
                        : `이 문제로 묶인 자리가 ${bodySourceCount}곳입니다 — 아래에서 바꿔 볼 수 있습니다`)}
                    </span>
                  )}
                  {/* 앞뒤 페이지 넘기기 — 이 문제가 가리키지 않는 페이지도 확인할 수 있어야 한다.
                      (서답형이 마지막 장에 몰려 있는데 소스가 그 장을 안 가리키면 볼 방법이 없었다) */}
                  <span className="inline-flex items-center gap-0.5">
                    {/* 양끝은 실제 페이지 목록으로 판정한다 — 없는 페이지를 열려다 실패하지 않도록 */}
                    <button
                      type="button"
                      onClick={() => stepPageBy(-1)}
                      disabled={!canStepPage(-1)}
                      className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="원본 이전 페이지"
                    >
                      <ChevronLeft className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => stepPageBy(1)}
                      disabled={!canStepPage(1)}
                      className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                      aria-label="원본 다음 페이지"
                    >
                      <ChevronRight className="size-4" />
                    </button>
                  </span>
                  <span>
                    {activeSource.file_name || activeSource.file_uuid} · p.{viewPageIndex + 1}
                    {siblingPages.length > 0 && `/${siblingPages.length}`}
                    {!isOtherPage && activeSource.confidence != null && ` · 신뢰도 ${activeSource.confidence}`}
                  </span>
                  {isOtherPage && (
                    <span className="inline-flex items-center gap-1.5 text-amber-700">
                      이 문제의 페이지가 아닙니다
                      <button
                        type="button"
                        onClick={() => setPageOverride(null)}
                        className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 transition-colors hover:bg-amber-100"
                      >
                        원래 페이지로
                      </button>
                    </span>
                  )}
                  {/* 대상이 바로 왼쪽에 적혀 있는 자리 — 페이지를 넘기면 겨누는 대상도 같이 바뀐다.
                      아래 액션 바에 두지 않는 이유: 그 줄은 전부 '문제 한 건'에 가하는 조작이고
                      이것은 '페이지 한 장'에 가하는 조작이다. 섞이면 삭제 옆의 재실행으로 읽힌다. */}
                  {onRerunPage && (
                    // 완료된 쪽도 다시 읽는다 — force:true(BE-20260914-reply-page-reocr-force.md §2).
                    // 서버가 「파일 + 한 쪽 범위」가 함께 올 때만 받고 호출 1회 = 최대 1쪽을 보장한다.
                    // 쓰임새는 문제가 통째로 빠진 쪽을 되살리는 것이고, 한 문제의 오독은 아래
                    // [이 문제 다시 OCR](POST /problems/{id}/reocr)이 맞다 — 창 본문이 둘을 가른다.
                    // 종전(09-13 까지)에는 서버가 완료된 쪽을 건너뛰어 대개 아무 일도 하지 않았다.
                    // 얼굴은 본문 머리줄의 [이미지 추가]와 같은 공용 Button(size="xs" · variant="primary-outline" ·
                    // 아이콘 size-3.5)이다(2026-09-14 오너 지시) — 값을 베끼지 않고 같은 컴포넌트를 쓴다.
                    // 진행 표시는 loading 대신 아이콘 회전 — [이 문제 다시 OCR]과 같은 이유(스피너가 하나 더 붙으면 얼굴이 갈린다).
                    <Button
                      size="xs"
                      variant="primary-outline"
                      className="ml-auto shrink-0"
                      onClick={openRerunPageAi}
                      disabled={rerunning || projectLocked || targetFileId == null || busy || transitioning}
                      title={
                        projectLocked
                          ? '컨펌 대기/종료 상태에서는 다시 실행할 수 없습니다'
                          : targetFileId == null
                            ? '이 페이지가 어느 파일의 것인지 특정하지 못했습니다 — ② 단계에서 범위를 지정해 실행하세요'
                            : `${activeSource.file_name || '이 파일'} ${viewPageIndex + 1}쪽만 다시 OCR 합니다 (되돌릴 수 없음)`
                      }
                      icon={<RotateCw className={`size-3.5 ${rerunning ? 'motion-safe:animate-spin' : ''}`} />}
                    >
                      {rerunning ? '실행 중…' : '이 페이지 다시 OCR'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── 우측: 추출 데이터 + 편집 ──
              전환 중에는 이전 문제의 값이 읽히지 않을 만큼 흐려졌다가, 새 값이 들어온 뒤
              다시 진해진다. 값이 교체되는 순간이 가장 흐린 지점이라 눈에 걸리지 않는다. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div ref={rightPaneRef} className={`min-h-0 flex-1 space-y-4 overflow-y-auto p-4 ${dimCls}`}>
              {/* 기본 정보 — 상태 배지를 이 행 오른쪽에 붙인다.
                  전에는 모달 상단에 전폭 한 줄을 따로 썼는데, 그 줄이 없어진 만큼 원본 이미지가 커진다.
                  흐림 전환(dimCls)은 이 패널 전체에 이미 걸려 있어 배지에 따로 주지 않는다. */}
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  <h3 className="shrink-0 text-sm font-bold text-gray-900">기본 정보</h3>
                  {showSkeleton && (
                    <span className="ml-auto flex items-center gap-1.5">
                      <Skel className="h-5 w-14 rounded-full" />
                      <Skel className="h-5 w-14 rounded-full" />
                      <Skel className="h-5 w-20 rounded-full" />
                    </span>
                  )}
                  {detail && (
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                      {promoted && (
                        detail.promoted_problem_id != null ? (
                          <Link
                            to={problemDetailPath(detail.promoted_problem_id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isModifiedClick(e)) return;
                              // 한 문제만 볼 때는 바로 그 문제 화면 위에 떠 있다 — 같은 주소로 가는 대신 창을 닫아 돌아간다
                              if (singleProblem) {
                                e.preventDefault();
                                guardedClose();
                                return;
                              }
                              if (!confirmDiscard()) e.preventDefault();
                            }}
                            aria-label={`등록된 ${detail.promoted_problem_id}번 문제 열기`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 underline-offset-2 hover:underline"
                            title={`문제은행에 등록된 문제입니다 (#${detail.promoted_problem_id}) — ${singleProblem ? '선택하면 이 창을 닫고 문제 화면으로 돌아갑니다' : '선택하면 문제 메뉴에서 엽니다'}. 여기서 고쳐도 은행에는 반영되지 않습니다`}
                          >
                            <span>등록됨</span>
                            <span aria-hidden="true">·</span>
                            <span className="font-mono font-bold">{detail.promoted_problem_id}</span>
                          </Link>
                        ) : (
                          <span
                            className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                            title="문제은행 등록 이력은 있지만 연결할 PID가 없습니다 — 문제은행에서 삭제되었을 수 있습니다"
                          >
                            등록됨
                          </span>
                        )
                      )}
                      {/* 여기 남는 둘은 '화면을 봐서는 알 수 없는 것'이다.
                          answerType: 정답 칸에 무엇을 넣어야 하는지 — 다른 어디에도 없다.
                          analysis: [다시 분석] 버튼이 왜 있는지/없는지를 설명하는 짝이다.
                          뺀 넷과 그 이유:
                          · 매칭(match_status)은 제본 단계의 기록이라 그 뒤 AI가 채운 정답·풀이를
                            반영하지 않는다 — 정답도 풀이도 있는 문제에 「본문만」이 붙어 화면과
                            정면으로 어긋났다. 살리려면 백엔드가 갱신해야 한다(docs/ 왕복 사안).
                          · 검수 상태는 인덱스 줄 배경·액션바 버튼·인덱스 지도가 이미 말한다.
                          · AI 정답/AI 풀이는 정답·풀이 섹션 제목 옆 「AI 생성」과 같은 말이고,
                            그쪽이 '어느 것이 AI인지'를 바로 옆에서 말해 더 정확하다. */}
                      {badge(answerTypeInfo)}
                      {badge(analysisInfo)}
                      {flags.map((code) => {
                        const fi = getFlagDisplay(code);
                        return (
                          <span key={code} title={fi.hint || undefined} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${fi.cls}`}>
                            {fi.text}
                          </span>
                        );
                      })}
                    </span>
                  )}
                </div>
                {!showSkeleton && flagHints.length > 0 && (
                  <ul className="mb-2 space-y-0.5 text-xs text-amber-700">
                    {flagHints.map((f) => (
                      <li key={f.code}>· <span className="font-medium">{f.text}</span> — {f.hint}</li>
                    ))}
                  </ul>
                )}
                {/* 검수 상태를 줄 전체 바탕으로 말한다 — 바로 윗줄의 [검수완료]·[반려] 배지는
                    여러 배지 사이에 섞여 눈에 잘 들지 않는다. 배지 톤(green-100/red-100)보다
                    한 단 연한 값을 쓴다 — 여기는 면적이 배지의 몇 배라 같은 값이면 화면이 무겁다.
                    색만으로 나르지 않는다: 같은 상태를 윗줄 배지가 글자로도 말한다. */}
                {!singleWorkflow && (showSkeleton ? <Skel className="h-8 w-32" /> : (
                <div className={`flex flex-wrap items-center gap-3 rounded-xl px-3 py-2 ${
                  isApproved ? 'bg-green-50' : isRejected ? 'bg-red-50' : 'bg-transparent'
                }`}>
                  <label className="flex items-center gap-1.5 text-sm text-gray-600">
                    인덱스
                    <input
                      ref={numberInputRef}
                      type="number"
                      min="1"
                      value={numberText}
                      onChange={(e) => setNumberText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); approveAndNext(); } }}
                      disabled={busy}
                      className="w-24 rounded-lg border px-2 py-1 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
                    />
                  </label>
                  {/* 「확인함」 표시와 「다음」 안내·버튼은 목록을 훑는 흐름의 것이다 — 한 문제만 볼 때는 걷고,
                      Enter 가 승인한다는 사실만 남긴다(넘어갈 곳이 없으니 승인하고 그 자리에 머문다) */}
                  {singleProblem ? (
                    <span className="text-xs text-gray-400">Enter 승인</span>
                  ) : (
                    <>
                      {checkedIds?.has(problemId) ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                          <Check className="size-3.5" />
                          이번에 확인함
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">아직 확인 전</span>
                      )}
                      <span className="text-xs text-gray-400">Enter 승인하고 다음 · ←→ 이전/다음</span>
                      {/* 승인까지는 하지 않고 고친 것만 남기며 넘어가는 길 — Enter(승인하고 다음)와
                          다른 동작이라 버튼으로 남긴다. 번호만 훑는 동안 시선이 이 행을 떠나지 않게 한다 */}
                      <Button
                        size="xs"
                        variant="primary-outline"
                        className="ml-auto"
                        onClick={saveAndNext}
                        disabled={busy || (!nextItem && !canNextPage)}
                        icon={<ChevronRight className="size-4" />}
                      >
                        저장하고 다음
                      </Button>
                    </>
                  )}
                </div>
                ))}
                {/* 개념 — 칩·검색은 문제 편집과 같은 부품(ConceptPicker · 오너 결정 2026-09-13 D4).
                    칩을 누르면 개념 설명이 뜬다(onChipClick). 두 단계(④ 검수 · ⑤ 분석) 모두에서 고친다(D1). */}
                <div className="mt-2">
                  {/* 덧말은 아래 라벨 칸과 같은 자리·같은 얼굴이다(제목 줄 오른쪽 끝 · 10px 회색 400).
                      ④에서 넣은 값이 분석 한 번에 말없이 바뀌면 사람은 저장이 안 된 줄 안다 — 그래서 미리 말한다. */}
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="shrink-0 text-xs font-medium text-gray-500">개념</span>
                    <span className="text-right text-[10px] leading-tight text-gray-400">
                      AI 분석을 돌리면 개념·난이도는 분석 결과로 바뀝니다.
                    </span>
                  </div>
                  {showSkeleton ? <Skel className="h-6 w-48" /> : (
                    <ConceptPicker
                      value={conceptIds}
                      onChange={setConceptIds}
                      busy={busy}
                      resetKey={problemId}
                      onChipClick={showConceptDetail}
                      emptyText="없음"
                      listInFlow /* 스크롤 칸 안이고 바로 아래 라벨 칸이 붙는다 — 띄우면 그 밑에 깔린다(부품 주석) */
                    />
                  )}
                </div>
                {/* 라벨 — 등록(승격) 때 문제은행에 실리는 것은 공통(프로젝트 키워드) ∪ 자동("N번") ∪
                    개별(extra_labels) 셋의 합이다(BE 회신 Q6). 셋을 다 그리고, 고칠 수 있는 것은 개별뿐이다.
                    칩·입력칸은 문제 편집과 같은 부품(LabelEditor). 이 창은 이름을 모아 extra_labels 로 보내므로
                    사전 후보(suggest)는 주지 않는다 — 사전에 오르는 것은 등록 때 서버가 한다. */}
                <div className="mt-2">
                  {/* 안내는 제목 줄 오른쪽 끝에 둔다 — 입력칸 아래에 있을 때는 라벨을 다 치고 나서야
                      눈에 들어와, '안 쳐도 된다'는 말을 이미 친 뒤에 읽게 됐다. */}
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="shrink-0 text-xs font-medium text-gray-500">
                      라벨
                      {!showSkeleton && promotionLabelCount > 0 && (
                        <span className="ml-1 font-normal text-gray-400">등록되면 {promotionLabelCount}개</span>
                      )}
                    </span>
                    <span className="text-right text-[10px] leading-tight text-gray-400">
                      {singleWorkflow ? '직접 입력한 라벨만 저장합니다. 라벨당 100자 이하.' : '회색은 등록 때 서버가 붙이는 공통 키워드 — 여기서 뗄 수 없습니다. 위 인덱스는 라벨이 아닙니다.'}
                    </span>
                  </div>
                  {showSkeleton ? <Skel className="h-6 w-48" /> : (
                    /* 세 출처를 한 줄에 모은다 — 등록 뒤 문제은행에 실제로 붙는 라벨이 이 줄 전체다.
                       줄을 갈라 놓으면 「그래서 몇 개가 붙는가」를 눈으로 셀 수 없다.
                       색이 권한을 말한다: 회색+자물쇠 = 서버가 붙임(못 뗌) · 파랑+× = 이 문제에만 단 것(LabelEditor). */
                    <LabelEditor
                      items={[
                        ...lockedLabels.map((lk) => ({ key: `lk-${lk.text}`, text: lk.text, locked: true, hint: lk.hint })),
                        ...extraLabels.map((lb) => ({
                          key: lb,
                          text: lb,
                          /* 「24번」 꼴은 바로 위 인덱스 칸의 숫자와 헷갈린다 — 둘은 다른 값이고
                             문제은행에 실리는 것은 이쪽뿐이므로 여기서 말한다. */
                          hint: PRINTED_NUMBER_LABEL.test(lb)
                            ? '지면에 인쇄된 번호입니다 — 매칭 때 굳혔거나 직접 입력한 값. 위 인덱스 칸과 다른 값입니다.'
                            : undefined,
                        })),
                      ]}
                      onRemove={(key) => setExtraLabels((prev) => prev.filter((v) => v !== key))}
                      inputValue={labelInput}
                      onInputChange={setLabelInput}
                      onCommit={addLabel}
                      busy={busy}
                      emptyText="없음"
                      placeholder="라벨 추가 — Enter 또는 콤마로 구분 (예: 기출, 3점)"
                    />
                  )}
                </div>
                {/* 이 문제에 쓰인 AI 지시 — 값이 있을 때만 나타난다(usedPrompts 주석).
                    자리를 [기본 정보]의 마지막으로 고른 이유 셋:
                    ① 이 값은 본문 하나의 것이 아니다 — 다시 읽기는 본문·풀이·정답을 함께 바꾸고
                       분석은 바로 위 [개념]·난이도와 AI 정답·풀이를 만든다. 한 섹션에 붙이면 거짓이 된다.
                    ② [본문] 제목 줄은 이미 제목·[이 문제 다시 OCR]·보기/편집기/JSON·수정됨 배지·난이도로
                       꽉 차 있어, 길이를 모르는 문장을 넣을 자리가 없다.
                    ③ 읽기 전용 기록이라 고치는 것들(개념·라벨) 아래에 둔다 — 검수 흐름을 끊지 않는다.
                    얼굴은 바로 위 [개념]·[라벨] 두 블록을 그대로 베꼈다(mt-2 · 제목 12px/500 회색 +
                    덧말은 회색 400 normal). 같은 층위에 다른 값을 쓰지 않는다.
                    긴 값은 CSS truncate 한 줄 + title 전문 — 같은 모달의 목록 탭이 문제 미리보기에
                    쓰는 방식이다(OcrProblemList.jsx: `truncate text-xs` + `title={p.body_preview}`).
                    truncate 가 먹으려면 flex 안에서 글자 쪽이 min-w-0 이어야 한다(안 주면 안 줄고 넘친다). */}
                {!showSkeleton && usedPrompts.length > 0 && (
                  <div className="mt-2">
                    <div className="mb-1 text-xs font-medium text-gray-500">
                      AI 지시
                      <span className="ml-1 font-normal text-gray-400">이 문제에 실려 나간 문장</span>
                    </div>
                    <div className="space-y-0.5">
                      {usedPrompts.map((p) => (
                        <div key={p.key} className="flex items-baseline gap-1.5">
                          <span className="shrink-0 text-xs text-gray-400">{p.label}</span>
                          <span className="min-w-0 truncate text-xs text-gray-600" title={p.text}>{p.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 본문 — 보기(원문 payload 렌더) / 편집기(Lexical, 편집본=정본) / JSON(원문 안전판) */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900">본문</h3>
                  {/* 이 문제 다시 OCR — 원본 영역만 동기 재추출. 자리를 액션바에서 이 줄로 올렸다:
                      고칠 대상인 본문 바로 위이고, [보기]·[편집기]·[JSON] 어느 탭에서도 같은
                      자리에 선다(상자 안에 두면 탭마다 자리가 달라진다).
                      얼굴은 같은 모달의 [저장하고 다음]과 같은 공용 Button(size="xs" ·
                      variant="primary-outline") — 값을 베끼지 않고 같은 컴포넌트를 쓴다.
                      진행 표시는 loading prop 대신 아이콘 회전으로 한다 — loading 은 원형 스피너를
                      하나 더 붙여 아이콘·스피너·글자 셋이 되고, 그러면 레퍼런스와 얼굴이 갈린다.
                      분석은 하단의 AI 분석 버튼으로 별도 실행한다. */}
                  {!showSkeleton && (!modeAnalysis || singleWorkflow) && (
                    <Button
                      size="xs"
                      variant="primary-outline"
                      onClick={openReocrAi}
                      disabled={busy || transitioning || reocring}
                      title="이 문제의 원본 영역만 다시 읽습니다 (검수 상태 유지 · AI 생성분은 제거)"
                      icon={<RotateCw className={`size-4 ${reocring ? 'animate-spin' : ''}`} />}
                    >
                      {reocring ? '다시 읽는 중…' : '이 문제 다시 OCR'}
                    </Button>
                  )}
                  {!showSkeleton && renderFigureAddButtons('body')}
                  {!showSkeleton && (
                    <>
                      <div className="flex overflow-hidden rounded-full bg-gray-100 text-[10px] text-gray-500">
                        {[['render', '보기'], ['editor', '편집기'], ['json', 'JSON']].map(([mode, lbl]) => (
                          <button
                            key={mode}
                            onClick={() => {
                              flushLexBeforeSwitch('본문', bodyEditorRef, bodyLexPendingRef, lexKey('b'), setBodyEditorDirty);
                              setBodyView(mode);
                            }}
                            className={`px-2 py-0.5 ${bodyView === mode ? 'bg-primary text-white' : 'hover:bg-gray-200'}`}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {bodyEditorDirty && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">수정됨 · 미저장</span>
                      )}
                      {/* 난이도 — 문제 전체의 값이지만 줄을 따로 쓰지 않으려 이 행 오른쪽 끝에 둔다.
                          두 단계 모두에서 고친다(오너 결정 2026-09-13 D1). 보기는 「3 (보통)」 — 문제 편집의
                          분석 카드와 같은 표기(problemApi.difficultyOptionLabel · D4). 숫자만이면 방향을 모른다. */}
                      <label className="ml-auto flex shrink-0 items-center gap-1.5 text-sm text-gray-600">
                        난이도
                        <select
                          value={difficulty}
                          onChange={(e) => setDifficulty(e.target.value)}
                          disabled={busy}
                          className="rounded-lg border px-2 py-1 text-sm disabled:bg-gray-50"
                        >
                          <option value="">없음</option>
                          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{difficultyOptionLabel(n)}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                </div>
                {showSkeleton ? <Skel className="h-40 w-full" /> : bodyView === 'json' ? (
                  /* JSON = 정본의 원문(editorState). 읽기 전용이다 — 고치는 곳은 편집기 한 곳으로
                     모은다. 두 창구가 같은 필드를 쓰면 어느 쪽이 이기는지 규칙이 필요해지고,
                     손으로 쓴 노드가 저장 시 화이트리스트에 걸려 400이 난다(규약 회신 §2-2). */
                  <>
                    <textarea
                      value={prettyLexical(lexNow(bodyEditorRef, bodyLexPendingRef, lexKey('b'), detail?.body_lexical))}
                      readOnly
                      rows={14}
                      spellCheck={false}
                      className="w-full rounded-xl border bg-gray-50 p-2 font-mono text-xs text-gray-600 focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      {lexNow(bodyEditorRef, bodyLexPendingRef, lexKey('b'), detail?.body_lexical)
                        ? '읽기 전용입니다 — 본문을 고치려면 [편집기] 탭을 쓰세요.'
                        : '편집기 데이터(body_lexical)가 응답에 없어 보여 줄 원문이 없습니다 — 백엔드 확인 필요.'}
                    </p>
                  </>
                ) : bodyView === 'editor' ? (
                  detail?.body_lexical ? (
                    /* key: 문제·저장/재OCR(updated_at)마다 리마운트 — 서버 최신 편집본으로 기준 재설정 */
                    <div className="rounded-xl border border-gray-200">
                      <OcrLexicalEditor
                        key={lexKey('b')}
                        /* 모드 전환으로 언마운트됐던 편집분이 있으면 그것으로 복원(없으면 서버 값) */
                        initialState={
                          bodyLexPendingRef.current?.key === lexKey('b') && bodyLexPendingRef.current?.dirty
                            ? bodyLexPendingRef.current.state
                            : detail.body_lexical
                        }
                        fallbackState={detail.body_lexical} /* 스냅샷이 깨졌으면 서버 값으로 */
                        baselineState={detail.body_lexical} /* dirty 기준은 항상 서버 값 */
                        label="본문"
                        stateRef={bodyEditorRef}
                        stateKey={lexKey('b')} /* 이 편집기가 어느 문제의 것인지 — readLexEdit가 대조한다 */
                        onDirtyChange={setBodyEditorDirty}
                        onUnmount={(snap) => keepLexSnapshot('본문', snap, bodyLexPendingRef, lexKey('b'), setBodyEditorDirty)}
                        readOnly={busy}
                        readOnlyReason={closed ? '종료된 프로젝트는 읽기 전용입니다.' : null}
                        figureEdit={figureEditFor('body')}
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-100 p-3 text-sm text-gray-400">
                      편집기 데이터(body_lexical)가 응답에 없습니다 — 백엔드 확인 필요.
                    </div>
                  )
                ) : lexNow(bodyEditorRef, bodyLexPendingRef, lexKey('b'), detail?.body_lexical) ? (
                  /* 보기 = 정본(lexical)을 편집 잠금으로 그린다. 세 탭이 같은 것을 봐야
                     검수자가 승인한 것과 승격되는 것이 갈리지 않는다. key에 'v'를 더해
                     편집기 인스턴스와 섞이지 않게 한다(같은 key면 탭 전환에 리마운트가 안 된다). */
                  <div
                    className="rounded-xl border border-gray-200"
                    {...enterEditorOnDoubleClick(setBodyView, '본문', bodyEditorRef, bodyLexPendingRef, 'b', setBodyEditorDirty)}
                  >
                    <OcrLexicalEditor
                      key={`${lexKey('b')}|v`}
                      initialState={lexNow(bodyEditorRef, bodyLexPendingRef, lexKey('b'), detail?.body_lexical)}
                      fallbackState={detail?.body_lexical}
                      label="본문"
                      readOnly
                    />
                  </div>
                ) : (
                  /* 편집기 데이터가 아예 없는 문제 — 보여 줄 것이 OCR 원문뿐이다 */
                  <div className="rounded-xl border border-gray-100 p-3">
                    <p className="mb-2 text-[11px] text-amber-600">
                      편집기 데이터(body_lexical)가 없어 OCR 원문을 그대로 보여 줍니다.
                    </p>
                    <PageImageContext.Provider value={bodyPageImage}>
                      <BodyPayloadView payload={detail?.body_payload} />
                    </PageImageContext.Provider>
                  </div>
                )}
              </div>

              {/* 정답 */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900">정답</h3>
                  {aiGen?.answer && <AiChip label="AI 생성" />}
                </div>
                {/* 객관식이면 원문자 칩으로 바로 고른다 — 문제은행 정답 관례가 '①'~'⑤' 원문자
                    문자열 그대로이고(AnswerModal 라디오와 동일), 자유 입력만 두면 "3"/"③" 두 표기가
                    staging에 섞여 쌓인다. 자유 입력은 비표준 값 대비로 아래에 유지. */}
                {!showSkeleton && detail?.body_payload?.answerType === 'multiple_choice' && (() => {
                  const choiceList = Array.isArray(detail?.body_payload?.choices) ? detail.body_payload.choices : [];
                  const markCount = Math.min(CIRCLED.length, Math.max(5, choiceList.length || 5));
                  // 「3」·「(3)」·「3번」도 ③ 으로 읽어 버튼을 켠다(F15). 원문자가 아니면 누를 때 원문자로 맞춘다.
                  const chosenNo = choiceNoOfAnswer(answerText, markCount);
                  const loose = isLooseChoiceAnswer(answerText, markCount);
                  // 그 선지의 내용 — 값 3 과 「③ 선지」를 헷갈리지 않게(선지 값이 숫자인 문제에서 특히)
                  const chosen = chosenNo != null
                    ? choiceList.find((c, i) => (c?.no ?? i + 1) === chosenNo) || null
                    : null;
                  return (
                    <>
                      <div className="mb-1.5 flex flex-wrap items-center gap-1">
                        {CIRCLED.slice(0, markCount).map((mark, i) => {
                          const on = answerText.trim() === mark || chosenNo === i + 1;
                          return (
                            <button
                              key={mark}
                              type="button"
                              // 원문자로 이미 맞으면 해제, 「3」처럼 느슨하게 켜졌으면 원문자로 맞춘다
                              onClick={() => setAnswerText(answerText.trim() === mark ? '' : mark)}
                              disabled={busy}
                              title={on && loose
                                ? `정답 칸 값 「${answerText.trim()}」을 ${mark}로 읽었습니다 — 누르면 ${mark}로 맞춥니다`
                                : `정답을 ${mark}로 (다시 누르면 해제)`}
                              className={`h-8 w-8 rounded-full border text-sm transition-colors disabled:opacity-40 ${
                                on
                                  ? 'border-primary bg-primary font-bold text-white'
                                  : 'border-gray-200 bg-white text-gray-600 hover:border-primary/50'
                              }`}
                            >
                              {mark}
                            </button>
                          );
                        })}
                      </div>
                      {chosen && Array.isArray(chosen.content) && chosen.content.length > 0 && (
                        <div className="mb-1.5 flex min-w-0 items-baseline gap-1.5 text-xs text-gray-500">
                          <span className="shrink-0">{CIRCLED[chosenNo - 1]} 선지:</span>
                          <div className="min-w-0 text-gray-700"><Blocks blocks={chosen.content} inline /></div>
                          {loose && <span className="shrink-0 text-amber-700">(정답 칸 값 「{answerText.trim()}」)</span>}
                        </div>
                      )}
                    </>
                  );
                })()}
                {showSkeleton ? <Skel className="h-10 w-full" /> : (
                  <AnswerField
                    value={answerText}
                    onChange={setAnswerText}
                    editing={answerEditing}
                    setEditing={setAnswerEditing}
                    disabled={busy}
                  />
                )}
              </div>

              {/* 풀이 — 본문과 동일한 3상태(보기/편집기/JSON) */}
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900">풀이</h3>
                  {aiGen?.solution && <AiChip label="AI 생성" />}
                  {!showSkeleton && detail?.lexical_edited?.solution && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary" title="편집기에서 수정한 풀이가 저장돼 있습니다 — 렌더·등록 모두 편집본이 쓰입니다">
                      편집본
                    </span>
                  )}
                  {!showSkeleton && renderFigureAddButtons('solution')}
                  {!showSkeleton && (
                    <>
                      <div className="flex overflow-hidden rounded-full bg-gray-100 text-[10px] text-gray-500">
                        {[['render', '보기'], ['editor', '편집기'], ['json', 'JSON']].map(([mode, lbl]) => (
                          <button
                            key={mode}
                            onClick={() => {
                              flushLexBeforeSwitch('풀이', solutionEditorRef, solutionLexPendingRef, lexKey('s'), setSolutionEditorDirty);
                              setSolutionView(mode);
                            }}
                            className={`px-2 py-0.5 ${solutionView === mode ? 'bg-primary text-white' : 'hover:bg-gray-200'}`}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {solutionEditorDirty && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">수정됨 · 미저장</span>
                      )}
                      {/* 풀이 삭제 — JSON 탭을 비워 저장하던 경로를 대신한다. 그 탭이 읽기 전용이 되면서
                          풀이를 지울 수단이 화면에서 사라지기 때문에 여기로 옮겼다. */}
                      {(detail?.solution_payload || detail?.solution_lexical) && (
                        <button
                          type="button"
                          onClick={handleDeleteSolution}
                          disabled={busy || transitioning || deletingSolution}
                          title="이 문제의 풀이를 지웁니다 (본문·정답은 그대로)"
                          className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                        >
                          <Trash2 className="size-3.5" />
                          {deletingSolution ? '삭제 중…' : '풀이 삭제'}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {showSkeleton ? <Skel className="h-28 w-full" /> : solutionView === 'json' ? (
                  <>
                    <textarea
                      value={prettyLexical(lexNow(solutionEditorRef, solutionLexPendingRef, lexKey('s'), detail?.solution_lexical))}
                      readOnly
                      rows={10}
                      spellCheck={false}
                      className="w-full rounded-xl border bg-gray-50 p-2 font-mono text-xs text-gray-600 focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      {lexNow(solutionEditorRef, solutionLexPendingRef, lexKey('s'), detail?.solution_lexical)
                        ? '읽기 전용입니다 — 풀이를 고치려면 [편집기] 탭을, 지우려면 위 [풀이 삭제]를 쓰세요.'
                        : '이 문제에는 풀이가 없습니다.'}
                    </p>
                  </>
                ) : solutionView === 'editor' ? (
                  detail?.solution_lexical ? (
                    <div className="rounded-xl border border-gray-200">
                      <OcrLexicalEditor
                        key={lexKey('s')}
                        initialState={
                          solutionLexPendingRef.current?.key === lexKey('s') && solutionLexPendingRef.current?.dirty
                            ? solutionLexPendingRef.current.state
                            : detail.solution_lexical
                        }
                        fallbackState={detail.solution_lexical}
                        baselineState={detail.solution_lexical}
                        label="풀이"
                        stateRef={solutionEditorRef}
                        stateKey={lexKey('s')} /* 위와 같음 — 남의 문제 편집기를 정본으로 읽지 않게 */
                        onDirtyChange={setSolutionEditorDirty}
                        onUnmount={(snap) => keepLexSnapshot('풀이', snap, solutionLexPendingRef, lexKey('s'), setSolutionEditorDirty)}
                        readOnly={busy}
                        readOnlyReason={closed ? '종료된 프로젝트는 읽기 전용입니다.' : null}
                        figureEdit={figureEditFor('solution')}
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-100 p-3 text-sm text-gray-400">
                      풀이가 없거나 편집기 데이터(solution_lexical)가 응답에 없습니다.
                    </div>
                  )
                ) : lexNow(solutionEditorRef, solutionLexPendingRef, lexKey('s'), detail?.solution_lexical) ? (
                  /* 본문과 같은 규칙 — 보기도 정본(lexical)을 편집 잠금으로 그린다 */
                  <div
                    className="rounded-xl border border-gray-200"
                    {...enterEditorOnDoubleClick(setSolutionView, '풀이', solutionEditorRef, solutionLexPendingRef, 's', setSolutionEditorDirty)}
                  >
                    <OcrLexicalEditor
                      key={`${lexKey('s')}|v`}
                      initialState={lexNow(solutionEditorRef, solutionLexPendingRef, lexKey('s'), detail?.solution_lexical)}
                      fallbackState={detail?.solution_lexical}
                      label="풀이"
                      readOnly
                    />
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-100 p-3">
                    {detail?.solution_payload && (
                      <p className="mb-2 text-[11px] text-amber-600">
                        편집기 데이터(solution_lexical)가 없어 OCR 원문을 그대로 보여 줍니다.
                      </p>
                    )}
                    <PageImageContext.Provider value={solutionPageImage}>
                      <SolutionPayloadView payload={detail?.solution_payload} />
                    </PageImageContext.Provider>
                  </div>
                )}
              </div>
            </div>

            {/* 액션 바 — 승격된 문제·종료 프로젝트는 저장·승인·반려 전부 차단(FE-5).
                전환 중(transitioning)에도 잠근다 — 골격이 그대로 남아 있어 누를 수 있게 됐고,
                아직 넘어오지 않은 문제를 대상으로 저장·승인이 나가면 안 된다. */}
            <div className={`flex shrink-0 flex-wrap items-center gap-2 border-t p-3 ${singleWorkflow ? 'justify-end' : ''}`}>
              {singleWorkflow && actionNote && <span role="status" className={`w-full min-w-0 text-right text-xs ${actionNote.cls}`}>{actionNote.text}</span>}
              {/* 왼쪽 = 이 문제 자체에 가하는 조작. 검수 흐름(오른쪽)과 무게를 갈라 둔다 —
                  삭제는 되돌릴 수 없고, 재분석은 AI 산출물을 갈아 끼운다. */}
              {!singleWorkflow && <button
                type="button"
                onClick={handleDelete}
                disabled={busy || transitioning || deleting}
                title="이 문제 행을 삭제합니다 (되돌릴 수 없음)"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
              >
                <Trash2 className="size-3.5" />
                {deleting ? '삭제 중…' : '삭제'}
              </button>}
              {/* 처리 중(150)만 뺀다 — 백엔드도 지정 재분석에서 그것만 제외한다(ocrApi.js:472).
                  미실행(100)에서도 눌려야 한다: 재-OCR은 분석 상태를 100으로 되돌리는데,
                  여기서 못 걸면 그 문제 하나 때문에 ⑤ 단계를 다녀와야 한다.
                  상태를 모를 때(null)도 낸다 — 지정 재분석은 승인 여부를 보지 않고 처리 중(150)만
                  제외하므로, 상태를 못 읽었다는 이유로 버튼을 감추면 되레 길이 막힌다. */}
              {singleWorkflow && onReanalyze && (
                <Button
                  size="sm"
                  variant="primary-outline"
                  icon={<Sparkles className={`size-3.5 ${reanalyzing ? 'animate-pulse' : ''}`} />}
                  onClick={openAnalyzeOneAi}
                  disabled={busy || transitioning || reanalyzing}
                  title="정답·풀이·개념·난이도를 분석합니다."
                >
                  {reanalyzing ? 'AI 분석 중…' : analysisStatusValue === 200 ? 'AI 재분석' : 'AI 분석'}
                </Button>
              )}
              {!singleWorkflow && modeAnalysis && onReanalyze && analysisStatusValue !== 150 && (
                <button
                  type="button"
                  onClick={openAnalyzeOneAi}
                  disabled={busy || transitioning || reanalyzing}
                  title={
                    analysisStatusValue === 200
                      ? '이 문제 하나만 다시 분석합니다 (좌측 목록의 [다시 분석]은 미분석 전체가 대상)'
                      : '이 문제 하나만 분석합니다 (좌측 목록의 [다시 분석]은 미분석 전체가 대상)'
                  }
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-purple-50 hover:text-purple-700 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                >
                  <Sparkles className="size-3.5" />
                  {reanalyzing ? '분석 중…' : (analysisStatusValue === 200 ? '이 문제 다시 분석' : '이 문제 AI 분석')}
                </button>
              )}
              {!singleWorkflow && actionNote && <span className={`min-w-0 text-xs ${actionNote.cls}`}>{actionNote.text}</span>}
              {/* 편집 취소 — 미저장 편집을 원본(seed)으로 되돌린다(편집기 0단계 안전망).
                  seedEdits만으로는 Lexical이 안 되돌아간다(키가 그대로라 리마운트가 없다) —
                  lexResetSeq를 올려 편집기를 서버 값으로 다시 세운다. */}
              <Button
                className={singleWorkflow ? '' : 'ml-auto'}
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!detail || !window.confirm('저장하지 않은 편집을 모두 되돌릴까요?')) return;
                  seedEdits(detail, { keepView: true });
                  setLexResetSeq((n) => n + 1);
                }}
                disabled={busy || transitioning || !hasUnsavedChanges()}
              >
                편집 취소
              </Button>
              <Button size="sm" onClick={handleSave} loading={saving} disabled={busy || transitioning}>
                {singleWorkflow ? '편집 저장' : '저장'}
              </Button>
              {singleWorkflow && <Button size="sm" onClick={() => runSingleAction(onBankSave, true)} loading={singleActionBusy && externalBusy} disabled={busy || transitioning || !onBankSave || detail?.analysis_status !== 200} title={detail?.analysis_status !== 200 ? 'AI 분석을 완료한 뒤 저장할 수 있습니다.' : undefined}>문제 저장</Button>}
              {/* 이미 한 쪽은 무엇을 했는지 보여 주고 잠근다. 반대쪽은 살려 둔다 —
                  승인·반려는 서로 되돌리는 길이고, 되돌릴 수 없는 것은 승격뿐이다. */}
              {!singleWorkflow && <><Button
                size="sm"
                variant={isApproved ? 'secondary' : 'success'}
                onClick={() => handleReview('approve')}
                loading={reviewing === 'approve'}
                disabled={busy || transitioning || isApproved}
                icon={isApproved ? <Check className="size-3.5" /> : null}
              >
                {isApproved ? '검수완료' : '승인'}
              </Button>
              <Button
                size="sm"
                variant={isRejected ? 'secondary' : 'danger'}
                onClick={() => handleReview('reject')}
                loading={reviewing === 'reject'}
                disabled={busy || transitioning || isRejected}
                icon={isRejected ? <Check className="size-3.5" /> : null}
              >
                {isRejected ? '반려됨' : '반려'}
              </Button></>}
            </div>
          </div>
        </div>
      )}

      {/* 그림 편집 창. 개념 상세와 같은 이유로 검수 모달 안에 둔다 — 검수를 닫으면 함께 사라진다.
          문제은행과 같은 컴포넌트이고 target 만 'ocr' 이다(계약 §7 「경로만 다르다」). */}
      {figureTarget && (
        <FigureModal
          open
          target="ocr"
          problemId={figureTarget.problemId ?? problemId}
          field={figureTarget.field}
          index={figureTarget.index}
          nodeSrc={figureTarget.src}
          nodeList={figureTarget.nodes}
          lexicalEdited={figureTarget.edited}
          action={figureTarget.action}
          onRemoveNode={removeUnknownFigureNode}
          getAccessToken={getAccessToken}
          onClose={closeFigure}
        />
      )}

      {/* 서버가 가진 그림 골라 넣기 — 서버에 쓰지 않는다(등록하면 같은 그림이 둘이 된다). */}
      {figPickField && (
        <OcrFigurePickModal
          isOpen
          problemId={detail?.ocr_prob_id}
          field={figPickField}
          docState={lexNow(
            figPickField === 'solution' ? solutionEditorRef : bodyEditorRef,
            figPickField === 'solution' ? solutionLexPendingRef : bodyLexPendingRef,
            lexKey(figPickField === 'solution' ? 's' : 'b'),
            figPickField === 'solution' ? detail?.solution_lexical : detail?.body_lexical,
          )}
          getAccessToken={getAccessToken}
          onClose={() => setFigPickField(null)}
          onPick={({ src }) => {
            const f = figPickField;
            setFigPickField(null);
            const ref = f === 'solution' ? solutionEditorRef : bodyEditorRef;
            const key = lexKey(f === 'solution' ? 's' : 'b');
            const live = ref.current;
            const w = f === 'solution' ? '풀이' : '본문';
            const ok = live && live.key === key && typeof live.insertImage === 'function'
              && live.insertImage({ src, altText: '' });
            setToast(ok
              ? { type: 'info', text: `${w}에 그림을 넣었습니다 — [저장]을 눌러야 반영됩니다.` }
              : { type: 'error', text: `${w} 편집기에 넣지 못했습니다 — [편집기] 탭을 연 뒤 다시 시도해 주세요.` });
          }}
        />
      )}

      {/* [이미지 추가] 가 여는 파일 고르기 — 고른 파일은 붙여넣기와 **같은 창**으로 들어간다.
          공용 편집기 툴바의 이미지 삽입은 공용 다이얼로그를 한 번 더 거쳐 창이 두 번 뜬다. */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => { takeImageFile(e.target.files?.[0]); e.target.value = ''; }}
      />

      {/* [이미지 추가]의 갈래 고르기 — 문제 편집과 같은 창(FigureAddModal). */}
      <FigureAddModal
        isOpen={figAddField != null}
        target="ocr"
        field={figAddField || 'body'}
        onClose={() => setFigAddField(null)}
        onChoose={(kind) => {
          const f = figAddField;
          if (kind === 'generate') openFigGen(f);
          else if (kind === 'file') openImagePicker(f);
          else if (kind === 'pick') setFigPickField(f);
        }}
      />

      {/* AI 생성 — 재요청과 **같은 창**(kind='generate' · 계약 §9-2 의 OCR 경로). 창을 열어 둔 채
          기다린다 — 만들 그림이 아직 없어 진행 표시를 넘길 자리가 없기 때문이다. */}
      <FigureReconvertModal
        isOpen={figGenField != null}
        kind="generate"
        target="ocr"
        field={figGenField || 'body'}
        /* 이 자리 그림들의 원본 카드(2026-09-06 A 안) — 고르면 그 그림을 그 자리에서 다시 그린다(runFigGen 'figure'). */
        figureOrigins={figGenOrigins.items}
        figureOriginsLoading={figGenOrigins.loading}
        busy={figGenBusy}
        onConfirm={(payload) => runFigGen(payload)}
        onClose={() => setFigGenField(null)}
      />

      {/* 붙여넣은 이미지를 무엇으로 삼을지 묻는 창(계약 §10 · 회신 §1·§3).
          편집기가 이미지를 받으려는 순간 PasteFigurePlugin 이 가로채 여기로 보낸다 —
          그 순간이 아니면 그 이미지가 「그림 교체」인지 「참고 이미지」인지 가릴 수 없다. */}
      {pasteTarget && (
        <OcrPasteImageModal
          isOpen
          problemId={detail?.ocr_prob_id}
          pasted={pasteTarget}
          getAccessToken={getAccessToken}
          onBeforeReplace={async () => {
            // 교체는 서버 쓰기다 — 저장하지 않은 편집이 있으면 먼저 저장한다(그림 창과 같은 규칙).
            // 실패 사유는 토스트가 이 창 뒤에 깔리므로 창 안으로 되돌려 준다(lastErrorRef 주석).
            if (!hasUnsavedChanges()) return { ok: true, saved: false };
            lastErrorRef.current = null;
            const ok = await handleSave({ quiet: true, keepView: true });
            return ok ? { ok: true, saved: true } : { ok: false, text: lastErrorRef.current };
          }}
          onClose={() => setPasteTarget(null)}
          onDone={(r) => {
            const target = pasteTarget;
            setPasteTarget(null);
            if (r?.plain) { insertImageNow(target.field, target.src, target.altText); return; }
            /**
             * 서버가 payload 와 편집본을 함께 고쳤다(회신 §2 update) — 상세를 다시 읽으면
             * 편집기에 그 그림이 들어와 있다. 여기서 편집기에 직접 꽂으면 두 장이 된다.
             */
            const w = target.field === 'solution' ? '풀이' : '본문';
            setToast(r?.editedCopyDropped
              ? {
                type: 'error',
                text: `${w} ${Number(r.index) + 1}번째 그림을 바꿨습니다. 다만 ${w}의 편집본이 서버가 세는`
                  + ' 그림과 맞지 않아 버려졌습니다 — 편집기에서 고쳤던 문장이 OCR 원문으로 되돌아갑니다.',
              }
              : { type: 'info', text: `${w} ${Number(r.index) + 1}번째 그림을 바꿨습니다.` });
            loadDetail({ keepView: true });
          }}
        />
      )}

      {/* AI 요청 창 — 이 모달(기본 9999980) 위에 뜬다(--z-modal-ai-run 9999986).
          위 closeOnEsc 의 `aiRun == null` 과 짝이다: 안 걸면 ESC 한 번에 검수 모달까지 닫힌다.

          기본 지시 안내는 창을 열 때 만든 문구(aiRun.summary) 뒤에 여기서 한 번만 붙인다.
          네 갈래(reocr·rerunPage·analyzeOne·analyzeAll)가 전부 해당하므로 나눌 것이 없고(DefaultPromptNotice
          주석 — rerunPage 가 부르는 run 도 비우면 기본 지시를 쓴다), 여는 자리 넷에 같은 줄을 네 번 적으면
          그중 하나가 낡아 갈린다.
          걸린 값이 없으면 DefaultPromptNotice 가 null 이라 창은 종전과 한 글자도 다르지 않다.
          `aiRun ? … : ''` 로 한 번 더 가르는 것은 닫히는 동안 때문이다 — 창은 aiRun 이 null 이 된
          뒤에도 사라지는 애니메이션만큼 더 그려지는데, 그냥 이으면 그 사이 본문 문구만 비고 안내
          한 줄이 홀로 남아 뜬다. 빈 문자열로 돌려야 종전처럼 안내 칸 전체가 함께 접힌다. */}
      <AiRunModal
        isOpen={aiRun != null}
        title={aiRun?.title || 'AI 요청'}
        summary={aiRun ? (
          <>
            {aiRun.summary || ''}
            <DefaultPromptNotice prompt={projectDefaultPrompt} />
          </>
        ) : ''}
        warning={aiRun?.warning || null}
        placeholder={aiRun?.kind === 'reocr' ? '예: 선지가 6개다 · 아래 표는 본문에 넣지 마라'
          // 회신 §5-3 의 복구 지시 그대로 — 잘려 실패한 쪽을 좁혀 다시 읽을 때 쓴다
          : aiRun?.kind === 'rerunPage' ? '예: 이 쪽에서 12번 문항만 추출하세요 · 9·10·11번은 출력하지 마세요'
            : '예: 고1 과정 개념만 쓴다 · 풀이는 두 줄 이내로'}
        confirmText={aiRun?.kind === 'reocr' || aiRun?.kind === 'rerunPage' ? '다시 읽기' : '분석'}
        busy={aiRun?.kind === 'reocr' ? reocring : aiRun?.kind === 'rerunPage' ? rerunning : !!reanalyzing}
        onConfirm={(userPrompt) => {
          const kind = aiRun?.kind;
          const target = aiRun?.target; // rerunPage 만 — 창을 연 순간 굳힌 쪽(openRerunPageAi)
          setAiRun(null);
          if (kind === 'reocr') handleReocr(userPrompt);
          else if (kind === 'rerunPage') handleRerunPage(userPrompt, target);
          else if (kind === 'analyzeOne') handleReanalyzeOne(userPrompt);
          else if (kind === 'analyzeAll' && onReanalyze) onReanalyze(undefined, userPrompt);
        }}
        onClose={() => setAiRun(null)}
      />

      {/* 개념 칩을 누르면 뜨는 상세. 검수 모달 안에 두어 검수를 닫으면 함께 사라지게 한다.
          공용 Modal이 createPortal이라 중첩돼도 잘리지 않고, 나중에 열린 쪽이 위에 그려진다. */}
      <MathceptDetailModal
        isOpen={conceptDetailOpen}
        onClose={closeConceptDetail}
        data={conceptDetailData}
        loading={conceptDetailLoading}
        handleAuth={false}
      />
    </Modal>
  );
}

export default OcrReviewModal;
